/**
 * Pattern of Life Dashboard Page
 * 
 * Comprehensive behavioral analysis dashboard showing:
 * - Entity behavioral profiles with scores
 * - Recurring patterns (periodic visits, routes)
 * - Entity correlations (co-location, co-travel)
 * - Anomalies and routine breaks
 * - Temporal activity heatmaps
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import type { ChangeEvent, HTMLAttributes, MouseEvent, SyntheticEvent } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Chip,
  LinearProgress,
  IconButton,
  Tooltip,
  FormControl,
  Select,
  MenuItem,
  Stack,
  Divider,
  Button,
  Avatar,
  useTheme,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  ToggleButtonGroup,
  ToggleButton,
  Snackbar,
} from '@mui/material';
import type { AutocompleteRenderInputParams, SelectChangeEvent } from '@mui/material';
import {
  People as SocialIcon,
  Warning as WarningIcon,
  Place as PlaceIcon,
  Timeline as TimelineIcon,
  Slideshow as SlideshowIcon,
  CalendarMonth as CalendarIcon,
  Route as RouteIcon,
  Groups as GroupsIcon,
  NotificationsActive as AlertIcon,
  AccessTime as TimeIcon,
  Psychology as AIIcon,
  Smartphone as DigitalIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  MyLocation as CenterIcon,
  Bookmark as BookmarkIcon,
  Traffic as TrafficIcon,
  Close as CloseIcon,
  Check as CheckIcon,
  FiberManualRecord as DotIcon,
  InfoOutlined as InfoIcon,
  FilterAlt as FilterIcon,
  FilterAlt as FilterByMapIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Block as BlockIcon,
  LinkOff as LinkOffIcon,
  AutoAwesome as AutoAwesomeIcon,
  Insights as InsightsIcon,
  DirectionsRun as DirectionsRunIcon,
  AddLocationAlt as AddLocationAltIcon,
  WrongLocation as WrongLocationIcon,
  HelpOutline as HelpOutlineIcon,
  ScheduleSend as ScheduleSendIcon,
  HourglassTop as HourglassTopIcon,
  EventBusy as EventBusyIcon,
  AltRoute as AltRouteIcon,
  PersonAddAlt as PersonAddAltIcon,
} from '@mui/icons-material';
import type { SvgIconComponent } from '@mui/icons-material';
import Menu from '@mui/material/Menu';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import {
  XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer,
  ScatterChart, Scatter, ZAxis, Cell, ReferenceLine,
} from 'recharts';
import MapGL, { Source, Layer, Marker, Popup, ScaleControl, MapRef } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';

import polService, {
  DashboardResponse,
  EntitySummary,
  RecurringPattern,
  DigitalInsightsResponse,
  EntityCorrelation,
  PolAnomaly,
  AnomalySummary,
  LocationSequence,
  RoutePattern,
  DigitalPattern,
  DigitalSimilarity,
  CourseInfo,
  LocationVisit,
  TemporalHeatmapData,
  TimelineDwell,
  HourlyEvent,
} from '../services/polService';
import type { MapStyleType } from '../types/mapStyle';
import {
  Add as ZoomInIcon,
  Remove as ZoomOutIcon,
  Navigation as CompassIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  MyLocation as MyLocationIcon,
  Satellite as SatelliteIcon,
  Straighten as MeasureIcon,
  Delete as DeleteIcon,
  Map as MapLegendIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import POLSentinel from '../components/analysis/POLSentinel';
import { POL_TAB, polTabIndex, type POLTabId } from '../polTabs';
import DigitalInsightsPanel from '../components/analysis/DigitalInsightsPanel';
import SimilarEntitiesPanel from '../components/analysis/SimilarEntitiesPanel';
import AnomalyDetectionPanel from '../components/analysis/AnomalyDetectionPanel';
import ForecastTab from '../components/analysis/ForecastTab';
import DwellAnalysisTab from '../components/DwellAnalysisTab';
import PatternWheel from '../components/PatternWheel';
import WheelCellTimeline from '../components/WheelCellTimeline';
import { useParticipantLabels } from '@signals/app-shell/hooks/useParticipantLabels';
import { useCourseLabels } from '@signals/app-shell/hooks/useCourseLabels';
import { useEffectiveAccess } from '@signals/app-shell/stores/accessControlStore';
import { hasPermission, POL_TAB_PERMISSION_BY_LABEL } from '@signals/app-shell/types/accessControl';
import {
  DARK_MAP_POPUP_CLASS,
  DarkMapPopupShell,
  DarkMapPopupRow,
  DarkMapPopupCoords,
  useDarkMapPopupChrome,
} from '@signals/app-shell/components/map/DarkMapPopup';
import {
  exportPOLToPowerPoint,
  exportTriageBriefToPowerPoint,
  type POLTriageBriefDayDetail,
  type PowerPointTheme,
  THEME_DEFINITIONS,
} from '@signals/app-shell/utils/powerpointExport';
import { resolveLatestCourses } from '@signals/app-shell/utils/latestCourses';

// Mapbox token from environment
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

const DIGITAL_EVENT_TYPE_STYLES = [
  { type: 'call', label: 'Calls', color: '#f85149' },
  { type: 'sms', label: 'SMS', color: '#f59e0b' },
  { type: 'app', label: 'Apps', color: '#3fb950' },
  { type: 'browsing', label: 'Web', color: '#58a6ff' },
] as const;

const DIGITAL_EVENT_INTERACTIVE_LAYER_IDS = DIGITAL_EVENT_TYPE_STYLES.flatMap(({ type }) => [
  `digital-pol-clusters-${type}`,
  `digital-pol-events-${type}`,
]);

const PIPELINE_INFO_HTML = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<link href="https://fonts.googleapis.com/css2?family=DM+Serif+Display&family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet">
<style>
:root{--ink:#1a1a2e;--paper:#faf9f6;--accent:#e07a5f;--accent-soft:#f4e4dd;--muted:#6b7280;--border:#e5e2dc;--card:#fff;--tag-bg:#eef2f7;--tag-text:#3d5a80;--green:#4a7c59;--green-soft:#e4ede6;--purple:#6c5b7b;--purple-soft:#ebe7ef;--amber:#b07d2f;--amber-soft:#f5edda}
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:'DM Sans',sans-serif;background:var(--paper);color:var(--ink);line-height:1.7;-webkit-font-smoothing:antialiased}
.container{max-width:760px;margin:0 auto;padding:40px 28px 80px}
header{text-align:center;margin-bottom:48px}
header .badge{display:inline-block;font-size:11px;font-weight:600;letter-spacing:1.5px;text-transform:uppercase;color:var(--accent);background:var(--accent-soft);padding:6px 16px;border-radius:100px;margin-bottom:20px}
header h1{font-family:'DM Serif Display',serif;font-size:clamp(28px,5vw,38px);font-weight:400;line-height:1.2;margin-bottom:16px}
header p{font-size:16px;color:var(--muted);max-width:560px;margin:0 auto}
.analogy{background:var(--ink);color:#fff;border-radius:16px;padding:32px 28px;margin-bottom:48px;position:relative;overflow:hidden}
.analogy h2{font-family:'DM Serif Display',serif;font-size:20px;font-weight:400;margin-bottom:12px;color:var(--accent)}
.analogy p{font-size:15px;line-height:1.75;color:rgba(255,255,255,0.85)}
.section-header{display:flex;align-items:center;gap:14px;margin:48px 0 24px;padding-bottom:14px;border-bottom:2px solid var(--border)}
.section-header .icon{width:44px;height:44px;border-radius:12px;display:flex;align-items:center;justify-content:center;font-size:20px;flex-shrink:0}
.section-header h2{font-family:'DM Serif Display',serif;font-size:24px;font-weight:400}
.section-header p{font-size:14px;color:var(--muted)}
.phase{background:var(--card);border:1px solid var(--border);border-radius:14px;padding:22px 24px;margin-bottom:14px}
.phase-head{display:flex;align-items:flex-start;gap:14px;margin-bottom:10px}
.phase-num{flex-shrink:0;width:32px;height:32px;border-radius:8px;display:flex;align-items:center;justify-content:center;font-size:14px;font-weight:700;color:#fff}
.phase-num.blue{background:var(--tag-text)}.phase-num.green{background:var(--green)}.phase-num.amber{background:var(--amber)}.phase-num.purple{background:var(--purple)}
.phase-head h3{font-size:16px;font-weight:600;line-height:1.35;padding-top:4px}
.phase-head .tag{margin-top:5px;display:inline-block;font-size:11px;font-weight:600;letter-spacing:0.5px;text-transform:uppercase;padding:2px 10px;border-radius:100px}
.tag.blue{background:var(--tag-bg);color:var(--tag-text)}.tag.green{background:var(--green-soft);color:var(--green)}.tag.amber{background:var(--amber-soft);color:var(--amber)}.tag.purple{background:var(--purple-soft);color:var(--purple)}
.phase p{font-size:14px;color:var(--muted);line-height:1.7}
.features{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-top:14px}
@media(max-width:540px){.features{grid-template-columns:1fr}}
.feature-card{background:var(--paper);border:1px solid var(--border);border-radius:10px;padding:14px 16px}
.feature-card .icon{font-size:20px;margin-bottom:4px}
.feature-card strong{display:block;font-size:13px;font-weight:600;margin-bottom:3px}
.feature-card span{font-size:12.5px;color:var(--muted);line-height:1.55}
.callout{background:var(--tag-bg);border-left:4px solid var(--tag-text);border-radius:0 12px 12px 0;padding:18px 22px;margin-top:16px}
.callout p{font-size:14px;color:var(--tag-text);margin:0}
.callout strong{color:var(--ink)}
.callout.warn{background:var(--amber-soft);border-left-color:var(--amber)}
.callout.warn p{color:var(--amber)}
.arch{background:var(--ink);border-radius:16px;padding:28px 24px;margin-top:36px;margin-bottom:36px}
.arch h3{font-family:'DM Serif Display',serif;font-size:20px;font-weight:400;color:var(--accent);margin-bottom:20px;text-align:center}
.arch-flow{display:flex;flex-direction:column;align-items:center;gap:0}
.arch-box{width:100%;max-width:440px;border-radius:12px;padding:16px 20px;text-align:center}
.arch-box.data{background:rgba(61,90,128,0.3);border:1px solid rgba(61,90,128,0.5)}
.arch-box.engine{background:rgba(224,122,95,0.2);border:1px solid rgba(224,122,95,0.4)}
.arch-box.db{background:rgba(74,124,89,0.25);border:1px solid rgba(74,124,89,0.45)}
.arch-box.dash{background:rgba(108,91,123,0.25);border:1px solid rgba(108,91,123,0.45)}
.arch-box h4{font-size:14px;font-weight:700;color:#fff;margin-bottom:6px}
.arch-box p{font-size:12px;color:rgba(255,255,255,0.7);line-height:1.5;margin:0}
.arch-arrow{font-size:22px;color:rgba(255,255,255,0.3);padding:6px 0;text-align:center}
.model-card{background:var(--card);border:1px solid var(--border);border-radius:14px;padding:20px 22px;margin-bottom:14px}
.model-card h4{font-size:15px;font-weight:600;margin-bottom:4px}
.model-card .model-tag{display:inline-block;font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;padding:2px 10px;border-radius:100px;margin-bottom:10px}
.model-card p{font-size:13.5px;color:var(--muted);margin-bottom:8px}
.tech-detail{background:var(--paper);border:1px solid var(--border);border-radius:10px;padding:14px 18px;margin-top:12px}
.tech-detail-label{font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:0.6px;color:var(--purple);margin-bottom:10px}
.tech-row{display:flex;gap:10px;font-size:12.5px;line-height:1.55;padding:4px 0;border-bottom:1px solid rgba(0,0,0,0.04)}
.tech-row:last-child{border-bottom:none}
.tech-key{flex-shrink:0;width:110px;font-weight:600;color:var(--ink)}
.tech-val{color:var(--muted)}
.tech-val code{background:rgba(0,0,0,0.05);padding:1px 6px;border-radius:4px;font-size:11px;font-family:'SF Mono','Fira Code',monospace}
.classification{background:var(--ink);border-radius:16px;padding:24px 22px;margin-top:24px}
.classification h4{color:#fff;font-size:15px;font-weight:600;margin-bottom:18px;text-align:center}
.class-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px}
@media(max-width:540px){.class-grid{grid-template-columns:1fr}}
.class-item{border-radius:10px;padding:14px 16px}
.class-item strong{display:block;font-size:13px;margin-bottom:2px}
.class-item>span{display:block;font-size:11px;font-weight:500;margin-bottom:8px;opacity:0.7}
.class-item p{font-size:12.5px;line-height:1.55;margin:0;opacity:0.85}
.class-item.dl{background:rgba(224,122,95,0.15);border:1px solid rgba(224,122,95,0.3);color:#fff}
.class-item.dl strong{color:var(--accent)}
.class-item.ml{background:rgba(176,125,47,0.15);border:1px solid rgba(176,125,47,0.3);color:#fff}
.class-item.ml strong{color:#d4a74a}
.class-item.shallow{background:rgba(61,90,128,0.2);border:1px solid rgba(61,90,128,0.35);color:#fff}
.class-item.shallow strong{color:#7fb3e0}
.class-item.eng{background:rgba(255,255,255,0.06);border:1px solid rgba(255,255,255,0.12);color:#fff}
.class-item.eng strong{color:rgba(255,255,255,0.8)}
.summary{background:var(--card);border:2px solid var(--border);border-radius:16px;padding:32px 28px;margin-top:40px;text-align:center}
.summary h3{font-family:'DM Serif Display',serif;font-size:20px;font-weight:400;margin-bottom:12px}
.summary p{font-size:14px;color:var(--muted);max-width:560px;margin:0 auto 20px}
.tags{display:flex;flex-wrap:wrap;gap:8px;justify-content:center}
.stag{font-size:12px;font-weight:600;padding:5px 14px;border-radius:100px}
.stag.a{background:var(--accent-soft);color:var(--accent)}.stag.b{background:var(--tag-bg);color:var(--tag-text)}.stag.c{background:var(--green-soft);color:var(--green)}.stag.d{background:var(--purple-soft);color:var(--purple)}
</style>
</head>
<body>
<div class="container">
<header>
<div class="badge">Non-Technical Overview</div>
<h1>How Pathfinder Turns Raw Data into Actionable Intelligence</h1>
<p>A plain-language walkthrough of the 13-step pipeline that converts device telemetry into behavioral profiles, layered anomaly alerts, recurring patterns, per-day atypicality rankings, and forward-looking forecasts.</p>
</header>
<div class="analogy">
<h2>The big picture</h2>
<p>Imagine a team of analysts who each specialize in one thing: one watches daily routines, another maps travel routes, another listens for unusual phone activity, another spots when people keep showing up in the same places, a senior analyst at the end of every day reads everyone&rsquo;s reports and ranks <em>which person had the most interesting day</em>, and a forecasting analyst then projects <em>what each person is most likely to do tomorrow</em>. Now imagine that team works 24/7, processes hundreds of people simultaneously, and never misses a detail. That&rsquo;s what this pipeline does &mdash; automatically, in 13 coordinated steps.</p>
</div>
<div class="arch">
<h3>How Data Flows Through the System</h3>
<div class="arch-flow">
<div class="arch-box data"><h4>Raw Device Data</h4><p>GPS locations, call logs, text messages, app usage, and web browsing history</p></div>
<div class="arch-arrow">&darr;</div>
<div class="arch-box engine"><h4>The Pipeline (12 Steps)</h4><p>Fingerprints behavior, trains AI models, runs layered detectors, finds patterns &amp; correlations, ranks daily atypicality</p></div>
<div class="arch-arrow">&darr;</div>
<div class="arch-box db"><h4>Database</h4><p>Stores fingerprints, model results, anomaly scores, patterns, and route data</p></div>
<div class="arch-arrow">&darr;</div>
<div class="arch-box dash"><h4>Dashboard &amp; Ask Sentinel</h4><p>Analysts explore profiles, compare entities, review anomalies, and ask AI questions</p></div>
</div>
</div>
<div class="section-header"><div class="icon" style="background:var(--tag-bg)">1</div><div><h2>Phase 1 &mdash; Build Digital Fingerprints</h2><p>Steps 1&ndash;3 turn raw data into numerical profiles that computers can compare.</p></div></div>
<div class="phase"><div class="phase-head"><div class="phase-num blue">1</div><div><h3>Behavior &amp; Travel Fingerprints</h3><span class="tag blue">Embeddings</span></div></div><p>For each person, the system looks at their overall habits (where they go, how often, when) and creates a <strong>64-number summary</strong> &mdash; their behavioral fingerprint. It also summarizes their travel sequences into a separate 64-number travel fingerprint. Two people with similar lifestyles will have similar numbers.</p></div>
<div class="phase"><div class="phase-head"><div class="phase-num blue">2</div><div><h3>Social Network Fingerprints</h3><span class="tag blue">Graph Embeddings</span></div></div><p>The system builds a web of connections based on who shows up near whom, and how often. It then uses <strong>Node2Vec</strong> &mdash; essentially letting a virtual &ldquo;walker&rdquo; randomly stroll through this web &mdash; to learn a 64-number fingerprint for each person&rsquo;s position in the social network.</p></div>
<div class="phase"><div class="phase-head"><div class="phase-num blue">3</div><div><h3>Place Fingerprints</h3><span class="tag blue">Location Embeddings</span></div></div><p>Each important location gets its own 64-number fingerprint based on <em>when</em> people visit, <em>how long</em> they stay, and <em>how many</em> different people come and go.</p></div>
<div class="callout"><p><strong>Why fingerprints?</strong> A computer can&rsquo;t &ldquo;understand&rdquo; behavior the way a human does. By converting habits into lists of numbers, the system can compare thousands of people in seconds.</p></div>
<div class="section-header"><div class="icon" style="background:var(--amber-soft)">2</div><div><h2>Phase 2 &mdash; Spot the Unusual</h2><p>Steps 4&ndash;7 train AI models to learn what &ldquo;normal&rdquo; looks like, then flag days that don&rsquo;t fit.</p></div></div>
<div class="phase"><div class="phase-head"><div class="phase-num amber">4</div><div><h3>Learn Normal Physical Behavior</h3><span class="tag amber">Model Training</span></div></div><p>The system studies up to 90 days of each person&rsquo;s <strong>physical activity</strong> and trains a personal AI model that learns their version of &ldquo;normal.&rdquo;</p>
<div class="features"><div class="feature-card"><strong>Deep Learning Model</strong><span>Used with 20+ days of data. Learns to replay a person&rsquo;s weekly patterns; anything it can&rsquo;t replay well is unusual.</span></div><div class="feature-card"><strong>Isolation Forest</strong><span>Used with 7&ndash;19 days of data. Identifies days that look like outliers compared to the rest.</span></div></div></div>
<div class="phase"><div class="phase-head"><div class="phase-num amber">5</div><div><h3>Score Each Day (Physical)</h3><span class="tag amber">Anomaly Scoring</span></div></div><p>The trained model gives each day an <strong>anomaly score from 0 to 1</strong>. Near 0 means totally normal. Near 1 means very different from the usual pattern. Days above the threshold get flagged.</p></div>
<div class="phase"><div class="phase-head"><div class="phase-num amber">6</div><div><h3>Learn Normal Digital Behavior</h3><span class="tag amber">Model Training</span></div></div><p>Same idea, but for <strong>digital activity</strong> &mdash; call volume, texting frequency, app usage, and browsing habits. The system learns what a normal digital day looks like for each person.</p></div>
<div class="phase"><div class="phase-head"><div class="phase-num amber">7</div><div><h3>Score Each Day (Digital)</h3><span class="tag amber">Anomaly Scoring</span></div></div><p>Each day is scored for digital anomalies. A sudden burst of late-night calls, an unusual app, or a dramatic drop in texting would push the score up toward 1.</p></div>
<div class="callout warn"><p><strong>How severity works:</strong> Anomaly scores map to severity &mdash; <strong>critical</strong> (&ge; 0.9), <strong>high</strong> (&ge; 0.7), <strong>medium</strong> (&ge; 0.5), and <strong>low</strong> (below 0.5).</p></div>
<div class="section-header"><div class="icon" style="background:var(--green-soft)">3</div><div><h2>Phase 3 &mdash; Find Patterns, Anomalies &amp; Daily Rollups</h2><p>Steps 8&ndash;12 surface recurring habits, run statistical anomaly detectors, map routes, detect digital habits, and rank each entity&rsquo;s most atypical days.</p></div></div>
<div class="phase"><div class="phase-head"><div class="phase-num green">8</div><div><h3>Detect Patterns, Correlations &amp; Statistical Anomalies</h3><span class="tag green">Pattern + Anomaly Detection</span></div></div><p>Scans the last 90 days for <strong>recurring patterns</strong> (locations visited 3+ times with consistent timing), <strong>co-location events</strong> (two people within ~100m within 30 minutes, 3+ times), <strong>route patterns</strong> (repeatable trips between dwell clusters), and <strong>location sequences</strong> (A &rarr; B &rarr; C). It then runs six purpose-built statistical detectors that flag specific events &mdash; not just whole anomalous days &mdash; against each entity&rsquo;s own historical baseline.</p>
<div class="features" style="margin-bottom:14px"><div class="feature-card"><strong>Recurring Patterns</strong><span>A location visited 3+ times with consistent day &amp; arrival window.</span></div><div class="feature-card"><strong>Co-Location</strong><span>Two people repeatedly near each other with overlapping dwells.</span></div><div class="feature-card"><strong>Route Patterns</strong><span>Recurring origin&ndash;destination trips with similar geometry.</span></div><div class="feature-card"><strong>Location Sequences</strong><span>Repeated A &rarr; B &rarr; C trip chains.</span></div></div>
<div class="tech-detail" style="margin-top:0;background:var(--green-soft);border-color:rgba(74,124,89,0.25)"><div class="tech-detail-label" style="color:var(--green)">Statistical Anomaly Detectors (per-entity, per-location baselines)</div>
<div class="features" style="margin-top:10px"><div class="feature-card"><strong>Off-Schedule Visits</strong><span>Arrived at a recurring location at an hour outside the median&plusmn;MAD arrival distribution. <em>(time_shift)</em></span></div><div class="feature-card"><strong>Unusual Dwell Duration</strong><span>Stayed much longer or briefer than the historical median for that location. <em>(dwell_duration_anomaly)</em></span></div><div class="feature-card"><strong>Missed Recurring Visits</strong><span>Hasn&rsquo;t returned to a high-confidence recurring spot in well over the typical inter-visit interval. <em>(lapse_break)</em></span></div><div class="feature-card"><strong>Route Deviations</strong><span>Trip along a known route had abnormal duration or distance vs. that route&rsquo;s historical instances. <em>(route_deviation)</em></span></div><div class="feature-card"><strong>New Relationships</strong><span>First-time co-location or co-travel between two entities not previously correlated. <em>(new_relationship)</em></span></div><div class="feature-card"><strong>Unusual Locations</strong><span>Dwelled at a place far from any of the entity&rsquo;s known recurring patterns. <em>(unusual_location)</em></span></div></div></div></div>
<div class="phase"><div class="phase-head"><div class="phase-num green">9</div><div><h3>Send AI Alerts to the Dashboard</h3><span class="tag green">Bridge</span></div></div><p>AI-detected anomalies from Steps 5 and 7 are merged into the same alert feed as the statistical detectors above, so analysts see <strong>everything in one place</strong> &mdash; with type chips, severity colors, and explanations.</p></div>
<div class="phase"><div class="phase-head"><div class="phase-num green">10</div><div><h3>Map Travel Routes</h3><span class="tag green">Route Analysis</span></div></div><p>Clusters nearby dwell locations (~75m), then traces GPS tracks between them to identify <strong>recurring travel routes</strong>. Scores each route by how often it&rsquo;s been traveled, computes peak hour/day, and stores per-trip instances that the route_deviation detector compares against.</p></div>
<div class="phase"><div class="phase-head"><div class="phase-num green">11</div><div><h3>Detect Digital Habits</h3><span class="tag green">Digital Patterns</span></div></div><p>Looks for heuristic digital patterns &mdash; &ldquo;this person always uses WhatsApp between 9&ndash;10 PM&rdquo; or &ldquo;call volume spikes every Monday morning.&rdquo;</p></div>
<div class="phase"><div class="phase-head"><div class="phase-num green">12</div><div><h3>Daily Atypicality Rollup</h3><span class="tag green">Daily Score</span></div></div><p>The senior-analyst step. For each <strong>(entity, day)</strong> pair, it rolls up everything from Steps 4&ndash;11 &mdash; total distance, unique places, dwell counts, and the count of every anomaly type fired &mdash; then fuses them into a single <strong>atypicality_score</strong> from 0 to 1. The Anomalies tab uses this to surface the &ldquo;Top Atypical Days&rdquo; list at a glance: one click on a card focuses the rest of the dashboard on that entity&rsquo;s day.</p>
<div class="features"><div class="feature-card"><strong>Mobility Deviation</strong><span>How far this day&rsquo;s distance traveled is from the entity&rsquo;s own median.</span></div><div class="feature-card"><strong>Anomaly Density</strong><span>How many distinct detectors fired on this day, weighted by severity.</span></div><div class="feature-card"><strong>Composite Score</strong><span>Single 0&ndash;1 ranking that fuses both signals to surface outlier days.</span></div><div class="feature-card"><strong>Persisted Daily</strong><span>Stored in <code>daily_activity_metrics</code> with a unique (entity, day) index.</span></div></div></div>
<div class="section-header"><div class="icon" style="background:var(--purple-soft)">4</div><div><h2>Phase 4 &mdash; Forecast What Comes Next</h2><p>Step 13 turns everything Phases 1&ndash;3 learned about each person into a forward-looking view: an &ldquo;expected day&rdquo; for tomorrow, a near-real-time &ldquo;Right Now&rdquo; panel, and a measured quality score so analysts know how much to trust the prediction.</p></div></div>
<div class="phase"><div class="phase-head"><div class="phase-num purple">13</div><div><h3>Daily Behavioral Forecast</h3><span class="tag purple">Forecast &amp; Right Now</span></div></div><p>Once every entity&rsquo;s baselines and per-day scores are settled, the forecaster combines them into a prediction for the next day and updates a live &ldquo;Right Now&rdquo; view in the Forecast tab.</p>
<div class="features" style="margin-bottom:14px"><div class="feature-card"><strong>Expected Day</strong><span>Hour-by-hour timeline of likely visits, transits, co-presence and digital activity, synthesized directly from the recurring patterns, route patterns, correlations, and digital baselines built in Phases 1&ndash;3. No new model required &mdash; only as accurate as the underlying baselines.</span></div><div class="feature-card"><strong>LSTM Next-Day Forecast</strong><span>A small per-entity neural network learns the rhythm of the entity&rsquo;s 48-feature daily vector and predicts tomorrow&rsquo;s. Used to score how &ldquo;surprising&rdquo; today&rsquo;s actual behavior is once it lands.</span></div><div class="feature-card"><strong>Likely Next Stops</strong><span>A Markov chain over the entity&rsquo;s dwells (or its route patterns, when dwells are sparse) gives the top few places the entity is most likely to go to next, conditioned on where they are and what hour it is.</span></div><div class="feature-card"><strong>Forecast Quality</strong><span>Every prediction can be backtested against what actually happened. Composite accuracy, visit recall, and timeline agreement roll up into a single Quality cell so analysts know whether to trust the forecast.</span></div></div>
<div class="callout"><p><strong>How &ldquo;Right Now&rdquo; works:</strong> Every minute the Forecast tab asks the backend, &ldquo;Where is this entity, what were they supposed to be doing this hour, and where will they probably go next?&rdquo; The backend reads their latest GPS dwell, looks up the matching slot in today&rsquo;s expected-day forecast, and consults the Markov chain &mdash; or the entity&rsquo;s historical route patterns when the chain is too sparse. It is not a tactical real-time feed; it is a poll-driven check-in designed for situational awareness.</p></div></div>
<div class="section-header"><div class="icon" style="background:var(--purple-soft)">AI</div><div><h2>The AI Models &mdash; Plain English + Technical Detail</h2><p>Three model families handle anomaly detection, plus one for social network analysis. Each tier catches a different class of unusual behavior.</p></div></div>
<div class="model-card"><h4>1. LSTM Autoencoder</h4><span class="model-tag" style="background:var(--accent-soft);color:var(--accent)">Deep Learning &middot; Physical Anomaly Detection</span><p><strong>What it does:</strong> Learns to compress a week of behavior into a tiny summary, then reconstruct it. High reconstruction error means something unusual happened.</p><p><strong>Used for:</strong> Physical anomaly detection when 20+ days of history are available.</p>
<div class="tech-detail"><div class="tech-detail-label">Technical Detail</div>
<div class="tech-row"><span class="tech-key">File</span><span class="tech-val"><code>anomaly_embedding_service.py</code></span></div>
<div class="tech-row"><span class="tech-key">Framework</span><span class="tech-val">PyTorch &mdash; <code>TemporalAutoencoder</code></span></div>
<div class="tech-row"><span class="tech-key">Architecture</span><span class="tech-val">Input (48-dim) &rarr; LSTM encoder (hidden=32, latent=16) &rarr; LSTM decoder &rarr; output</span></div>
<div class="tech-row"><span class="tech-key">Training</span><span class="tech-val">Per-entity, MSE loss, 50 epochs, batch 16, sequence 7 days</span></div>
<div class="tech-row"><span class="tech-key">Threshold</span><span class="tech-val">95th percentile of training reconstruction errors</span></div>
<div class="tech-row"><span class="tech-key">Fallback</span><span class="tech-val">If PyTorch unavailable or &lt;20 days &rarr; Isolation Forest</span></div>
</div></div>
<div class="model-card"><h4>2. Isolation Forest</h4><span class="model-tag" style="background:var(--amber-soft);color:var(--amber)">Ensemble Trees &middot; Physical &amp; Digital Anomaly Detection</span><p><strong>What it does:</strong> Randomly splits the data over and over. Normal points take many splits to isolate. Weird ones get separated quickly because they&rsquo;re already different.</p><p><strong>Used for:</strong> All digital anomaly detection (96-dim) and physical fallback (48-dim, 7&ndash;19 days).</p>
<div class="tech-detail"><div class="tech-detail-label">Technical Detail</div>
<div class="tech-row"><span class="tech-key">File</span><span class="tech-val"><code>anomaly_lite_service.py</code></span></div>
<div class="tech-row"><span class="tech-key">Framework</span><span class="tech-val">scikit-learn <code>IsolationForest</code></span></div>
<div class="tech-row"><span class="tech-key">Config</span><span class="tech-val">200 estimators, 5% contamination</span></div>
<div class="tech-row"><span class="tech-key">Scoring</span><span class="tech-val">Negated decision_function &rarr; min-max [0, 1]</span></div>
<div class="tech-row"><span class="tech-key">Thresholds</span><span class="tech-val">95th and 99th percentile of calibrated training scores</span></div>
</div></div>
<div class="model-card"><h4>3. Statistical Detectors (Median + MAD)</h4><span class="model-tag" style="background:var(--green-soft);color:var(--green)">Robust Statistics &middot; Event-Level Anomaly Detection</span><p><strong>What it does:</strong> Where the autoencoder asks &ldquo;was this <em>day</em> weird?&rdquo;, these detectors ask &ldquo;was <em>this specific event</em> weird?&rdquo;. For each (entity, location) and each (entity, route), the system maintains a robust baseline using median and Median Absolute Deviation (MAD). Any new dwell, arrival hour, or trip is z-scored against that baseline; events past a tunable threshold fire as discrete anomalies with severity proportional to deviation.</p><p><strong>Used for:</strong> Off-schedule visits, unusual dwell durations, missed recurring visits, route deviations, new relationships, and unusual locations &mdash; the six event-level detectors visible as colored chips on every anomaly card.</p>
<div class="tech-detail"><div class="tech-detail-label">Technical Detail</div>
<div class="tech-row"><span class="tech-key">File</span><span class="tech-val"><code>pol_processing_service.py</code></span></div>
<div class="tech-row"><span class="tech-key">Baseline</span><span class="tech-val">Median + MAD per entity per location/route, refreshed each run</span></div>
<div class="tech-row"><span class="tech-key">Robustness</span><span class="tech-val">MAD &times; 1.4826 approximates &sigma; without being skewed by outliers</span></div>
<div class="tech-row"><span class="tech-key">Gates</span><span class="tech-val">Min occurrences, min confidence, and absolute deviation floor before firing</span></div>
<div class="tech-row"><span class="tech-key">Severity</span><span class="tech-val">low / medium / high / critical based on z-score magnitude</span></div>
<div class="tech-row"><span class="tech-key">Why MAD</span><span class="tech-val">A single bad GPS jitter or one weird day won&rsquo;t poison future detections</span></div>
</div></div>
<div class="model-card"><h4>4. Node2Vec + Word2Vec</h4><span class="model-tag" style="background:var(--tag-bg);color:var(--tag-text)">Shallow Embedding &middot; Network Analysis</span><p><strong>What it does:</strong> Sends virtual walkers wandering through the social map. The paths teach the system which people occupy similar positions in the network.</p>
<div class="tech-detail"><div class="tech-detail-label">Technical Detail</div>
<div class="tech-row"><span class="tech-key">File</span><span class="tech-val"><code>graph_embedding_service.py</code></span></div>
<div class="tech-row"><span class="tech-key">Algorithm</span><span class="tech-val">Node2Vec (p=1.0, q=0.5) &rarr; gensim Word2Vec</span></div>
<div class="tech-row"><span class="tech-key">Output</span><span class="tech-val">64-dim embeddings per entity</span></div>
</div></div>
<div class="classification"><h4>What&rsquo;s Actually &ldquo;AI&rdquo; vs. Traditional Engineering?</h4><div class="class-grid">
<div class="class-item dl"><strong>Deep Learning</strong><span>LSTM Autoencoder only</span><p>The only true neural network. Learns temporal patterns in sequences of behavior.</p></div>
<div class="class-item ml"><strong>Traditional ML</strong><span>Isolation Forest</span><p>An ensemble of decision trees. Effective but not a neural network.</p></div>
<div class="class-item shallow"><strong>Shallow Embeddings</strong><span>Node2Vec + Word2Vec</span><p>Learns from graph structure using random walks &mdash; a single-layer model.</p></div>
<div class="class-item eng"><strong>Robust Statistics</strong><span>Median + MAD detectors &middot; Daily atypicality rollup</span><p>No neural networks. Per-entity baselines using outlier-resistant statistics, fused into a single daily score.</p></div>
</div></div>
<div class="section-header"><div class="icon" style="background:var(--accent-soft)">UI</div><div><h2>What Analysts Actually See</h2><p>All this work feeds into the Pattern of Life Dashboard and Ask Sentinel.</p></div></div>
<div class="features" style="margin-bottom:0">
<div class="feature-card"><strong>Entity Profiles</strong><span>Behavioral fingerprint and routine/mobility/social scores per entity.</span></div>
<div class="feature-card"><strong>Top Atypical Days</strong><span>Ranked list of (entity, day) pairs by atypicality_score &mdash; click to drill in.</span></div>
<div class="feature-card"><strong>Layered Anomaly Alerts</strong><span>Autoencoder day-level + 6 statistical event-level detectors, color-coded by type.</span></div>
<div class="feature-card"><strong>Recurring Patterns</strong><span>Where, when, and how reliably each entity repeats themselves.</span></div>
<div class="feature-card"><strong>Route Visualization</strong><span>Recurring travel routes on the map, with deviations flagged.</span></div>
<div class="feature-card"><strong>Similarity &amp; Network Search</strong><span>&ldquo;Find everyone who behaves like Person X&rdquo; via cosine similarity over embeddings.</span></div>
<div class="feature-card"><strong>Digital Baselines</strong><span>Normal app/call/SMS patterns and deviations from them.</span></div>
<div class="feature-card"><strong>Ask Sentinel</strong><span>AI chat that uses embeddings and patterns to answer questions.</span></div>
</div>
<div class="summary"><h3>The Bottom Line</h3><p>Pathfinder&rsquo;s 13-step pipeline automatically ingests device data, builds behavioral and social fingerprints, trains personal AI models for whole-day anomalies, runs six event-level statistical detectors against per-entity baselines, surfaces recurring patterns &amp; routes, rolls everything up into a single daily atypicality score, and projects each entity&rsquo;s most likely next day &amp; next stops &mdash; all delivered to analysts through one dashboard and AI assistant.</p><div class="tags"><span class="stag a">Behavioral Fingerprints</span><span class="stag b">Layered Anomaly Detection</span><span class="stag c">Pattern &amp; Route Analysis</span><span class="stag d">Daily Atypicality Ranking</span><span class="stag a">Behavioral Forecasting</span></div></div>
</div>
</body>
</html>`;

type SharedLocationPoint = EntityCorrelation['shared_locations'][number];

type RouteStopMarker = {
  lon: number;
  lat: number;
  name: string;
  order: number;
  isFirst: boolean;
  isLast: boolean;
};

type LocationSeqPoint = LocationSequence['locations'][number];
type CompareRouteRow = RoutePattern & { is_primary?: boolean };
type RouteTripInstance = NonNullable<RoutePattern['trip_instances']>[number];

type HourlyHeatmapCell = NonNullable<EntitySummary['hourly_heatmap']>[number];

// Point-in-polygon test using ray casting algorithm
// Works with GeoJSON Polygon coordinates [[lon, lat], [lon, lat], ...]
const isPointInPolygon = (lat: number, lon: number, polygon: number[][]): boolean => {
  const x = lon;
  const y = lat;
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [xi, yi] = polygon[i];  // GeoJSON is [lon, lat]
    const [xj, yj] = polygon[j];
    const intersect = ((yi > y) !== (yj > y)) && 
                     (x < (xj - xi) * (y - yi) / (yj - yi + Number.EPSILON) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
};

/** Outer rings from a Polygon or MultiPolygon GeoJSON geometry ([lon, lat][]). */
const extractPolygonRings = (geometry: any): number[][][] => {
  if (!geometry || !geometry.type || !geometry.coordinates) return [];
  if (geometry.type === 'Polygon') {
    const ring = geometry.coordinates[0];
    return Array.isArray(ring) && ring.length >= 3 ? [ring] : [];
  }
  if (geometry.type === 'MultiPolygon') {
    return (geometry.coordinates as number[][][][])
      .map((poly) => poly?.[0])
      .filter((ring): ring is number[][] => Array.isArray(ring) && ring.length >= 3);
  }
  return [];
};

const isPointInAnyRing = (lat: number, lon: number, rings: number[][][] | null): boolean => {
  if (!rings || rings.length === 0) return false;
  return rings.some((ring) => isPointInPolygon(lat, lon, ring));
};

/**
 * Normalize pol_route_patterns.route_geometry into GeoJSON [lon, lat] pairs.
 * Tolerates JSON strings, numeric strings, Feature/LineString wrappers, and
 * accidental [lat, lon] order (detected via origin proximity / CONUS heuristic).
 */
const normalizeRouteGeometry = (
  geom: unknown,
  origin?: { lat?: number | null; lon?: number | null } | null,
): [number, number][] => {
  let raw: unknown = geom;
  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw);
    } catch {
      return [];
    }
  }
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const obj = raw as Record<string, unknown>;
    if (obj.type === 'Feature' && obj.geometry && typeof obj.geometry === 'object') {
      raw = (obj.geometry as { coordinates?: unknown }).coordinates;
    } else if (obj.type === 'LineString') {
      raw = obj.coordinates;
    } else {
      raw = obj.coordinates ?? obj.route_geometry ?? [];
    }
  }
  if (!Array.isArray(raw) || raw.length < 2) return [];

  const coords: [number, number][] = [];
  for (const c of raw) {
    if (!Array.isArray(c) || c.length < 2) continue;
    const a = Number(c[0]);
    const b = Number(c[1]);
    if (!Number.isFinite(a) || !Number.isFinite(b)) continue;
    coords.push([a, b]);
  }
  if (coords.length < 2) return [];

  const oLat = origin?.lat;
  const oLon = origin?.lon;
  if (oLat != null && oLon != null && Number.isFinite(oLat) && Number.isFinite(oLon)) {
    const [a0, b0] = coords[0];
    // Interpret as [lon, lat] vs [lat, lon] and keep the order nearer the origin.
    const dLonLat =
      Math.hypot(b0 - oLat, a0 - oLon); // rough degrees — enough for order detection
    const dLatLon = Math.hypot(a0 - oLat, b0 - oLon);
    if (dLatLon < dLonLat * 0.25 && dLatLon < 0.05) {
      return coords.map(([lat, lon]) => [lon, lat] as [number, number]);
    }
  } else {
    const sample = coords.slice(0, Math.min(20, coords.length));
    const latLonVotes = sample.filter(([a, b]) => a >= 15 && a <= 72 && b >= -170 && b <= -50).length;
    const lonLatVotes = sample.filter(([a, b]) => a >= -170 && a <= -50 && b >= 15 && b <= 72).length;
    if (latLonVotes > lonLatVotes && latLonVotes >= Math.max(2, Math.floor(sample.length / 2))) {
      return coords.map(([lat, lon]) => [lon, lat] as [number, number]);
    }
  }
  return coords;
};

/** True when a route is primarily on-base (both ends in DM, or ≥50% of GPS points). */
const isRouteInExclusionZone = (
  route: { origin?: { lat?: number | null; lon?: number | null }; destination?: { lat?: number | null; lon?: number | null }; route_geometry?: number[][] | null },
  rings: number[][][] | null,
): boolean => {
  if (!rings || rings.length === 0) return false;
  const oLat = route.origin?.lat;
  const oLon = route.origin?.lon;
  const dLat = route.destination?.lat;
  const dLon = route.destination?.lon;
  const originIn = oLat != null && oLon != null && isPointInAnyRing(oLat, oLon, rings);
  const destIn = dLat != null && dLon != null && isPointInAnyRing(dLat, dLon, rings);
  if (originIn && destIn) return true;

  const geom = normalizeRouteGeometry(route.route_geometry, route.origin);
  if (geom.length === 0) return originIn || destIn;

  let inside = 0;
  for (const [lon, lat] of geom) {
    if (isPointInAnyRing(lat, lon, rings)) inside += 1;
  }
  return inside / geom.length >= 0.5;
};

type MapBoundsBox = { west: number; south: number; east: number; north: number };

const isLatLonInBounds = (
  lat: number | null | undefined,
  lon: number | null | undefined,
  bounds: MapBoundsBox | null,
): boolean => {
  if (!bounds) return true;
  if (lat == null || lon == null || !Number.isFinite(lat) || !Number.isFinite(lon)) return false;
  const { west, south, east, north } = bounds;
  if (lat < south || lat > north) return false;
  // Antimeridian-spanning viewport: west > east.
  return west <= east ? lon >= west && lon <= east : lon >= west || lon <= east;
};

/** True when any route endpoint or geometry vertex is inside the map bounds. */
const isRouteInMapBounds = (
  route: {
    origin?: { lat?: number | null; lon?: number | null };
    destination?: { lat?: number | null; lon?: number | null };
    route_geometry?: number[][] | null;
  },
  bounds: MapBoundsBox | null,
): boolean => {
  if (!bounds) return true;
  if (isLatLonInBounds(route.origin?.lat, route.origin?.lon, bounds)) return true;
  if (isLatLonInBounds(route.destination?.lat, route.destination?.lon, bounds)) return true;
  const geom = normalizeRouteGeometry(route.route_geometry, route.origin);
  for (const [lon, lat] of geom) {
    if (isLatLonInBounds(lat, lon, bounds)) return true;
  }
  return false;
};

const isAnomalyInMapBounds = (anomaly: PolAnomaly, bounds: MapBoundsBox | null): boolean => {
  if (!bounds) return true;
  const loc = anomaly.expected_location;
  if (!loc) return false;
  if (isLatLonInBounds(loc.lat, loc.lon, bounds)) return true;
  if (isLatLonInBounds(loc.origin?.lat, loc.origin?.lon, bounds)) return true;
  if (isLatLonInBounds(loc.destination?.lat, loc.destination?.lon, bounds)) return true;
  return false;
};

// Convert map style type to Mapbox style URL
const getMapboxStyle = (style: MapStyleType): string => {
  const styles: Record<MapStyleType, string> = {
    streets: 'mapbox://styles/mapbox/streets-v12',
    satellite: 'mapbox://styles/mapbox/satellite-v9',
    'satellite-streets': 'mapbox://styles/mapbox/satellite-streets-v12',
    dark: 'mapbox://styles/mapbox/dark-v11',
    light: 'mapbox://styles/mapbox/light-v11',
    outdoors: 'mapbox://styles/mapbox/outdoors-v12',
  };
  return styles[style] || styles.dark;
};

// Chart color palette
const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#00C49F', '#FFBB28', '#FF8042', '#0088FE'];
const SEVERITY_COLORS = {
  low: '#4caf50',
  medium: '#ff9800',
  high: '#f44336',
  critical: '#9c27b0',
};

interface AnomalyTypeMeta {
  label: string;
  explanation: string;
  icon: SvgIconComponent;
  color: string;
}

const ANOMALY_TYPE_INFO: Record<string, AnomalyTypeMeta> = {
  ml_digital_anomaly: {
    label: 'Digital Behavior Anomaly',
    explanation: 'Digital activity (apps, calls, SMS) deviated significantly from this entity\'s established baseline.',
    icon: DigitalIcon,
    color: '#9c27b0',
  },
  ml_physical_anomaly: {
    label: 'Physical Movement Anomaly',
    explanation: 'GPS movement pattern (locations visited, dwell times, distances) was unusual for this entity.',
    icon: DirectionsRunIcon,
    color: '#7e57c2',
  },
  high_activity: {
    label: 'Unusually High Activity',
    explanation: 'Significantly more dwells/movement than this entity\'s daily average.',
    icon: TrendingUpIcon,
    color: '#ef5350',
  },
  low_activity: {
    label: 'Unusually Low Activity',
    explanation: 'Significantly less activity than expected based on historical patterns.',
    icon: TrendingDownIcon,
    color: '#42a5f5',
  },
  new_location: {
    label: 'New Location Visited',
    explanation: 'Entity visited a location not seen in their established routine.',
    icon: AddLocationAltIcon,
    color: '#26a69a',
  },
  unusual_time: {
    label: 'Activity at Unusual Time',
    explanation: 'Activity detected outside of this entity\'s normal active hours.',
    icon: TimeIcon,
    color: '#ffa726',
  },
  unusual_location: {
    label: 'Unusual Location',
    explanation: 'Entity spent time at a location that deviates from their typical pattern.',
    icon: WrongLocationIcon,
    color: '#ec407a',
  },
  no_activity: {
    label: 'No Activity Detected',
    explanation: 'Entity was expected to be active but no data was recorded.',
    icon: BlockIcon,
    color: '#78909c',
  },
  routine_break: {
    label: 'Routine Break',
    explanation: 'Entity broke an established recurring pattern (e.g., missed a regular visit).',
    icon: LinkOffIcon,
    color: '#f44336',
  },
  new_pattern: {
    label: 'New Pattern Emerging',
    explanation: 'A previously unseen behavioral pattern has been detected for this entity.',
    icon: AutoAwesomeIcon,
    color: '#5c6bc0',
  },
  // ---------- Statistical detectors (Phase 1+ pipeline upgrades) ----------
  time_shift: {
    label: 'Off-Schedule Visit',
    explanation:
      'Entity arrived at a recurring location at an hour outside their established arrival distribution (median +/- MAD baseline).',
    icon: ScheduleSendIcon,
    color: '#fb8c00',
  },
  dwell_duration_anomaly: {
    label: 'Unusual Dwell Duration',
    explanation:
      'Stay duration at this location is well outside the historical median for this entity (long enough loiter or unusually brief drop-in).',
    icon: HourglassTopIcon,
    color: '#7e57c2',
  },
  lapse_break: {
    label: 'Missed Recurring Visit',
    explanation:
      'Entity has not returned to a high-confidence recurring location for substantially longer than their typical inter-visit interval.',
    icon: EventBusyIcon,
    color: '#d84315',
  },
  route_deviation: {
    label: 'Route Deviation',
    explanation:
      'Trip along a known recurring route had abnormal duration or distance compared to that route\'s historical instances (potential reroute, stop, or surveillance maneuver).',
    icon: AltRouteIcon,
    color: '#00838f',
  },
  new_relationship: {
    label: 'New Relationship',
    explanation:
      'First-time co-location or co-travel detected between two entities not previously correlated in the dataset.',
    icon: PersonAddAltIcon,
    color: '#3949ab',
  },
};

const getAnomalyTypeMeta = (type: string): AnomalyTypeMeta => {
  return ANOMALY_TYPE_INFO[type.toLowerCase()] || {
    label: type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
    explanation: '',
    icon: HelpOutlineIcon,
    color: '#9e9e9e',
  };
};

/**
 * Anomaly types the current pipeline is capable of producing.
 * The Anomalies tab filter shows these in a fixed order (always visible, even
 * with zero counts so analysts can see the full detector menu) and falls back
 * to also showing any unexpected types that appear in live data.
 */
const CORE_ANOMALY_TYPES: string[] = [
  // ML autoencoder, day-level
  'ml_physical_anomaly',
  'ml_digital_anomaly',
  // Statistical event-level detectors
  'time_shift',
  'dwell_duration_anomaly',
  'lapse_break',
  'route_deviation',
  'new_relationship',
  'unusual_location',
  // Pattern-based
  'routine_break',
];

/** Short labels used by the KPI-style anomaly type indicators (must fit in ~58px). */
const ANOMALY_TYPE_SHORT_LABEL: Record<string, string> = {
  ml_physical_anomaly: 'AI Phys',
  ml_digital_anomaly: 'AI Digital',
  time_shift: 'Off-Sched',
  dwell_duration_anomaly: 'Dwell',
  lapse_break: 'Missed',
  route_deviation: 'Route',
  new_relationship: 'New Rel',
  unusual_location: 'Unusual',
  routine_break: 'Routine',
  high_activity: 'High Act',
  low_activity: 'Low Act',
  new_location: 'New Loc',
  unusual_time: 'Off Hrs',
  no_activity: 'No Act',
  new_pattern: 'New Pat',
};

// Modern sleek scrollbar styling
const sleekScrollbar = {
  '&::-webkit-scrollbar': {
    width: '6px',
    height: '6px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(255, 255, 255, 0.3)',
    borderRadius: '3px',
    '&:hover': {
      background: 'rgba(255, 255, 255, 0.5)',
    },
  },
  scrollbarWidth: 'thin',
  scrollbarColor: 'rgba(255, 255, 255, 0.3) transparent',
};

const compactScrollbar = {
  '&::-webkit-scrollbar': {
    width: '4px',
    height: '4px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(255, 255, 255, 0.3)',
    borderRadius: '2px',
    '&:hover': {
      background: 'rgba(255, 255, 255, 0.5)',
    },
  },
  scrollbarWidth: 'thin',
  scrollbarColor: 'rgba(255, 255, 255, 0.3) transparent',
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = ({ children, value, index }: TabPanelProps & { children?: any }) => (
  <Box role="tabpanel" hidden={value !== index} sx={{ height: '100%', overflow: 'auto', ...sleekScrollbar }}>
    {value === index && children}
  </Box>
);

/** Order must match POL_TAB / the right-panel <Tabs>. */
const TAB_DESCRIPTIONS: { title: string; description: string }[] = [
  {
    title: 'Entities',
    description:
      'Behavioral profiles for each tracked entity. Shows routine, mobility, and social scores computed from GPS dwell patterns, travel distances, and communication metadata. Weekly heatmaps show per-entity activity intensity by hour and day. Scores are re-computed nightly by the PoL pipeline and normalized against the entity\'s own historical baseline.',
  },
  {
    title: 'Activity',
    description:
      'Temporal activity heatmap showing aggregate event counts broken down by hour of day and day of week. Data comes from the dashboard summary for the selected time range and entity filter. Use this to identify peak activity windows, quiet periods, and weekly rhythms across the dataset.',
  },
  {
    title: 'Patterns',
    description:
      'Recurring location-visit patterns detected across all entities. The pipeline groups dwell events by location, day-of-week, and time window, then scores each pattern by frequency, adherence rate, and statistical confidence. Only patterns exceeding a minimum confidence threshold appear here. Patterns are classified as active, emerging, or fading based on recent adherence.',
  },
  {
    title: 'Correlations',
    description:
      'Relationships between entities detected through co-location (overlapping dwells at the same place/time) and co-travel (similar route patterns). Correlation strength is computed from shared occurrence count, average temporal overlap, and spatial proximity. Higher occurrence counts and tighter time deltas produce stronger scores.',
  },
  {
    title: 'Anomalies',
    description:
      'Individual anomaly EVENTS — each card is one specific moment that deviated from baseline. Two layers of detectors feed this list: (1) per-entity LSTM autoencoders flag whole days whose embedding-space reconstruction error exceeds the 95th percentile (ml_physical_anomaly, ml_digital_anomaly); (2) statistical detectors using median + MAD baselines per entity per location flag specific events — off-schedule visits (time_shift), unusual dwell durations, missed recurring visits (lapse_break), route deviations, first-time co-locations (new_relationship), and unusual locations. Use the indicator strip at the top to filter to a single detector type at a time. For a per-day RANKING that fuses everything into one score, see the Outlier Rankings tab.',
  },
  {
    title: 'Outlier Rankings',
    description:
      'Daily atypicality RANKINGS — each card is one (entity, day) pair, scored by a composite atypicality_score from 0 to 1 that fuses mobility deviation (how unusual the day\'s travel was vs. the entity\'s own median) and anomaly density (count of detectors that fired, weighted by severity). Use this tab to surface outlier days — "which entity had the most interesting day?" — and decide where to look first. Click a card to focus that entity in the rest of the dashboard. The Anomalies tab shows the underlying events; this tab shows the rolled-up score.',
  },
  {
    title: 'Forecast',
    description:
      'Forward-looking view for the selected entity, combining four predictors. (1) Right Now panel at the top polls every 60 seconds and reports the entity\'s most recent dwell/track, today\'s expected activity for the current hour, and the top likely next stops from the Markov chain (or pol_route_patterns when the chain is too sparse). (2) Expected Day timeline below it synthesizes a 24-hour forecast of visits, transits, co-presence and digital activity from recurring_patterns + pol_route_patterns + entity_correlations + digital_patterns + a 30-day mobility baseline — no ML, just the Phase 1 baseline fusion, with prediction_confidence and data_maturity reported transparently. (3) Phase 3 adds a per-entity LSTM next-day forecaster (model_type=\'temporal_forecaster\' in embedding_models) that predicts tomorrow\'s 48-feature vector and computes a surprise_score = 1 - cosine(predicted, actual) once the target date passes. (4) Quality cell in the KPI strip shows rolling backtest accuracy from entity_prediction_backtests so analysts know how much to trust the forecast. Export Brief generates a Day Ahead PowerPoint with AI narrative.',
  },
  {
    title: 'Routes',
    description:
      'GPS route patterns grouped by origin and destination. The pipeline extracts travel segments between consecutive dwells, clusters them by start/end location, and aggregates statistics across all matching trips. Usage count, peak hour/day, average distance and duration, and behavioral tags are computed per route. Route geometry is built by merging GPS points from the densest trip instances.',
  },
  {
    title: 'Dwells',
    description:
      'Individual dwell events for the selected entities and time range. Shows every detected stay (GPS cluster > 5 min) with location, duration, device type, and place category. Use filters to isolate long dwells, specific devices, or sort by duration. Click a row to zoom the map to that dwell location. Statistics show aggregate duration breakdown by time bucket.',
  },
  {
    title: 'Digital',
    description:
      'Digital behavior patterns extracted from phone metadata: app usage, browsing activity, call/SMS communication, and contact diversity. Patterns are categorized and scored by confidence. The Digital Insights sub-panel uses embedding-based anomaly detection (96-dimensional feature vectors) to flag days where digital behavior deviates from baseline.',
  },
];

const PatternOfLifePage: React.FC = () => {
  const { singular } = useParticipantLabels();
  const { courseSingular } = useCourseLabels();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const mapRef = useRef<MapRef | null>(null);
  const digitalMapReturnViewRef = useRef<{ longitude: number; latitude: number; zoom: number } | null>(null);
  const popupClassName = DARK_MAP_POPUP_CLASS;
  
  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dashboardData, setDashboardData] = useState<DashboardResponse | null>(null);
  
  // Filters
  const [periodType] = useState<'daily' | 'weekly' | 'monthly'>('weekly');
  const [daysBack, setDaysBack] = useState(7); // Default to 7 days
  const [selectedCourse, setSelectedCourse] = useState<string>('');
  const [selectedEntity, setSelectedEntity] = useState<string | null>(null);
  const [selectedDeviceType, setSelectedDeviceType] = useState<string>('');
  
  // Davis-Monthan exclusion zone filter: 'off' = show all, 'exclude' = hide DM data
  const [dmFilter, setDmFilter] = useState<'off' | 'exclude'>('off');
  
  // Davis-Monthan polygon from neighborhoods layer (fetched from API)
  const [dmPolygon, setDmPolygon] = useState<GeoJSON.Feature | null>(null);
  /** Outer rings ([lon,lat][]) for point-in-polygon — supports Polygon + MultiPolygon */
  const [dmPolygonRings, setDmPolygonRings] = useState<number[][][] | null>(null);
  
  // Date range mode - 'preset' uses daysBack, 'custom' uses explicit dates
  const [dateRangeMode, setDateRangeMode] = useState<'preset' | 'custom'>('preset');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  // Embedding info dialog
  const [embeddingInfoOpen, setEmbeddingInfoOpen] = useState(false);

  // Tab info dialog — stores which tab index is open, or null
  const [tabInfoOpen, setTabInfoOpen] = useState<number | null>(null);

  // LinkAnalysis-style Date Range dialog state
  const [dateDialogOpen, setDateDialogOpen] = useState(false);
  const [localDateRange, setLocalDateRange] = useState<{ start: Date | null; end: Date | null }>({ start: null, end: null });

  const toISODate = useCallback((d: Date) => d.toISOString().split('T')[0], []);

  const analysisPeriodLabel = useMemo(() => {
    if (dateRangeMode === 'custom' && startDate && endDate) {
      return `${startDate} – ${endDate}`;
    }
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - daysBack);
    return `${toISODate(start)} – ${toISODate(end)} (${daysBack} days)`;
  }, [dateRangeMode, startDate, endDate, daysBack, toISODate]);
  
  // Device types available
  const deviceTypes = [
    { id: '', label: 'All Devices' },
    { id: 'phone', label: 'Phone (Track + Digital PoL)' },
    { id: 'watch', label: 'Watch (Track Only)' },
    { id: 'vehicle', label: 'Vehicle (Track Only)' },
  ];
  
  // Dynamic courses from API
  const [availableCourses, setAvailableCourses] = useState<{
    id: string;
    label: string;
    entity_count?: number;
    dwell_count?: number;
    earliest_date?: string | null;
    latest_date?: string | null;
  }[]>([]);
  const [coursesLoading, setCoursesLoading] = useState(true);
  const [coursesError, setCoursesError] = useState<string | null>(null);
  
  // UI State
  const [activeTab, setActiveTab] = useState(0);
  const effectiveAccess = useEffectiveAccess();
  const polTabAllowed = useCallback((baseLabel: string) => {
    const key = POL_TAB_PERMISSION_BY_LABEL[baseLabel];
    if (!key) return true;
    return hasPermission(effectiveAccess, key);
  }, [effectiveAccess]);

  const POL_BASE_LABELS = ['Entities','Activity','Patterns','Correlations','Anomalies','Outlier Rankings','Forecast','Routes','Dwells','Digital'] as const;
  useEffect(() => {
    const label = POL_BASE_LABELS[activeTab];
    if (label && !polTabAllowed(label)) {
      const first = POL_BASE_LABELS.findIndex((l) => polTabAllowed(l));
      if (first >= 0) setActiveTab(first);
    }
  }, [activeTab, polTabAllowed]);
  // Map style based on theme: light mode = streets, dark mode = dark
  const [mapStyle, setMapStyle] = useState<MapStyleType>(() => 
    theme.palette.mode === 'light' ? 'streets' : 'dark'
  );
  const [viewport, setViewport] = useState({
    longitude: -112.07,
    latitude: 33.45,
    zoom: 12,
  });
  const [selectedMarker, setSelectedMarker] = useState<{
    lat: number;
    lon: number;
    name: string;
    visits: number;
    entities: string[];
    isCluster?: boolean;
    locationCount?: number;
    metricLabel?: string;
    detail?: string;
  } | null>(null);
  
  // Map control state
  const [is3DMode, setIs3DMode] = useState(false);
  // Crop right-panel tabs to the current map viewport (map markers stay full).
  const [filterByMapExtent, setFilterByMapExtent] = useState(false);
  const [mapBounds, setMapBounds] = useState<MapBoundsBox | null>(null);
  const [showLegend, setShowLegend] = useState(false);
  const [bookmarkAnchorEl, setBookmarkAnchorEl] = useState<null | HTMLElement>(null);
  const [styleAnchorEl, setStyleAnchorEl] = useState<null | HTMLElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isMeasuring, setIsMeasuring] = useState(false);
  const [measurementPoints, setMeasurementPoints] = useState<[number, number][]>([]);
  const [totalDistance, setTotalDistance] = useState<number>(0);
  const geocoderRef = useRef<MapboxGeocoder | null>(null);
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  
  // City bookmarks for quick navigation
  const cityBookmarks = [
    { name: 'Tucson', longitude: -110.9265, latitude: 32.2217, zoom: 11 },
    { name: 'Phoenix', longitude: -112.0740, latitude: 33.4484, zoom: 11 },
    { name: 'Marana', longitude: -111.2253, latitude: 32.4367, zoom: 12 },
    { name: 'Camp Pendleton', longitude: -117.3574, latitude: 33.3032, zoom: 11 },
  ];
  
  // Selected route for map visualization (legacy - location sequences)
  const [selectedRoute, setSelectedRoute] = useState<LocationSequence | null>(null);
  useDarkMapPopupChrome(!!selectedMarker || !!selectedRoute);

  // Rich route patterns with GPS geometry
  const [routePatterns, setRoutePatterns] = useState<RoutePattern[]>([]);
  const [selectedRoutePattern, setSelectedRoutePattern] = useState<RoutePattern | null>(null);
  const [routePatternsLoading, setRoutePatternsLoading] = useState(false);
  const [showAllRoutes, setShowAllRoutes] = useState(true);
  const [routeUsageFilter, setRouteUsageFilter] = useState({ high: true, medium: true, low: true });
  const [routeFilterOpen, setRouteFilterOpen] = useState(false);
  const [routeRepeatFilter, setRouteRepeatFilter] = useState<'repeated' | 'single' | 'all'>('repeated');
  
  // Route comparison state
  const [compareRoutes, setCompareRoutes] = useState<(RoutePattern & { is_primary?: boolean })[]>([]);
  const [isComparing, setIsComparing] = useState(false);
  const [hiddenCompareRoutes, setHiddenCompareRoutes] = useState<Set<number>>(new Set());
  
  // Digital patterns state
  const [digitalPatterns, setDigitalPatterns] = useState<DigitalPattern[]>([]);
  const [digitalPatternsLoading, setDigitalPatternsLoading] = useState(false);
  const [digitalCategory, setDigitalCategory] = useState<string>('all');
  const [digitalSimilarities, setDigitalSimilarities] = useState<DigitalSimilarity[]>([]);
  const [digitalInsights, setDigitalInsights] = useState<DigitalInsightsResponse | null>(null);
  const [compareLoading, setCompareLoading] = useState(false);
  
  // Dwell tab data
  const [dwellTabData, setDwellTabData] = useState<TimelineDwell[]>([]);
  const [dwellTabLoading, setDwellTabLoading] = useState(false);
  const [flashDwellLocation, setFlashDwellLocation] = useState<{ lat: number; lon: number } | null>(null);
  const [kpiFilter, setKpiFilter] = useState<'all' | 'dwells' | 'routes' | 'calls' | 'sms' | 'apps' | 'browsing'>('all');
  const [wheelCellFilter, setWheelCellFilter] = useState<{ hour: number; day: number } | null>(null);
  const [selectedDigitalEvents, setSelectedDigitalEvents] = useState<{
    events: HourlyEvent[];
    slot: { day: number; hour: number };
  } | null>(null);

  // Selected items for map visualization (click-to-zoom)
  const [selectedCorrelation, setSelectedCorrelation] = useState<EntityCorrelation | null>(null);
  const [selectedPattern, setSelectedPattern] = useState<RecurringPattern | null>(null);
  const [selectedAnomaly, setSelectedAnomaly] = useState<PolAnomaly | null>(null);
  // Empty set = "show all". Adding a type key restricts the visible cards to those types.
  // Single-select filter for the Anomalies tab type indicators (null = no filter).
  const [selectedAnomalyType, setSelectedAnomalyType] = useState<string | null>(null);
  /** Layer-1 ML summary for the selected entity — used so entity cards don't claim "normal" when ML flagged days. */
  const [selectedEntityMlSummary, setSelectedEntityMlSummary] = useState<AnomalySummary | null>(null);

  // Triage tab: when a daily-atypicality card is clicked, focus the map and
  // the inline detail panel on that (entity, day) pair. Null = no day focus.
  // The map will show only that day's anomalies, auto-fit to their bounds, and
  // the clicked card expands with the underlying event list.
  const [selectedTriageDay, setSelectedTriageDay] = useState<{ entityId: string; day: string } | null>(null);
  
  // Sentinel panel state
  const [showSentinel, setShowSentinel] = useState(true);

  // PPTX export state
  const [pptxThemeOpen, setPptxThemeOpen] = useState(false);
  const [pptxTheme, setPptxTheme] = useState<PowerPointTheme>('standard');
  const [pptxExportEntityId, setPptxExportEntityId] = useState<string | null>(null);
  const [pptxExportNarrative, setPptxExportNarrative] = useState<string | undefined>();
  const [pptxExporting, setPptxExporting] = useState(false);
  const [exportSnackbar, setExportSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({ open: false, message: '', severity: 'info' });

  // Outlier Rankings Brief export state — focused per-entity export from the
  // Outlier Rankings tab (previously called "Daily Review" / "Triage Brief").
  // Independent from the full POL pptx export so a running brief render does
  // not block (or get blocked by) the comprehensive dashboard export.
  const [briefExporting, setBriefExporting] = useState(false);
  
  // Resizable panel state
  const [rightPanelWidth, setRightPanelWidth] = useState(600);
  const [isResizing, setIsResizing] = useState(false);
  const resizeRef = useRef<{ startX: number; startWidth: number } | null>(null);
  
  // Fetch dashboard data
  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Only use custom dates if both are set
      const useCustomDates = dateRangeMode === 'custom' && startDate && endDate;
      const data = await polService.getDashboard(
        periodType,
        daysBack,
        selectedCourse || undefined,
        selectedEntity ? [selectedEntity] : undefined,
        selectedDeviceType || undefined,
        useCustomDates ? startDate : undefined,
        useCustomDates ? endDate : undefined
      );
      setDashboardData(data);
      
      // Update map viewport based on data
      if (data.entities.length > 0) {
        const firstEntity = data.entities[0];
        if (firstEntity.top_locations && firstEntity.top_locations.length > 0) {
          const loc = firstEntity.top_locations[0];
          if (loc.latitude && loc.longitude) {
            setViewport(prev => ({
              ...prev,
              latitude: loc.latitude,
              longitude: loc.longitude,
            }));
          }
        }
      }
    } catch (err: any) {
      console.error('Dashboard fetch error:', err);
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [periodType, daysBack, selectedCourse, selectedEntity, selectedDeviceType, dateRangeMode, startDate, endDate]);
  
  // Fetch courses on mount
  useEffect(() => {
    const fetchCourses = async () => {
      setCoursesLoading(true);
      setCoursesError(null);
      try {
        const data = await polService.getCourses();
        // Backend excludes Exercise Support, shows all training courses
        setAvailableCourses(data.courses.map((c: CourseInfo) => ({
          id: c.id,
          label: `${c.id} (${c.entity_count} entities, ${c.dwell_count} dwells)`,
          entity_count: c.entity_count,
          dwell_count: c.dwell_count,
          earliest_date: c.earliest_date,
          latest_date: c.latest_date,
        })));
      } catch (err: unknown) {
        console.error('Failed to fetch courses:', err);
        const ax = err as { response?: { data?: { detail?: string } }; message?: string };
        const detail = ax?.response?.data?.detail;
        const msg =
          typeof detail === 'string'
            ? detail
            : ax?.message || 'Could not load domains for Pattern of Life.';
        setCoursesError(msg);
        setAvailableCourses([]);
      } finally {
        setCoursesLoading(false);
      }
    };
    fetchCourses();
  }, []);

  // Fetch dwells for Dwell Analysis tab when dashboard data changes
  useEffect(() => {
    if (!dashboardData || !selectedCourse) {
      setDwellTabData([]);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setDwellTabLoading(true);
      try {
        const useCustom = dateRangeMode === 'custom' && startDate && endDate;
        const resp = await polService.getTimelineData(
          selectedCourse || undefined,
          selectedEntity ? [selectedEntity] : undefined,
          daysBack,
          useCustom ? startDate : undefined,
          useCustom ? endDate : undefined,
        );
        if (!cancelled) setDwellTabData(resp.dwells ?? []);
      } catch (err) {
        console.warn('[PoL] Dwell tab fetch failed:', err);
        if (!cancelled) setDwellTabData([]);
      } finally {
        if (!cancelled) setDwellTabLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [dashboardData, selectedCourse, selectedEntity, daysBack, dateRangeMode, startDate, endDate]);

  // Fetch Davis-Monthan polygon from neighborhoods layer on mount
  useEffect(() => {
    const fetchDMPolygon = async () => {
      try {
        // Prefer hyphenated query; backend normalizes '-' to wildcards so
        // "Davis Monthan AFB" also matches.
        const data = await polService.getExclusionZones('Davis-Monthan');
        const zone = (data.zones || []).find((z) => z.geojson) || data.zones?.[0];
        if (zone?.geojson) {
          setDmPolygon({
            type: 'Feature',
            properties: { name: zone.name },
            geometry: zone.geojson,
          });
          const rings = extractPolygonRings(zone.geojson);
          setDmPolygonRings(rings.length > 0 ? rings : null);
          console.log(
            '[POL] Loaded Davis-Monthan polygon:',
            zone.name,
            rings.reduce((n, r) => n + r.length, 0),
            'points across',
            rings.length,
            'ring(s)',
          );
        } else {
          console.warn('[POL] No Davis-Monthan exclusion zone found in neighborhoods');
        }
      } catch (err) {
        console.error('Failed to fetch Davis-Monthan polygon:', err);
      }
    };
    fetchDMPolygon();
  }, []);

  // Only fetch dashboard when a course is selected
  useEffect(() => {
    if (selectedCourse) {
      fetchDashboard();
    } else {
      // Clear data when no course selected
      setDashboardData(null);
    }
  }, [fetchDashboard, selectedCourse]);

  // Auto-set date range when course is selected - default to last 7 days via preset
  useEffect(() => {
    if (selectedCourse && availableCourses.length > 0) {
      // Default to last 7 days via dropdown preset (not custom date range)
      setDaysBack(7);
      setDateRangeMode('preset');
      setStartDate('');
      setEndDate('');
    }
  }, [selectedCourse, availableCourses]);
  
  // Fetch route patterns with GPS geometry
  const fetchRoutePatterns = useCallback(async () => {
    setRoutePatternsLoading(true);
    try {
      const minUsage = routeRepeatFilter === 'repeated' ? 2 : 1;
      const useCustomDates = dateRangeMode === 'custom' && startDate && endDate;
      let computedStart: string | undefined;
      let computedEnd: string | undefined;
      if (useCustomDates) {
        computedStart = startDate;
        computedEnd = endDate;
      } else if (daysBack) {
        const end = new Date();
        const start = new Date();
        start.setDate(end.getDate() - daysBack);
        computedStart = start.toISOString().split('T')[0];
        computedEnd = end.toISOString().split('T')[0];
      }
      const data = await polService.getRoutePatterns(
        selectedEntity || undefined,
        selectedCourse || undefined,
        minUsage,
        100, // limit
        selectedDeviceType || undefined,
        computedStart,
        computedEnd
      );
      setRoutePatterns(data.routes);
    } catch (err: any) {
      console.error('Route patterns fetch error:', err);
    } finally {
      setRoutePatternsLoading(false);
    }
  }, [selectedEntity, selectedCourse, selectedDeviceType, routeRepeatFilter, dateRangeMode, startDate, endDate, daysBack]);
  
  useEffect(() => {
    fetchRoutePatterns();
  }, [fetchRoutePatterns]);
  
  // Fetch digital patterns
  const fetchDigitalPatterns = useCallback(async () => {
    setDigitalPatternsLoading(true);
    try {
      const category = digitalCategory === 'all' ? undefined : digitalCategory;
      const entityIds = selectedEntity ? [selectedEntity] : undefined;
      const data = await polService.getDigitalPatterns(
        selectedCourse || undefined,
        entityIds,
        category,
        0.4  // min confidence
      );
      setDigitalPatterns(data.patterns);
      
      // Also fetch similarities
      const simData = await polService.getDigitalSimilarity(undefined, 0.7);
      setDigitalSimilarities(simData.similarities);

      // Fetch embedding-based digital insights (pass explicit dates when in custom mode)
      try {
        const useCustomDates = dateRangeMode === 'custom' && startDate && endDate;
        const insights = await polService.getDigitalInsights(
          selectedCourse || undefined,
          entityIds,
          daysBack,
          useCustomDates ? startDate : undefined,
          useCustomDates ? endDate : undefined,
        );
        setDigitalInsights(insights);
      } catch (insightErr) {
        console.warn('Digital insights not available (embeddings may not exist):', insightErr);
      }
    } catch (err: any) {
      console.error('Digital patterns fetch error:', err);
    } finally {
      setDigitalPatternsLoading(false);
    }
  }, [selectedEntity, selectedCourse, digitalCategory, daysBack, dateRangeMode, startDate, endDate]);
  
  useEffect(() => {
    fetchDigitalPatterns();
  }, [fetchDigitalPatterns]);
  
  // POL PowerPoint export handler
  const handlePOLExport = useCallback(async () => {
    if (!pptxExportEntityId || !dashboardData) return;
    setPptxExporting(true);
    setPptxThemeOpen(false);
    setExportSnackbar({ open: true, message: `Generating AI insights for ${pptxExportEntityId}...`, severity: 'info' });
    try {
      const entity = dashboardData.entities.find(e => e.entity_id === pptxExportEntityId);
      if (!entity) {
        setExportSnackbar({ open: true, message: `Entity ${pptxExportEntityId} not found in dashboard data`, severity: 'error' });
        setPptxExporting(false);
        return;
      }

      const entityPatterns = (dashboardData.top_patterns || [])
        .filter(p => p.entity_id === pptxExportEntityId)
        .map(p => ({
          pattern_type: p.pattern_type,
          location_name: p.location_name,
          frequency: p.frequency,
          confidence_score: p.confidence_score,
          occurrence_count: p.occurrence_count,
          days_of_week: p.days_of_week,
          time_window_start: p.time_window_start,
          time_window_end: p.time_window_end,
          avg_duration_minutes: p.avg_duration_minutes,
        }));

      const loadedStudentIds = new Set(dashboardData.entities.map((e) => e.entity_id));
      const entityCorrelations = (dashboardData.top_correlations || [])
        .filter(c => c.entity_a_id === pptxExportEntityId || c.entity_b_id === pptxExportEntityId)
        .filter((c) => {
          const other = c.entity_a_id === pptxExportEntityId ? c.entity_b_id : c.entity_a_id;
          return loadedStudentIds.size <= 1 || loadedStudentIds.has(other);
        })
        .map(c => ({
          other_entity_id: c.entity_a_id === pptxExportEntityId ? c.entity_b_id : c.entity_a_id,
          correlation_type: c.correlation_type,
          correlation_strength: c.correlation_strength,
          occurrence_count: c.occurrence_count,
          shared_locations: c.shared_locations?.map(l => ({ name: l.name, count: l.count })) || [],
          avg_time_delta_minutes: c.avg_time_delta_minutes,
        }));

      const entityAnomalies = (dashboardData.recent_anomalies || [])
        .filter(a => a.entity_id === pptxExportEntityId)
        .map(a => ({
          anomaly_type: a.anomaly_type,
          severity: a.severity,
          expected_behavior: a.expected_behavior,
          actual_behavior: a.actual_behavior,
          deviation_score: a.deviation_score,
          detected_at: a.detected_at,
        }));

      // Tally anomalies by detector type for the type-breakdown row on the slide
      // and for the AI context. Mirrors the KPI strip in the Anomalies tab.
      const entityAnomalyTypeBreakdown: Record<string, number> = {};
      entityAnomalies.forEach((a) => {
        const k = (a.anomaly_type || 'unknown').toLowerCase();
        entityAnomalyTypeBreakdown[k] = (entityAnomalyTypeBreakdown[k] || 0) + 1;
      });

      // Pull this entity's top atypical days from the dashboard rollup so the
      // Triage slide and AI narrative can reason about score-ranked days.
      const entityAtypicalDays = (dashboardData.top_atypical_days || [])
        .filter((d: any) => d.entity_id === pptxExportEntityId)
        .map((d: any) => ({
          entity_id: d.entity_id,
          date: d.date,
          atypicality_score: d.atypicality_score,
          total_anomaly_count: d.total_anomaly_count,
          total_dwells: d.total_dwells,
          unique_locations: d.unique_locations,
          total_distance_km: d.total_distance_km,
          anomaly_breakdown: d.anomaly_breakdown,
        }));

      const entityRoutes = routePatterns
        .filter(r => r.entity_id === pptxExportEntityId)
        .map(r => ({
          origin_name: r.origin?.name || 'Unknown',
          destination_name: r.destination?.name || 'Unknown',
          usage_count: r.usage_count,
          avg_duration_seconds: r.avg_duration_seconds,
          behavioral_tags: r.behavioral_tags || [],
        }));

      const entityDigital = digitalPatterns
        .filter(d => d.entity_id === pptxExportEntityId)
        .map(d => ({
          category: d.category,
          name: d.name,
          description: d.description,
          occurrence_count: d.occurrence_count,
          confidence: d.confidence,
          time_of_day: d.time_of_day,
        }));

      const entityFingerprints = digitalInsights?.entity_fingerprints?.[pptxExportEntityId]?.map(fp => ({
        category: fp.category,
        name: fp.name,
        description: fp.description,
        confidence: fp.confidence,
      })) || [];

      let mapShot;
      try {
        // Open the legend for the export screenshot
        const legendWasOpen = showLegend;
        if (!legendWasOpen) setShowLegend(true);
        await new Promise(r => setTimeout(r, 500));

        const mapEl = document.getElementById('pol-map-container');
        const canvas = mapEl?.querySelector('canvas.mapboxgl-canvas') as HTMLCanvasElement | null;
        if (canvas && mapEl) {
          const mapInstance = mapRef.current?.getMap?.();
          if (mapInstance?.triggerRepaint) mapInstance.triggerRepaint();
          await new Promise(r => setTimeout(r, 300));

          // Capture the full container (map + legend overlay) using html2canvas
          try {
            // @ts-ignore - html2canvas is available at runtime
            const html2canvas = (await import('html2canvas')).default;
            const scale = 2;
            const w = mapEl.clientWidth;
            const h = mapEl.clientHeight;
            const shot = await html2canvas(mapEl, {
              useCORS: true, allowTaint: true, scale, logging: false,
              width: w, height: h, backgroundColor: null,
            });
            const dataUrl = shot.toDataURL('image/png');
            if (dataUrl && dataUrl.length > 10000) {
              mapShot = { dataUrl, width: w * scale, height: h * scale, aspectRatio: w / h };
            }
          } catch {
            // Fallback: canvas only (no legend)
            const dataUrl = canvas.toDataURL('image/png');
            if (dataUrl && dataUrl.length > 5000) {
              mapShot = { dataUrl, width: canvas.clientWidth || canvas.width, height: canvas.clientHeight || canvas.height, aspectRatio: (canvas.clientWidth || canvas.width) / (canvas.clientHeight || canvas.height) };
            }
          }
        }

        // Restore legend state
        if (!legendWasOpen) setShowLegend(false);
      } catch { /* map capture is optional */ }

      // Generate AI narrative for the entity
      let aiNarrative = pptxExportNarrative;
      if (!aiNarrative) {
        setExportSnackbar({ open: true, message: `Generating AI analysis for ${pptxExportEntityId}...`, severity: 'info' });
        try {
          const aiContext = {
            selectedEntity: pptxExportEntityId,
            selectedCourse: selectedCourse || 'All',
            timeRange: analysisPeriodLabel,
            summary: {
              totalEntities: dashboardData.entities.length,
              totalPatterns: entityPatterns.length,
              totalCorrelations: entityCorrelations.length,
              totalAnomalies: entityAnomalies.length,
              totalRoutes: entityRoutes.length,
            },
            entities: [{
              id: entity.entity_id,
              mobility_score: entity.mobility_score,
              social_score: entity.social_score,
              total_locations: entity.unique_locations,
              total_dwells: entity.total_dwells,
              top_locations: entity.top_locations?.slice(0, 5).map(l => l.location_name),
            }],
            patterns: entityPatterns.slice(0, 10).map(p => ({
              entity: pptxExportEntityId, type: p.pattern_type, location: p.location_name,
              frequency: p.frequency, confidence: p.confidence_score,
            })),
            correlations: entityCorrelations.slice(0, 10).map(c => ({
              entity1: pptxExportEntityId, entity2: c.other_entity_id,
              type: c.correlation_type, strength: c.correlation_strength,
            })),
            anomalies: entityAnomalies.slice(0, 15).map(a => ({
              entity: pptxExportEntityId, type: a.anomaly_type, severity: a.severity,
              expected: a.expected_behavior, actual: a.actual_behavior,
              deviation_score: a.deviation_score, detected_at: a.detected_at,
            })),
            // Layer-2 detector breakdown so the AI can describe WHICH behavioral
            // signals are firing (route deviation vs off-schedule vs lapse, etc.)
            anomalyTypeBreakdown: entityAnomalyTypeBreakdown,
            // Layer-3 daily atypicality rollup (Triage view). Lets the AI rank days
            // and explain whether high scores are detector-driven or mobility-driven.
            topAtypicalDays: entityAtypicalDays.slice(0, 10).map((d) => ({
              date: d.date, atypicality_score: d.atypicality_score,
              total_anomaly_count: d.total_anomaly_count, total_dwells: d.total_dwells,
              unique_locations: d.unique_locations, total_distance_km: d.total_distance_km,
              anomaly_breakdown: d.anomaly_breakdown,
            })),
            routes: entityRoutes.slice(0, 8).map(r => ({
              entity: pptxExportEntityId, origin: r.origin_name, destination: r.destination_name,
              usage_count: r.usage_count,
            })),
            digitalPatterns: entityDigital.slice(0, 10).map(d => ({
              entity: pptxExportEntityId, category: d.category, name: d.name,
              description: d.description, occurrence_count: d.occurrence_count, confidence: d.confidence,
            })),
          };
          const aiResp = await fetch('/api/pol-chat/ask', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}`,
            },
            body: JSON.stringify({
              question: `Generate a comprehensive Pattern of Life executive summary for ${pptxExportEntityId} for a PowerPoint briefing. Cover: 1) Behavioral profile overview (mobility, social, routine scores), 2) Key recurring patterns and locations, 3) Notable correlations with other entities, 4) Anomalies and behavioral deviations — distinguish between Layer 1 ML autoencoder anomalies (ml_*) and Layer 2 statistical detectors (time_shift, dwell_duration_anomaly, lapse_break, route_deviation, new_relationship, unusual_location); call out the dominant detector types and any route deviations, 5) Daily atypicality outlier rankings — identify the highest-scoring days from topAtypicalDays, explain whether each was detector-driven or mobility-driven, and recommend which days warrant deepest review, 6) Digital activity patterns (apps, browsing, communication), 7) Actionable intelligence insights and recommendations for further investigation.`,
              context: aiContext,
            }),
          });
          if (aiResp.ok) {
            const aiData = await aiResp.json();
            aiNarrative = aiData.response || '';
          }
        } catch (aiErr) {
          console.warn('AI narrative generation failed, proceeding without:', aiErr);
        }
      }

      setExportSnackbar({ open: true, message: `Building PowerPoint for ${pptxExportEntityId}...`, severity: 'info' });

      await exportPOLToPowerPoint({
        entityId: pptxExportEntityId,
        profile: {
          entity_id: entity.entity_id,
          mobility_score: entity.mobility_score,
          social_score: entity.social_score,
          routine_score: entity.routine_score,
          anomaly_score: entity.anomaly_score,
          unique_locations: entity.unique_locations,
          total_dwells: entity.total_dwells,
          total_dwell_duration_minutes: entity.total_dwell_duration_minutes,
          total_routes: entity.total_routes,
          total_distance_km: entity.total_distance_km,
          total_calls: entity.total_calls,
          total_sms: entity.total_sms,
          unique_contacts: entity.unique_contacts,
          top_locations: entity.top_locations?.map(l => ({
            location_name: l.location_name,
            visit_count: (l as any).visits ?? (l as any).visit_count ?? 0,
            total_duration_minutes: (l as any).total_minutes ?? (l as any).total_duration_minutes ?? 0,
          })) || [],
          active_hours: entity.active_hours || [],
          hourly_heatmap: entity.hourly_heatmap,
        },
        patterns: entityPatterns,
        correlations: entityCorrelations,
        anomalies: entityAnomalies,
        routes: entityRoutes,
        digitalPatterns: entityDigital,
        digitalFingerprints: entityFingerprints,
        mapScreenshot: mapShot,
        aiNarrative: aiNarrative,
        daysBack,
        periodLabel: analysisPeriodLabel,
        courseName: selectedCourse || undefined,
        theme: pptxTheme,
        topAtypicalDays: entityAtypicalDays,
        anomalyTypeBreakdown: entityAnomalyTypeBreakdown,
      });
      setExportSnackbar({ open: true, message: `PowerPoint exported for ${pptxExportEntityId}`, severity: 'success' });
    } catch (err) {
      console.error('POL PPTX export failed:', err);
      setExportSnackbar({ open: true, message: `Export failed: ${err instanceof Error ? err.message : 'Unknown error'}`, severity: 'error' });
    } finally {
      setPptxExporting(false);
    }
  }, [pptxExportEntityId, dashboardData, routePatterns, digitalPatterns, digitalInsights,
      pptxExportNarrative, daysBack, analysisPeriodLabel, selectedCourse, pptxTheme, showLegend]);

  // ─── Outlier Rankings Brief Export ──────────────────────────────────
  // Focused per-entity export driven by the Outlier Rankings tab. Pulls the
  // entity's top atypical days, materializes the events that fired on each
  // top day, asks the AI for an outlier-focused interpretation, then renders
  // a tight 4-6 slide brief via exportTriageBriefToPowerPoint.
  const handleTriageBriefExport = useCallback(async () => {
    if (!dashboardData) return;
    const entityId = selectedEntity;
    if (!entityId) {
      setExportSnackbar({
        open: true,
        message: 'Select an entity from the filter before exporting the brief',
        severity: 'info',
      });
      return;
    }

    // Filter top atypical days to the chosen entity (server returns all entities).
    const entityDays = (dashboardData.top_atypical_days || []).filter(
      (d: any) => d.entity_id === entityId
    );

    if (entityDays.length === 0) {
      setExportSnackbar({
        open: true,
        message: `No atypical days found for ${entityId} in the current window`,
        severity: 'info',
      });
      return;
    }

    setBriefExporting(true);
    setExportSnackbar({
      open: true,
      message: `Generating AI analysis for ${entityId}...`,
      severity: 'info',
    });

    // Local copy of the day-key normalizer — the page-level isoToLocalDayKey
    // is defined further down the file (after this handler), so we use an
    // identical inline implementation here to avoid a temporal dead zone.
    const toDayKey = (iso: string | null | undefined): string => {
      if (!iso) return '';
      const t = new Date(iso);
      if (isNaN(t.getTime())) return '';
      const y = t.getFullYear();
      const m = String(t.getMonth() + 1).padStart(2, '0');
      const d = String(t.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    };

    try {
      // Normalize day rows into the export-shape (POLDailyAtypicalSummary).
      const normalizedDays = entityDays.map((d: any) => ({
        entity_id: d.entity_id,
        date: d.day,
        atypicality_score: d.atypicality_score,
        total_anomaly_count: d.total_anomaly_count,
        total_dwells: d.dwell_count,
        unique_locations: d.unique_places_visited,
        total_distance_km: d.distance_traveled_km,
        anomaly_breakdown: d.anomaly_breakdown,
      }));

      // Sort descending by atypicality_score so the brief leads with the worst day.
      normalizedDays.sort((a, b) => (b.atypicality_score || 0) - (a.atypicality_score || 0));

      // Build per-day deep-dive payloads for the top 4 days (any more makes the
      // PPTX too long for a "brief"). Each payload pulls the actual anomalies
      // that fired on that day for the selected entity.
      const TOP_N_DAYS = 4;
      const detailDays = normalizedDays.slice(0, TOP_N_DAYS);
      const allAnomalies = dashboardData.recent_anomalies || [];

      const dayDetails: POLTriageBriefDayDetail[] = detailDays.map((day, idx) => {
        const sourceRow = entityDays.find(
          (d: any) =>
            d.day === day.date ||
            toDayKey(d.day) === toDayKey(day.date)
        ) as any;
        const targetKey = toDayKey(day.date);

        const dayAnomalies = allAnomalies
          .filter((a: any) => {
            if (a.entity_id !== entityId) return false;
            const aKey = toDayKey(a.detected_at || '');
            return aKey === targetKey;
          })
          .map((a: any) => ({
            anomaly_type: a.anomaly_type,
            severity: a.severity,
            expected_behavior: a.expected_behavior || '',
            actual_behavior: a.actual_behavior || '',
            deviation_score: a.deviation_score || 0,
            detected_at: a.detected_at,
          }))
          .sort((a, b) => {
            const ta = a.detected_at ? new Date(a.detected_at).getTime() : 0;
            const tb = b.detected_at ? new Date(b.detected_at).getTime() : 0;
            return ta - tb;
          });

        return {
          date: day.date,
          atypicality_score: day.atypicality_score,
          total_anomaly_count: day.total_anomaly_count,
          total_dwells: day.total_dwells,
          unique_locations: day.unique_locations,
          total_distance_km: day.total_distance_km,
          total_dwell_minutes: sourceRow?.total_dwell_minutes,
          max_distance_from_home_km: sourceRow?.max_distance_from_home_km,
          track_point_count: sourceRow?.track_point_count,
          anomaly_breakdown: day.anomaly_breakdown,
          anomalies: dayAnomalies,
        };
      });

      // AI prompt — focused on interpretation of these specific days. We pass
      // the full ranked list + deep-dive context so the model can reason about
      // detector-driven vs mobility-driven days and cross-day trends.
      let aiNarrative: string | undefined;
      try {
        const aiContext = {
          selectedEntity: entityId,
          selectedCourse: selectedCourse || 'All',
          timeRange: `Last ${daysBack} days`,
          briefMode: true,
          atypicalDays: normalizedDays.map((d) => ({
            day: d.date,
            atypicality_score: d.atypicality_score,
            total_anomaly_count: d.total_anomaly_count,
            distance_traveled_km: d.total_distance_km,
            unique_places_visited: d.unique_locations,
            dwell_count: d.total_dwells,
            anomaly_breakdown: d.anomaly_breakdown,
          })),
          dayDetails: dayDetails.map((d) => ({
            day: d.date,
            score: d.atypicality_score,
            events: d.anomalies.slice(0, 12).map((a) => ({
              type: a.anomaly_type,
              severity: a.severity,
              expected: a.expected_behavior,
              actual: a.actual_behavior,
              detected_at: a.detected_at,
            })),
          })),
        };

        const briefPrompt =
          `You are writing a focused Outlier Rankings brief for ${entityId}. ` +
          `For each of the top ${dayDetails.length} atypical days listed in dayDetails, explain in plain prose: ` +
          `1) what happened (the actual events fired, and whether they cluster around a place, time, or relationship), ` +
          `2) why the atypicality score is at this level — distinguish detector-driven days ` +
          `(time_shift, dwell_duration_anomaly, lapse_break, route_deviation, new_relationship, unusual_location, ml_*) ` +
          `from mobility-driven days (unusual distance, unusual number of places, no detectors but score elevated), ` +
          `and 3) what an analyst should investigate first. ` +
          `Then conclude with a short cross-day section covering: recurring detector types, escalation or de-escalation trend across the ranked days, ` +
          `and 3 concrete next-step recommendations (e.g. pull additional sensors, run co-traveler analysis, validate annotations, contact subject). ` +
          `Keep the entire response between 600 and 900 words. Use simple paragraphs, no markdown, no headings, no bullet symbols — the export builder will format it.`;

        const aiResp = await fetch('/api/pol-chat/ask', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}`,
          },
          body: JSON.stringify({ question: briefPrompt, context: aiContext }),
        });
        if (aiResp.ok) {
          const aiData = await aiResp.json();
          aiNarrative = aiData.response || '';
        } else {
          console.warn('[Brief] AI request returned non-OK:', aiResp.status);
        }
      } catch (aiErr) {
        console.warn('[Brief] AI narrative generation failed, proceeding without:', aiErr);
      }

      setExportSnackbar({
        open: true,
        message: `Building Outlier Rankings brief for ${entityId}...`,
        severity: 'info',
      });

      await exportTriageBriefToPowerPoint({
        entityId,
        briefLabel: 'Outlier Rankings',
        daysBack,
        courseName: selectedCourse || undefined,
        theme: pptxTheme,
        topAtypicalDays: normalizedDays,
        dayDetails,
        aiNarrative,
      });

      setExportSnackbar({
        open: true,
        message: `Outlier Rankings brief exported for ${entityId}`,
        severity: 'success',
      });
    } catch (err) {
      console.error('Outlier Rankings brief export failed:', err);
      setExportSnackbar({
        open: true,
        message: `Brief export failed: ${err instanceof Error ? err.message : 'Unknown error'}`,
        severity: 'error',
      });
    } finally {
      setBriefExporting(false);
    }
  }, [
    dashboardData,
    selectedEntity,
    selectedCourse,
    daysBack,
    pptxTheme,
  ]);

  // Compare similar routes function
  const handleCompareRoutes = useCallback(async (routeId: number) => {
    setCompareLoading(true);
    try {
      const data = await polService.getSimilarRoutes(routeId);
      setCompareRoutes(data.routes);
      setIsComparing(true);
      setHiddenCompareRoutes(new Set());
      
      // Zoom to fit all routes
      if (data.routes.length > 0 && data.routes[0].origin.lat) {
        const allLats = data.routes.flatMap(r => [r.origin.lat, r.destination.lat]).filter(Boolean) as number[];
        const allLons = data.routes.flatMap(r => [r.origin.lon, r.destination.lon]).filter(Boolean) as number[];
        
        const centerLat = (Math.min(...allLats) + Math.max(...allLats)) / 2;
        const centerLon = (Math.min(...allLons) + Math.max(...allLons)) / 2;
        
        setViewport({
          latitude: centerLat,
          longitude: centerLon,
          zoom: 12,
        });
      }
    } catch (err) {
      console.error('Compare routes error:', err);
    } finally {
      setCompareLoading(false);
    }
  }, []);
  
  // Exit comparison mode
  const exitCompareMode = useCallback(() => {
    setIsComparing(false);
    setCompareRoutes([]);
    setHiddenCompareRoutes(new Set());
  }, []);

  const compareTimeRange = useMemo(() => {
    if (!isComparing || compareRoutes.length === 0) return null;
    let min = Infinity;
    let max = -Infinity;
    compareRoutes.forEach(r => {
      r.trip_instances?.forEach(t => {
        const ts = new Date(t.timestamp).getTime();
        if (!isNaN(ts)) {
          if (ts < min) min = ts;
          if (ts > max) max = ts;
        }
      });
    });
    if (!isFinite(min) || !isFinite(max) || min === max) return null;
    return { min, max, span: max - min };
  }, [isComparing, compareRoutes]);
  
  // Trigger map resize when sentinel panel opens/closes
  useEffect(() => {
    // Small delay to let CSS transition complete before resizing
    const timeout = setTimeout(() => {
      if (mapRef.current) {
        mapRef.current.resize();
      }
    }, 350); // Match the transition duration (0.3s = 300ms) + buffer
    
    return () => clearTimeout(timeout);
  }, [showSentinel]);

  // Panel resize handlers
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    resizeRef.current = { startX: e.clientX, startWidth: rightPanelWidth };
  }, [rightPanelWidth]);

  useEffect(() => {
    const handleMouseMove = (e: globalThis.MouseEvent) => {
      if (!isResizing || !resizeRef.current) return;
      
      const deltaX = resizeRef.current.startX - e.clientX;
      const newWidth = Math.min(Math.max(resizeRef.current.startWidth + deltaX, 320), 800);
      setRightPanelWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      resizeRef.current = null;
      // Trigger map resize after panel resize
      setTimeout(() => {
        if (mapRef.current) {
          mapRef.current.resize();
        }
      }, 50);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  // Add 3D buildings layer
  const add3DBuildingsLayer = useCallback(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;

    try {
      // Check if the style is loaded
      if (!map.isStyleLoaded()) {
        map.once('styledata', add3DBuildingsLayer);
        return;
      }

      // Check if the layer already exists (authoritative check)
      if (map.getLayer('3d-buildings')) return;

      // Find the first label layer to insert buildings before
      const layers = map.getStyle().layers;
      const labelLayerId = layers?.find(
        (layer: any) => layer.type === 'symbol' && layer.layout?.['text-field']
      )?.id;

      // Add the 3D buildings layer
      map.addLayer(
        {
          id: '3d-buildings',
          source: 'composite',
          'source-layer': 'building',
          filter: ['==', 'extrude', 'true'],
          type: 'fill-extrusion',
          minzoom: 14,
          paint: {
            'fill-extrusion-color': [
              'interpolate',
              ['linear'],
              ['get', 'height'],
              0, '#1a1a2e',
              50, '#16213e',
              100, '#0f3460',
              200, '#533483'
            ],
            'fill-extrusion-height': ['get', 'height'],
            'fill-extrusion-base': ['get', 'min_height'],
            'fill-extrusion-opacity': 0.7
          }
        },
        labelLayerId
      );
    } catch (error) {
      console.error('Error adding 3D buildings layer:', error);
    }
  }, []); // We check map.getLayer() directly instead of tracking state

  // Publish current viewport bounds for "crop tabs to map view".
  const emitMapBounds = useCallback(() => {
    const map = mapRef.current?.getMap?.();
    if (!map) return;
    try {
      const b = map.getBounds();
      if (!b) return;
      setMapBounds({
        west: b.getWest(),
        south: b.getSouth(),
        east: b.getEast(),
        north: b.getNorth(),
      });
    } catch {
      // Bounds may briefly be undefined during style swaps.
    }
  }, []);

  // Handle map load
  const handleMapLoad = useCallback(() => {
    if (is3DMode) {
      setTimeout(() => add3DBuildingsLayer(), 100);
    }
    
    // Add geocoder control
    const map = mapRef.current?.getMap();
    if (map && !geocoderRef.current && MAPBOX_TOKEN) {
      const geocoder = new MapboxGeocoder({
        accessToken: MAPBOX_TOKEN,
        mapboxgl: require('mapbox-gl') as any,
        marker: false,
        placeholder: 'Search for address or ...',
        proximity: {
          longitude: viewport.longitude,
          latitude: viewport.latitude
        } as any,
      });
      
      map.addControl(geocoder, 'top-left');
      geocoderRef.current = geocoder;
    }

    // Initial bounds so tabs can filter before the user pans.
    emitMapBounds();
  }, [is3DMode, add3DBuildingsLayer, viewport.longitude, viewport.latitude, emitMapBounds]);

  const handleDigitalEventMapSelect = useCallback((event: HourlyEvent) => {
    if (event.lat == null || event.lon == null || Number.isNaN(event.lat) || Number.isNaN(event.lon)) {
      return;
    }

    mapRef.current?.getMap()?.flyTo({
      center: [event.lon, event.lat],
      zoom: 16,
      duration: 800,
    });
    setSelectedMarker({
      lat: event.lat,
      lon: event.lon,
      name: `${event.entity_id} ${event.type.toUpperCase()}`,
      visits: 1,
      entities: [event.entity_id],
      metricLabel: 'event',
      detail: event.detail,
    });
    setFlashDwellLocation({ lat: event.lat, lon: event.lon });
    setTimeout(() => setFlashDwellLocation(null), 2000);
  }, []);

  const handleDigitalEventsMapLoad = useCallback((events: HourlyEvent[], slot?: { day: number; hour: number }) => {
    const locatedEvents = events.filter((event) => (
      event.lat != null && event.lon != null && !Number.isNaN(event.lat) && !Number.isNaN(event.lon)
    ));

    if (!slot || locatedEvents.length === 0) {
      setSelectedDigitalEvents(null);
      setSelectedMarker(null);
      if (digitalMapReturnViewRef.current) {
        mapRef.current?.flyTo({
          center: [digitalMapReturnViewRef.current.longitude, digitalMapReturnViewRef.current.latitude],
          zoom: digitalMapReturnViewRef.current.zoom,
          duration: 600,
        });
        digitalMapReturnViewRef.current = null;
      }
      return;
    }

    setSelectedDigitalEvents({ events: locatedEvents, slot });

    const map = mapRef.current;
    if (!map) return;
    const currentMap = map.getMap?.();
    if (currentMap && !digitalMapReturnViewRef.current) {
      const center = currentMap.getCenter();
      digitalMapReturnViewRef.current = {
        longitude: center.lng,
        latitude: center.lat,
        zoom: currentMap.getZoom(),
      };
    }

    const lats = locatedEvents.map((event) => event.lat as number);
    const lons = locatedEvents.map((event) => event.lon as number);

    if (locatedEvents.length === 1) {
      map.flyTo({
        center: [lons[0], lats[0]],
        zoom: 16,
        duration: 800,
      });
      return;
    }

    const pad = 0.002;
    map.fitBounds(
      [
        [Math.min(...lons) - pad, Math.min(...lats) - pad],
        [Math.max(...lons) + pad, Math.max(...lats) + pad],
      ],
      {
        padding: { top: 60, bottom: 60, left: 60, right: 60 },
        duration: 800,
      },
    );
  }, []);

  // Note: the auto-fit effect that consumes `triageDayAnomalies` lives AFTER
  // that memo is declared (see further below) to avoid a temporal-dead-zone
  // reference under strict TypeScript.

  // If the entity is cleared or changed, clear any active triage day focus too
  // so the day filter never refers to a phantom entity.
  useEffect(() => {
    if (!selectedTriageDay) return;
    if (!selectedEntity || selectedEntity !== selectedTriageDay.entityId) {
      setSelectedTriageDay(null);
    }
  }, [selectedEntity, selectedTriageDay]);

  // Zoom controls
  const handleZoomIn = useCallback(() => {
    mapRef.current?.getMap().zoomIn();
  }, []);

  const handleZoomOut = useCallback(() => {
    mapRef.current?.getMap().zoomOut();
  }, []);

  const handleResetBearing = useCallback(() => {
    mapRef.current?.getMap().resetNorth();
  }, []);

  // Fullscreen toggle
  const toggleFullscreen = useCallback(() => {
    if (!mapContainerRef.current) return;
    
    if (!document.fullscreenElement) {
      mapContainerRef.current.requestFullscreen?.();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  }, []);

  // Listen for fullscreen change events
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Calculate distance for measurement tool (returns km)
  const calculateMeasurementDistance = useCallback((lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371; // Earth's radius in kilometers
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }, []);

  // Update total distance when measurement points change
  useEffect(() => {
    if (measurementPoints.length < 2) {
      setTotalDistance(0);
      return;
    }
    
    let total = 0;
    for (let i = 1; i < measurementPoints.length; i++) {
      total += calculateMeasurementDistance(
        measurementPoints[i - 1][1], measurementPoints[i - 1][0],
        measurementPoints[i][1], measurementPoints[i][0]
      );
    }
    setTotalDistance(total);
  }, [measurementPoints, calculateMeasurementDistance]);

  // Clear measurement
  const clearMeasurement = useCallback(() => {
    setMeasurementPoints([]);
    setTotalDistance(0);
  }, []);

  // Toggle measurement mode
  const toggleMeasurement = useCallback(() => {
    if (isMeasuring) {
      setIsMeasuring(false);
    } else {
      setIsMeasuring(true);
      clearMeasurement();
    }
  }, [isMeasuring, clearMeasurement]);

  // Format distance for display
  const formatDistance = useCallback((km: number): string => {
    if (km < 1) {
      return `${(km * 1000).toFixed(0)} m`;
    }
    return `${km.toFixed(2)} km (${(km * 0.621371).toFixed(2)} mi)`;
  }, []);

  // Handle map click for measurement
  const handleMeasurementClick = useCallback((lngLat: { lng: number; lat: number }) => {
    if (isMeasuring) {
      setMeasurementPoints(prev => [...prev, [lngLat.lng, lngLat.lat]]);
    }
  }, [isMeasuring]);

  // Re-add buildings layer when 3D mode changes
  useEffect(() => {
    if (is3DMode && mapRef.current) {
      setTimeout(() => add3DBuildingsLayer(), 100);
    } else if (!is3DMode && mapRef.current) {
      const map = mapRef.current.getMap();
      if (map.getLayer('3d-buildings')) {
        map.removeLayer('3d-buildings');
      }
    }
  }, [is3DMode, mapStyle, add3DBuildingsLayer]);

  // Center on entities
  const centerOnEntities = useCallback(() => {
    if (!dashboardData?.entities || dashboardData.entities.length === 0) return;
    
    const validLocations: { lat: number; lon: number }[] = [];
    dashboardData.entities.forEach((entity: EntitySummary) => {
      entity.top_locations?.forEach((loc: LocationVisit) => {
        if (loc.latitude && loc.longitude) {
          validLocations.push({ lat: loc.latitude, lon: loc.longitude });
        }
      });
    });
    
    if (validLocations.length === 0) return;
    
    // Calculate bounds
    const minLat = Math.min(...validLocations.map((l) => l.lat));
    const maxLat = Math.max(...validLocations.map((l) => l.lat));
    const minLon = Math.min(...validLocations.map((l) => l.lon));
    const maxLon = Math.max(...validLocations.map((l) => l.lon));
    
    // Center on midpoint
    setViewport(prev => ({
      ...prev,
      longitude: (minLon + maxLon) / 2,
      latitude: (minLat + maxLat) / 2,
      zoom: 11,
    }));
  }, [dashboardData?.entities]);

  // Handle city bookmark selection
  const handleCitySelect = useCallback((city: typeof cityBookmarks[0]) => {
    setViewport({
      longitude: city.longitude,
      latitude: city.latitude,
      zoom: city.zoom,
    });
    setBookmarkAnchorEl(null);
  }, []);
  
  // Filtered heatmap data for Pattern Wheel based on KPI filter selection.
  // Backend provides per-category counts (dwell_count, call_count, sms_count, browsing_count, app_count)
  // so we can swap activity_count to the matching category without re-querying.
  const wheelHeatmapData = useMemo((): TemporalHeatmapData[] => {
    const base = dashboardData?.temporal_heatmap ?? [];

    const projectToCategory = (cells: TemporalHeatmapData[], fields: string[]): TemporalHeatmapData[] => {
      return cells
        .map((c) => {
          const sum = fields.reduce((acc, f) => acc + ((c as any)[f] || 0), 0);
          return { ...c, activity_count: sum };
        })
        .filter((c) => c.activity_count > 0);
    };

    switch (kpiFilter) {
      case 'dwells':   return projectToCategory(base, ['dwell_count']);
      case 'routes':   return projectToCategory(base, ['route_count']);
      case 'calls':    return projectToCategory(base, ['call_count']);
      case 'sms':      return projectToCategory(base, ['sms_count']);
      case 'apps':     return projectToCategory(base, ['app_count']);
      case 'browsing': return projectToCategory(base, ['browsing_count']);
      default:         return base;
    }
  }, [kpiFilter, dashboardData?.temporal_heatmap]);

  const wheelTitle = useMemo(() => {
    switch (kpiFilter) {
      case 'dwells':   return '24-Hour Pattern Wheel · Dwells';
      case 'routes':   return '24-Hour Pattern Wheel · Route Trips';
      case 'calls':    return '24-Hour Pattern Wheel · Calls';
      case 'sms':      return '24-Hour Pattern Wheel · SMS';
      case 'apps':     return '24-Hour Pattern Wheel · App Usage';
      case 'browsing': return '24-Hour Pattern Wheel · Browsing';
      default:         return '24-Hour Pattern Wheel · All Activity';
    }
  }, [kpiFilter]);

  // Per-category totals computed from the heatmap (always aligned with what the wheel shows)
  const heatmapTotals = useMemo(() => {
    const base = dashboardData?.temporal_heatmap ?? [];
    return base.reduce(
      (acc, c) => ({
        dwell:    acc.dwell    + (c.dwell_count    || 0),
        route:    acc.route    + (c.route_count    || 0),
        call:     acc.call     + (c.call_count     || 0),
        sms:      acc.sms      + (c.sms_count      || 0),
        app:      acc.app      + (c.app_count      || 0),
        browsing: acc.browsing + (c.browsing_count || 0),
      }),
      { dwell: 0, route: 0, call: 0, sms: 0, app: 0, browsing: 0 },
    );
  }, [dashboardData?.temporal_heatmap]);

  // Per-cell counts when a Pattern Wheel cell is selected (hour + day_of_week).
  // Returns null when no cell is selected so callers fall back to period totals.
  const heatmapCellTotals = useMemo(() => {
    if (!wheelCellFilter) return null;
    const base = dashboardData?.temporal_heatmap ?? [];
    const cell = base.find(c => c.hour === wheelCellFilter.hour && c.day_of_week === wheelCellFilter.day);
    return {
      dwell:    cell?.dwell_count    || 0,
      route:    cell?.route_count    || 0,
      call:     cell?.call_count     || 0,
      sms:      cell?.sms_count      || 0,
      app:      cell?.app_count      || 0,
      browsing: cell?.browsing_count || 0,
    };
  }, [wheelCellFilter, dashboardData?.temporal_heatmap]);
  
  // Location marker type
  type LocationMarker = {
    lat: number;
    lon: number;
    name: string;
    visits: number;
    entities: string[];
  };

  // Calculate location markers for map (filtered by Davis-Monthan exclusion zone if enabled)
  const locationMarkers = useMemo((): LocationMarker[] => {
    if (!dashboardData?.entities) return [];
    
    const locationMap = new window.Map<string, LocationMarker>();
    
    dashboardData.entities.forEach((entity: EntitySummary) => {
      entity.top_locations?.forEach((loc: LocationVisit) => {
        if (loc.latitude && loc.longitude) {
          // Apply Davis-Monthan exclusion filter if enabled and polygon is loaded
          if (dmFilter === 'exclude' && dmPolygonRings) {
            if (isPointInAnyRing(loc.latitude, loc.longitude, dmPolygonRings)) return;
          }
          
          const key = `${loc.latitude.toFixed(4)}_${loc.longitude.toFixed(4)}`;
          if (locationMap.has(key)) {
            const existing = locationMap.get(key)!;
            existing.visits += loc.visits;
            existing.entities.push(entity.entity_id);
          } else {
            locationMap.set(key, {
              lat: loc.latitude,
              lon: loc.longitude,
              name: loc.location_name,
              visits: loc.visits,
              entities: [entity.entity_id],
            });
          }
        }
      });
    });
    
    return Array.from(locationMap.values());
  }, [dashboardData?.entities, dmFilter, dmPolygonRings]);

  // When a Pattern Wheel cell is clicked, override map markers with matching dwell locations
  const wheelFilteredMarkers = useMemo((): LocationMarker[] => {
    if (!wheelCellFilter || dwellTabData.length === 0) return locationMarkers;
    const { hour, day } = wheelCellFilter;
    const matchingDwells = dwellTabData.filter((d) => {
      if (!d.start_time || !d.lat || !d.lon) return false;
      if (dmFilter === 'exclude' && dmPolygonRings && isPointInAnyRing(d.lat, d.lon, dmPolygonRings)) {
        return false;
      }
      const dt = new Date(d.start_time);
      return dt.getHours() === hour && dt.getDay() === day;
    });
    const locMap = new window.Map<string, LocationMarker>();
    matchingDwells.forEach((d) => {
      const key = `${d.lat.toFixed(4)}_${d.lon.toFixed(4)}`;
      if (locMap.has(key)) {
        locMap.get(key)!.visits += 1;
      } else {
        locMap.set(key, {
          lat: d.lat,
          lon: d.lon,
          name: d.location_name || `${d.lat.toFixed(4)}, ${d.lon.toFixed(4)}`,
          visits: 1,
          entities: [d.entity_id],
        });
      }
    });
    return Array.from(locMap.values());
  }, [wheelCellFilter, dwellTabData, locationMarkers, dmFilter, dmPolygonRings]);

  // --- Map-extent → tab filters (tabs only; map layers stay full) ---
  const extentBounds = filterByMapExtent ? mapBounds : null;

  const extentFilteredEntities = useMemo(() => {
    const entities = dashboardData?.entities ?? [];
    if (!extentBounds) return entities;
    return entities.filter((e) =>
      (e.top_locations || []).some((loc) =>
        isLatLonInBounds(loc.latitude, loc.longitude, extentBounds),
      ),
    );
  }, [dashboardData?.entities, extentBounds]);

  const extentFilteredPatterns = useMemo(() => {
    const patterns = dashboardData?.top_patterns ?? [];
    if (!extentBounds) return patterns;
    return patterns.filter((p) =>
      isLatLonInBounds(p.location_coordinates?.lat, p.location_coordinates?.lon, extentBounds),
    );
  }, [dashboardData?.top_patterns, extentBounds]);

  const extentFilteredCorrelations = useMemo(() => {
    const corrs = dashboardData?.top_correlations ?? [];
    if (!extentBounds) return corrs;
    return corrs.filter((c) =>
      (c.shared_locations || []).some((loc) => isLatLonInBounds(loc.lat, loc.lon, extentBounds)),
    );
  }, [dashboardData?.top_correlations, extentBounds]);

  const extentFilteredAnomalies = useMemo(() => {
    const anomalies = dashboardData?.recent_anomalies ?? [];
    if (!extentBounds) return anomalies;
    return anomalies.filter((a) => isAnomalyInMapBounds(a, extentBounds));
  }, [dashboardData?.recent_anomalies, extentBounds]);

  /** Layer-2 detector events for the selected entity (exclude bridged ml_* rows — those are Layer 1). */
  const selectedEntityDetectorEvents = useMemo(() => {
    if (!selectedEntity) return [];
    return extentFilteredAnomalies
      .filter((a: PolAnomaly) => a.entity_id === selectedEntity)
      .filter((a: PolAnomaly) => !(a.anomaly_type || '').toLowerCase().startsWith('ml_'))
      .map((a: PolAnomaly) => ({
        date: a.anomaly_time || a.detected_at || '',
        anomaly_type: a.anomaly_type,
        severity: a.severity,
        deviation_score: a.deviation_score,
      }));
  }, [extentFilteredAnomalies, selectedEntity]);

  // Keep ML anomaly summary in sync with selection so entity Recent Activity can show both layers.
  useEffect(() => {
    if (!selectedEntity || !selectedCourse) {
      setSelectedEntityMlSummary(null);
      return;
    }
    let cancelled = false;
    polService
      .getAnomalySummary(selectedEntity, selectedCourse, daysBack)
      .then((summary) => {
        if (!cancelled) setSelectedEntityMlSummary(summary);
      })
      .catch(() => {
        if (!cancelled) setSelectedEntityMlSummary(null);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedEntity, selectedCourse, daysBack]);

  const extentFilteredRoutes = useMemo(() => {
    if (!extentBounds) return routePatterns;
    return routePatterns.filter((r) => isRouteInMapBounds(r, extentBounds));
  }, [routePatterns, extentBounds]);

  const extentFilteredDwells = useMemo(() => {
    if (!extentBounds) return dwellTabData;
    return dwellTabData.filter((d) => isLatLonInBounds(d.lat, d.lon, extentBounds));
  }, [dwellTabData, extentBounds]);

  const selectedDigitalEventGeoJson = useMemo(() => ({
    type: 'FeatureCollection' as const,
    features: (selectedDigitalEvents?.events ?? [])
      .filter((event) => event.lat != null && event.lon != null && !Number.isNaN(event.lat) && !Number.isNaN(event.lon))
      .map((event, index) => ({
        type: 'Feature' as const,
        geometry: {
          type: 'Point' as const,
          coordinates: [event.lon as number, event.lat as number],
        },
        properties: {
          index,
          entity_id: event.entity_id,
          event_type: event.type,
          detail: event.detail,
          timestamp: event.timestamp,
        },
      })),
  }), [selectedDigitalEvents]);

  // Filter routePatterns to those with a trip_instance matching the selected wheel cell.
  // Must respect the active period window so the map stays in sync with the KPI cards,
  // heatmap, and Occurrence Timeline — otherwise trips from outside the window (stored
  // historically on a route pattern) would leak onto the map and confuse users.
  const wheelFilteredRoutes = useMemo((): RoutePattern[] => {
    if (!wheelCellFilter) return routePatterns;
    const { hour, day } = wheelCellFilter;
    const useCustom = dateRangeMode === 'custom' && startDate && endDate;
    const periodStartMs = useCustom
      ? new Date(`${startDate}T00:00:00.000Z`).getTime()
      : Date.now() - daysBack * 86400000;
    const periodEndMs = useCustom
      ? new Date(`${endDate}T23:59:59.999Z`).getTime()
      : Date.now();
    return routePatterns.filter((route) => {
      if (!route.trip_instances || route.trip_instances.length === 0) return false;
      return route.trip_instances.some((trip) => {
        if (!trip.timestamp) return false;
        const dt = new Date(trip.timestamp);
        const ts = dt.getTime();
        if (ts < periodStartMs || ts >= periodEndMs) return false;
        return dt.getHours() === hour && dt.getDay() === day;
      });
    });
  }, [wheelCellFilter, routePatterns, dateRangeMode, startDate, endDate, daysBack]);

  // Score color helper (currently unused)
  // const getScoreColor = (score: number) => {
  //   if (score >= 0.7) return theme.palette.success.main;
  //   if (score >= 0.4) return theme.palette.warning.main;
  //   return theme.palette.error.main;
  // };
  
  // Handle route click - show on map (currently unused)
  // const handleRouteClick = useCallback((route: LocationSequence) => {
  //   if (selectedRoute?.id === route.id) {
  //     // Deselect if clicking same route
  //     setSelectedRoute(null);
  //     return;
  //   }
  //   
  //   setSelectedRoute(route);
  //   
  //   // Get coordinates from route locations
  //   const coords = route.locations?.filter((loc: any) => loc.lat && loc.lon) || [];
  //   
  //   if (coords.length > 0 && mapRef.current) {
  //     // Calculate bounds to fit all points
  //     const lats = coords.map((c: any) => c.lat);
  //     const lons = coords.map((c: any) => c.lon);
  //     
  //     const bounds: [[number, number], [number, number]] = [
  //       [Math.min(...lons) - 0.01, Math.min(...lats) - 0.01], // SW
  //       [Math.max(...lons) + 0.01, Math.max(...lats) + 0.01], // NE
  //     ];
  //     
  //     mapRef.current.fitBounds(bounds, {
  //       padding: { top: 50, bottom: 50, left: 50, right: 50 },
  //       duration: 1000,
  //     });
  //   }
  // }, [selectedRoute]);
  
  // Generate route line GeoJSON (legacy location sequences)
  const routeLineData = useMemo(() => {
    if (!selectedRoute?.locations) return null;
    
    const coords = selectedRoute.locations
      .filter((loc: LocationSeqPoint) => loc.lat && loc.lon)
      .map((loc: LocationSeqPoint) => [loc.lon, loc.lat]);
    
    if (coords.length < 2) return null;
    
    return {
      type: 'FeatureCollection' as const,
      features: [{
        type: 'Feature' as const,
        properties: {},
        geometry: {
          type: 'LineString' as const,
          coordinates: coords,
        },
      }],
    };
  }, [selectedRoute]);
  
  // Generate route pattern GeoJSON (rich GPS geometry)
  /**
   * Extract a single map point from an anomaly's location field.
   * Works for point-type anomalies whose expected/actual_location is { lat, lon }.
   * Returns null for trip-shaped (origin/destination) anomalies.
   */
  const getAnomalyPoint = (anomaly: PolAnomaly | null): { lat: number; lon: number; name?: string } | null => {
    if (!anomaly) return null;
    const loc = anomaly.actual_location || anomaly.expected_location;
    if (!loc) return null;
    const lat = (loc as any).lat;
    const lon = (loc as any).lon;
    if (typeof lat === 'number' && typeof lon === 'number' && !isNaN(lat) && !isNaN(lon)) {
      return { lat, lon, name: (loc as any).name };
    }
    return null;
  };

  /**
   * Extract origin + destination endpoints from a trip-shaped anomaly (route_deviation).
   * Returns null if the anomaly doesn't have valid endpoint data.
   */
  const getAnomalyTripEndpoints = (
    anomaly: PolAnomaly | null
  ): {
    origin: { lat: number; lon: number; name?: string };
    destination: { lat: number; lon: number; name?: string };
  } | null => {
    if (!anomaly) return null;
    const src = anomaly.expected_location || anomaly.actual_location;
    if (!src) return null;
    const o: any = (src as any).origin;
    const d: any = (src as any).destination;
    if (!o || !d) return null;
    if (typeof o.lat !== 'number' || typeof o.lon !== 'number' || isNaN(o.lat) || isNaN(o.lon)) return null;
    if (typeof d.lat !== 'number' || typeof d.lon !== 'number' || isNaN(d.lat) || isNaN(d.lon)) return null;
    return {
      origin: { lat: o.lat, lon: o.lon, name: o.name },
      destination: { lat: d.lat, lon: d.lon, name: d.name },
    };
  };

  /**
   * For a route_deviation anomaly, find the matching RoutePattern (so we can
   * render its real GPS geometry instead of a straight line).
   * Matches by origin+destination name first; falls back to nearest-by-coordinates.
   */
  const findMatchingRoutePattern = (anomaly: PolAnomaly | null): RoutePattern | null => {
    if (!anomaly) return null;
    const trip = getAnomalyTripEndpoints(anomaly);
    if (!trip) return null;
    const candidates = routePatterns.filter((rp) => rp.entity_id === anomaly.entity_id);
    const pool = candidates.length > 0 ? candidates : routePatterns;
    const byName = pool.find(
      (rp) =>
        rp.origin?.name &&
        rp.destination?.name &&
        rp.origin.name === trip.origin.name &&
        rp.destination.name === trip.destination.name
    );
    if (byName) return byName;
    let best: RoutePattern | null = null;
    let bestScore = Infinity;
    for (const rp of pool) {
      const oLat = rp.origin?.lat;
      const oLon = rp.origin?.lon;
      const dLat = rp.destination?.lat;
      const dLon = rp.destination?.lon;
      if (
        typeof oLat !== 'number' || typeof oLon !== 'number' ||
        typeof dLat !== 'number' || typeof dLon !== 'number'
      ) continue;
      const score =
        (oLat - trip.origin.lat) ** 2 + (oLon - trip.origin.lon) ** 2 +
        (dLat - trip.destination.lat) ** 2 + (dLon - trip.destination.lon) ** 2;
      // Within ~0.01 degrees (~1 km) on each endpoint summed = strong match
      if (score < 0.0008 && score < bestScore) {
        bestScore = score;
        best = rp;
      }
    }
    return best;
  };

  const routePatternLineData = useMemo((): GeoJSON.FeatureCollection<GeoJSON.LineString> | null => {
    if (!selectedRoutePattern) return null;

    const validCoords = normalizeRouteGeometry(
      selectedRoutePattern.route_geometry,
      selectedRoutePattern.origin,
    );

    // Prefer GPS path; fall back to origin→destination so "Click to view" never goes blank.
    const coordinates: [number, number][] =
      validCoords.length >= 2
        ? validCoords
        : selectedRoutePattern.origin.lat != null &&
            selectedRoutePattern.origin.lon != null &&
            selectedRoutePattern.destination.lat != null &&
            selectedRoutePattern.destination.lon != null
          ? [
              [selectedRoutePattern.origin.lon, selectedRoutePattern.origin.lat],
              [selectedRoutePattern.destination.lon, selectedRoutePattern.destination.lat],
            ]
          : [];

    if (coordinates.length < 2) return null;

    return {
      type: 'FeatureCollection',
      features: [{
        type: 'Feature',
        properties: {
          name: selectedRoutePattern.route_name,
          usage_count: selectedRoutePattern.usage_count,
        },
        geometry: {
          type: 'LineString',
          coordinates,
        },
      }],
    };
  }, [selectedRoutePattern]);

  /**
   * Build the GeoJSON line for a selected route_deviation anomaly.
   * Prefers real GPS geometry from a matched RoutePattern; falls back to a straight
   * origin->destination line if no match is found.
   */
  const routeDeviationLineData = useMemo((): GeoJSON.FeatureCollection<GeoJSON.LineString> | null => {
    if (!selectedAnomaly || selectedAnomaly.anomaly_type !== 'route_deviation') return null;
    const trip = getAnomalyTripEndpoints(selectedAnomaly);
    if (!trip) return null;

    const matched = findMatchingRoutePattern(selectedAnomaly);
    let coords: [number, number][] = [
      [trip.origin.lon, trip.origin.lat],
      [trip.destination.lon, trip.destination.lat],
    ];
    if (matched?.route_geometry) {
      const valid = normalizeRouteGeometry(matched.route_geometry, matched.origin);
      if (valid.length >= 2) coords = valid;
    }

    return {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          properties: {
            severity: selectedAnomaly.severity,
            deviation: selectedAnomaly.deviation_score ?? 0,
            matched: !!matched,
          },
          geometry: { type: 'LineString', coordinates: coords },
        },
      ],
    };
    // findMatchingRoutePattern uses routePatterns; include it so the line refreshes when patterns load
  }, [selectedAnomaly, routePatterns]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Normalize any ISO date/datetime string to a 'YYYY-MM-DD' local-day key so
   * that anomaly timestamps and the Step-12 daily rollup's `day` field can be
   * compared regardless of time-of-day / timezone formatting differences.
   */
  const isoToLocalDayKey = useCallback((iso: string | null | undefined): string => {
    if (!iso) return '';
    const t = new Date(iso);
    if (isNaN(t.getTime())) return '';
    const y = t.getFullYear();
    const m = String(t.getMonth() + 1).padStart(2, '0');
    const d = String(t.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }, []);

  /**
   * All anomalies belonging to the currently-focused (entity, day) from the
   * Triage tab. Computed off `dashboardData.recent_anomalies`; empty when no
   * triage day is selected. Each anomaly is enriched with a derived point
   * `(lat, lon)` for map rendering — uses actual_location, falls back to
   * expected_location, and finally to a route_deviation endpoint.
   */
  const triageDayAnomalies = useMemo(() => {
    if (!selectedTriageDay) return [] as Array<PolAnomaly & { mapLat: number; mapLon: number }>;
    const targetKey = isoToLocalDayKey(selectedTriageDay.day);
    if (!targetKey) return [];
    const all = dashboardData?.recent_anomalies ?? [];
    const out: Array<PolAnomaly & { mapLat: number; mapLon: number }> = [];
    for (const a of all) {
      if (a.entity_id !== selectedTriageDay.entityId) continue;
      if (isoToLocalDayKey(a.anomaly_time) !== targetKey) continue;
      const pt = getAnomalyPoint(a) || getAnomalyTripEndpoints(a)?.origin || null;
      if (!pt || typeof pt.lat !== 'number' || typeof pt.lon !== 'number') continue;
      if (isNaN(pt.lat) || isNaN(pt.lon)) continue;
      out.push({ ...a, mapLat: pt.lat, mapLon: pt.lon });
    }
    return out;
  }, [selectedTriageDay, dashboardData?.recent_anomalies, isoToLocalDayKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-fit map to the selected Outlier Rankings day's anomaly points ──
  // Declared HERE (after `triageDayAnomalies`) rather than alongside the other
  // map effects to avoid a temporal-dead-zone reference to the memo. If only
  // one plottable anomaly exists we flyTo it at street zoom; for multiple we
  // fitBounds with padding. Skipped when nothing is plottable (defensive).
  useEffect(() => {
    if (!selectedTriageDay || triageDayAnomalies.length === 0) return;
    const map = mapRef.current;
    if (!map) return;

    if (triageDayAnomalies.length === 1) {
      const a = triageDayAnomalies[0];
      map.flyTo({ center: [a.mapLon, a.mapLat], zoom: 14, duration: 800 });
      return;
    }

    const lats = triageDayAnomalies.map((a) => a.mapLat);
    const lons = triageDayAnomalies.map((a) => a.mapLon);
    const pad = 0.01;
    try {
      map.fitBounds(
        [
          [Math.min(...lons) - pad, Math.min(...lats) - pad],
          [Math.max(...lons) + pad, Math.max(...lats) + pad],
        ],
        { padding: { top: 80, bottom: 80, left: 80, right: 80 }, duration: 800, maxZoom: 14 },
      );
    } catch {
      // fitBounds can throw on degenerate input; ignore — user can still pan
    }
  }, [selectedTriageDay, triageDayAnomalies]);

  // Generate route markers
  const routeMarkers = useMemo(() => {
    if (!selectedRoute?.locations) return [];
    
    return selectedRoute.locations
      .filter((loc: LocationSeqPoint) => loc.lat && loc.lon)
      .map((loc: LocationSeqPoint, idx: number): RouteStopMarker => ({
        lon: loc.lon,
        lat: loc.lat,
        name: loc.name || `Stop ${idx + 1}`,
        order: idx + 1,
        isFirst: idx === 0,
        isLast: idx === selectedRoute.locations.length - 1,
      }));
  }, [selectedRoute]);
  
  // Helper function to get route color based on usage
  const getRouteColor = (usageCount: number): string => {
    if (usageCount >= 10) return '#ef4444'; // High usage - Red
    if (usageCount >= 5) return '#f59e0b';  // Medium usage - Orange/Amber
    return '#22c55e';                        // Low usage - Green
  };
  
  const getRouteLabel = (usageCount: number): string => {
    if (usageCount >= 10) return 'high';
    if (usageCount >= 5) return 'medium';
    return 'low';
  };
  
  // Generate all routes as colored lines based on usage, filtered by usage level + repeat toggles.
  // When a Pattern Wheel cell is selected, only routes with a matching trip instance are shown.
  const allRoutesGeoJSON = useMemo((): GeoJSON.FeatureCollection<GeoJSON.LineString> | null => {
    const activeRoutes = wheelCellFilter ? wheelFilteredRoutes : routePatterns;
    if (!showAllRoutes || activeRoutes.length === 0) return null;

    const features: GeoJSON.Feature<GeoJSON.LineString>[] = [];

    activeRoutes.forEach((route: RoutePattern) => {
      if (routeRepeatFilter === 'single' && route.usage_count !== 1) return;
      if (routeRepeatFilter === 'repeated' && route.usage_count < 2) return;

      // Hide on-base routes when Davis-Monthan exclusion is active
      if (dmFilter === 'exclude' && isRouteInExclusionZone(route, dmPolygonRings)) return;

      const level = getRouteLabel(route.usage_count);
      if (!routeUsageFilter[level as keyof typeof routeUsageFilter]) return;

      let validCoords = normalizeRouteGeometry(route.route_geometry, route.origin);
      // Straight O/D stub when GPS path is missing/unusable so routes still appear on the map.
      if (
        validCoords.length < 2 &&
        route.origin?.lat != null &&
        route.origin?.lon != null &&
        route.destination?.lat != null &&
        route.destination?.lon != null
      ) {
        validCoords = [
          [route.origin.lon, route.origin.lat],
          [route.destination.lon, route.destination.lat],
        ];
      }
      if (validCoords.length < 2) return;

      features.push({
        type: 'Feature',
        properties: {
          id: route.id,
          usage_count: route.usage_count,
          route_name: route.route_name,
          color: getRouteColor(route.usage_count),
          usage_level: level,
        },
        geometry: {
          type: 'LineString',
          coordinates: validCoords,
        },
      });
    });

    if (features.length === 0) return null;

    return {
      type: 'FeatureCollection',
      features,
    };
  }, [showAllRoutes, routePatterns, wheelFilteredRoutes, wheelCellFilter, routeUsageFilter, routeRepeatFilter, dmFilter, dmPolygonRings]);
  
  // Render entity card
  // Modern entity card with pattern strength, heatmap, and insights
  const renderEntityCard = (entity: EntitySummary) => {
    const isDark = theme.palette.mode === 'dark';
    
    // Calculate overall score
    const overallScore = Math.round(
      (entity.routine_score * 0.4 + entity.mobility_score * 0.3 + entity.social_score * 0.3) * 100
    );
    
    // Pattern strength data
    const patternStrengths = [
      { label: 'Where', value: Math.round(entity.routine_score * 100), color: '#3b82f6' },
      { label: 'When', value: Math.round((entity.routine_score * 0.8 + 0.2) * 100), color: '#8b5cf6' },
      { label: 'Route', value: Math.round(entity.mobility_score * 100), color: '#06b6d4' },
      { label: 'Social', value: Math.round(entity.social_score * 100), color: '#10b981' },
    ];
    
    // Generate weekly heatmap from real per-(day, hour) counts
    const days = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
    const fullDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    // DOW mapping: 0=Sun,1=Mon,...6=Sat → reindex to Mon=0..Sun=6
    const dowToIdx = [6, 0, 1, 2, 3, 4, 5]; // postgres DOW → our day index
    const timeSlots = [
      { label: '12a', hours: [0, 1, 2, 3], tooltip: '12am - 4am (Night)' },
      { label: '4a', hours: [4, 5, 6, 7], tooltip: '4am - 8am (Early Morning)' },
      { label: '8a', hours: [8, 9, 10, 11], tooltip: '8am - 12pm (Morning)' },
      { label: '12p', hours: [12, 13, 14, 15], tooltip: '12pm - 4pm (Afternoon)' },
      { label: '4p', hours: [16, 17, 18, 19], tooltip: '4pm - 8pm (Evening)' },
      { label: '8p', hours: [20, 21, 22, 23], tooltip: '8pm - 12am (Night)' },
    ];

    // Build a count grid from hourly_heatmap data
    const countGrid: number[][] = days.map(() => timeSlots.map(() => 0));
    const heatmap = entity.hourly_heatmap || [];
    heatmap.forEach(({ day_of_week, hour, count }: HourlyHeatmapCell) => {
      const dayIdx = dowToIdx[day_of_week];
      const slotIdx = timeSlots.findIndex((s) => s.hours.includes(hour));
      if (dayIdx !== undefined && slotIdx >= 0) {
        countGrid[dayIdx][slotIdx] += count;
      }
    });

    // Normalize: use entity's own max to create relative intensity
    const allCounts = countGrid.flat();
    const maxCount = Math.max(...allCounts, 1);
    const heatmapData = countGrid.map((row: number[]) =>
      row.map((count: number) => count / maxCount)
    );
    
    // Get top patterns (from entity top_locations) - GROUP by location name
    const locationMap = new Map<string, { visits: number; totalMinutes: number }>();
    entity.top_locations?.forEach((loc: LocationVisit) => {
      const name = loc.location_name || 'Unknown';
      const existing = locationMap.get(name);
      if (existing) {
        existing.visits += loc.visits;
        existing.totalMinutes += loc.total_minutes || 0;
      } else {
        locationMap.set(name, { 
          visits: loc.visits, 
          totalMinutes: loc.total_minutes || 0 
        });
      }
    });
    
    // Convert to sorted array and take top 3
    type TopLocRow = { name: string; detail: string; consistency: number };
    type LocAgg = { visits: number; totalMinutes: number };
    const topPatterns = Array.from(locationMap.entries())
      .map(([name, data]: [string, LocAgg]): TopLocRow => ({
        name,
        detail: `${data.visits} visits`,
        consistency: Math.min(99, Math.round(50 + data.visits * 3))
      }))
      .sort((a: TopLocRow, b: TopLocRow) => parseInt(b.detail, 10) - parseInt(a.detail, 10))
      .slice(0, 3);
    
    // Recent activity — combine Layer-1 ML days + Layer-2 detector events.
    // Prefer live list counts; fall back to pol_summaries.anomaly_count for detectors.
    const entityAnoms = extentFilteredAnomalies.filter(
      (a: PolAnomaly) => a.entity_id === entity.entity_id
    );
    const detectorEventCount = entityAnoms.filter(
      (a: PolAnomaly) => !(a.anomaly_type || '').toLowerCase().startsWith('ml_')
    ).length;
    const mlBridgedCount = entityAnoms.filter((a: PolAnomaly) =>
      (a.anomaly_type || '').toLowerCase().startsWith('ml_')
    ).length;
    const mlDaysFlagged =
      entity.entity_id === selectedEntity && selectedEntityMlSummary
        ? selectedEntityMlSummary.anomalous_days || 0
        : mlBridgedCount;
    // Prefer live non-ml events; fall back to summary count when the list has nothing for this entity.
    const detectorCount =
      entityAnoms.length > 0 ? detectorEventCount : (entity.anomaly_count || 0);
    const hasLayerActivity = mlDaysFlagged > 0 || detectorCount > 0;
    
    return (
      <Card 
        key={entity.entity_id}
        sx={{ 
          cursor: 'pointer',
          transition: 'all 0.2s',
          border: selectedEntity === entity.entity_id 
            ? `2px solid ${theme.palette.primary.main}` 
            : `1px solid ${alpha(theme.palette.divider, 0.15)}`,
          bgcolor: 'transparent', // Match page background
          borderRadius: 2,
          overflow: 'hidden',
          '&:hover': {
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
            transform: 'translateY(-2px)',
            borderColor: theme.palette.primary.main,
            bgcolor: isDark ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.01)',
          }
        }}
        onClick={() => {
          const isDeselecting = selectedEntity === entity.entity_id;
          setSelectedEntity(isDeselecting ? null : entity.entity_id);
          
          // Clear other selections
          setSelectedPattern(null);
          setSelectedCorrelation(null);
          setSelectedAnomaly(null);
          
          // Zoom to top location if selecting
          if (!isDeselecting && entity.top_locations && entity.top_locations.length > 0) {
            const topLoc = entity.top_locations[0];
            if (topLoc.latitude && topLoc.longitude) {
              setViewport({
                latitude: topLoc.latitude,
                longitude: topLoc.longitude,
                zoom: 14,
              });
            }
          }
        }}
      >
        {/* Header */}
        <Box sx={{ 
          p: 1.5, 
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          background: isDark 
            ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(139, 92, 246, 0.05) 100%)'
            : 'linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(139, 92, 246, 0.02) 100%)'
        }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ fontFamily: 'monospace', letterSpacing: 0.5 }}>
                  {entity.entity_id}
                </Typography>
                {/* Data Maturity Badge */}
                {entity.data_confidence !== undefined && (
                  <Chip
                    size="small"
                    label={
                      entity.data_confidence >= 0.85 ? 'High' :
                      entity.data_confidence >= 0.6 ? 'Good' :
                      entity.data_confidence >= 0.3 ? 'Medium' : 'Low'
                    }
                    color={
                      entity.data_confidence >= 0.85 ? 'success' :
                      entity.data_confidence >= 0.6 ? 'info' :
                      entity.data_confidence >= 0.3 ? 'warning' : 'error'
                    }
                    variant={entity.baseline_established ? 'filled' : 'outlined'}
                    sx={{ 
                      height: 18, 
                      fontSize: '0.6rem',
                      fontWeight: 'bold'
                    }}
                    title={
                      entity.baseline_established 
                        ? `Baseline established (${entity.days_with_data || 0} days)` 
                        : `Limited data (${entity.days_with_data || 0} days) - metrics may be unreliable`
                    }
                  />
                )}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {entity.entity_type || singular } • {entity.unique_locations} locations • {entity.unique_contacts} contacts
                {entity.days_with_data && ` • ${entity.days_with_data} days tracked`}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" color="text.secondary">Score</Typography>
              <Typography 
                variant="h6" 
                fontWeight="bold" 
                sx={{ 
                  color: overallScore >= 70 ? 'success.main' : overallScore >= 40 ? 'warning.main' : 'error.main',
                  fontFamily: 'monospace'
                }}
              >
                {overallScore}
              </Typography>
            </Box>
          </Stack>
        </Box>
        
        <CardContent sx={{ p: 1.5 }}>
          <Stack direction="row" spacing={2}>
            {/* Pattern Strength Bars */}
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ 
                textTransform: 'uppercase', 
                letterSpacing: 1,
                fontSize: '0.65rem',
                display: 'block',
                mb: 0.5
              }}>
                Pattern Strength
              </Typography>
              <Stack spacing={0.3}>
                {patternStrengths.map(({ label, value, color }: { label: string; value: number; color: string }) => (
                  <Stack key={label} direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="caption" sx={{ width: 40, fontSize: '0.7rem', color: 'text.secondary' }}>
                      {label}
                    </Typography>
                    <Box sx={{ flex: 1, height: 6, bgcolor: alpha(color, 0.15), borderRadius: 1, overflow: 'hidden' }}>
                      <Box sx={{ 
                        width: `${value}%`, 
                        height: '100%', 
                        bgcolor: color,
                        borderRadius: 1,
                        transition: 'width 0.3s ease'
                      }} />
                    </Box>
                    <Typography variant="caption" sx={{ width: 28, fontSize: '0.65rem', textAlign: 'right', fontFamily: 'monospace' }}>
                      {value}%
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>
            
            {/* Weekly Heatmap */}
            <Box sx={{ width: 140 }}>
              <Tooltip 
                title="Activity intensity per time block. Bright orange = peak, dim blue = low, gray = none. Normalized to this entity's own peak."
                arrow
                placement="top"
              >
                <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ 
                  textTransform: 'uppercase', 
                  letterSpacing: 1,
                  fontSize: '0.65rem',
                  display: 'block',
                  mb: 0.5,
                  cursor: 'help',
                  textDecoration: 'underline',
                  textDecorationStyle: 'dotted',
                  textUnderlineOffset: '2px',
                }}>
                  Weekly Heatmap
                </Typography>
              </Tooltip>
              <Stack direction="row" spacing={0.5}>
                {/* Y-axis labels (time slots) */}
                <Stack spacing={0.25} justifyContent="space-between" sx={{ pt: '2px', pb: '2px' }}>
                  {timeSlots.map((slot: (typeof timeSlots)[number], slotIdx: number) => (
                    <Typography 
                      key={slotIdx}
                      variant="caption" 
                      sx={{ 
                        fontSize: '0.5rem', 
                        color: 'text.secondary',
                        height: 10,
                        display: 'flex',
                        alignItems: 'center',
                        lineHeight: 1
                      }}
                    >
                      {slot.label}
                    </Typography>
                  ))}
                </Stack>
                {/* Heatmap grid */}
                <Stack direction="row" spacing={0.25}>
                  {days.map((day: string, dayIdx: number) => (
                    <Stack key={dayIdx} spacing={0.25} alignItems="center">
                      <Typography variant="caption" sx={{ fontSize: '0.55rem', color: 'text.secondary', mb: 0.25 }}>
                        {day}
                      </Typography>
                      {timeSlots.map((slot: (typeof timeSlots)[number], slotIdx: number) => {
                        const intensity = heatmapData[dayIdx][slotIdx];
                        const rawCount = countGrid[dayIdx][slotIdx];
                        return (
                          <Tooltip key={slotIdx} title={`${fullDays[dayIdx]} ${slot.tooltip} — ${rawCount} event${rawCount !== 1 ? 's' : ''}`} arrow placement="top">
                            <Box 
                              sx={{ 
                                width: 10, 
                                height: 10, 
                                borderRadius: 0.5,
                                cursor: 'pointer',
                                transition: 'transform 0.15s',
                                bgcolor: intensity > 0.65
                                  ? alpha('#f59e0b', 0.9)
                                  : intensity > 0.35
                                    ? alpha('#f59e0b', 0.55)
                                    : intensity > 0.1
                                      ? alpha('#3b82f6', 0.45)
                                      : intensity > 0
                                        ? alpha('#3b82f6', 0.2)
                                        : alpha(theme.palette.text.primary, 0.05),
                                '&:hover': {
                                  transform: 'scale(1.3)',
                                }
                              }} 
                            />
                          </Tooltip>
                        );
                      })}
                    </Stack>
                  ))}
                </Stack>
              </Stack>
            </Box>
          </Stack>
          
          {/* Top Locations - with actual place names */}
          {topPatterns.length > 0 && (
            <Box 
              mt={1.5} 
              p={1} 
              bgcolor={alpha(theme.palette.primary.main, 0.05)} 
              borderRadius={1}
              sx={{ ...compactScrollbar, maxHeight: 90, overflowY: 'auto' }}
            >
              <Typography variant="caption" color="primary.main" fontWeight="bold" sx={{ 
                textTransform: 'uppercase', 
                letterSpacing: 1,
                fontSize: '0.65rem',
                display: 'block',
                mb: 0.5
              }}>
                Top Locations
              </Typography>
              <Stack spacing={0.5}>
                {topPatterns.map((pattern: TopLocRow, idx: number) => (
                  <Stack key={idx} direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ 
                        width: 8, 
                        height: 8, 
                        borderRadius: '50%', 
                        bgcolor: COLORS[idx % COLORS.length],
                        flexShrink: 0
                      }} />
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          fontSize: '0.75rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {pattern.name}
                      </Typography>
                    </Stack>
                    <Chip
                      label={pattern.detail}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: '0.6rem',
                        bgcolor: alpha(COLORS[idx % COLORS.length], 0.15),
                        color: COLORS[idx % COLORS.length],
                        fontWeight: 'bold',
                        ml: 0.5,
                        flexShrink: 0
                      }}
                    />
                  </Stack>
                ))}
              </Stack>
            </Box>
          )}
          
          {/* Recent Activity & Insights */}
          <Box mt={1}>
            <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ 
              textTransform: 'uppercase', 
              letterSpacing: 1,
              fontSize: '0.65rem'
            }}>
              Recent Activity
            </Typography>
            <Stack spacing={0.5} mt={0.5}>
              {hasLayerActivity ? (
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <WarningIcon sx={{ fontSize: 14, color: 'warning.main' }} />
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'warning.main' }}>
                    {mlDaysFlagged > 0 && detectorCount > 0
                      ? `${mlDaysFlagged} ML day${mlDaysFlagged === 1 ? '' : 's'} flagged · ${detectorCount} detector event${detectorCount === 1 ? '' : 's'}`
                      : mlDaysFlagged > 0
                        ? `${mlDaysFlagged} ML day${mlDaysFlagged === 1 ? '' : 's'} flagged · 0 detector events`
                        : `${detectorCount} detector event${detectorCount === 1 ? '' : 's'} · no ML days flagged`}
                  </Typography>
                </Stack>
              ) : (
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <CheckIcon sx={{ fontSize: 12, color: 'success.main' }} />
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                    Normal pattern adherence
                  </Typography>
                </Stack>
              )}
              
              {/* AI Summary preview if available */}
              {entity.ai_summary && (
                <Tooltip title={entity.ai_summary} arrow>
                  <Typography 
                    variant="caption" 
                    color="text.secondary" 
                    sx={{ 
                      fontSize: '0.65rem',
                      fontStyle: 'italic',
                      cursor: 'help',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}
                  >
                    {entity.ai_summary.slice(0, 100)}...
                  </Typography>
                </Tooltip>
              )}
            </Stack>
          </Box>
        </CardContent>
        
        {/* Footer Stats */}
        <Box sx={{ 
          px: 1.5, 
          py: 0.75, 
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          bgcolor: alpha(theme.palette.background.default, 0.5),
        }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" spacing={2} alignItems="center">
              <Tooltip title="Total dwell events recorded">
                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', fontFamily: 'monospace', cursor: 'help' }}>
                  {entity.total_dwells || 0} dwells
                </Typography>
              </Tooltip>
              <Tooltip title="Total distance traveled">
                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', fontFamily: 'monospace', cursor: 'help' }}>
                  {(entity.total_distance_km || 0).toFixed(1)} mi
                </Typography>
              </Tooltip>
              <Tooltip title="Number of routes taken">
                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', fontFamily: 'monospace', cursor: 'help' }}>
                  {entity.total_routes || 0} routes
                </Typography>
              </Tooltip>
            </Stack>
            <Typography 
              variant="caption" 
              color="primary" 
              sx={{ 
                fontSize: '0.65rem', 
                fontWeight: 'bold',
                display: selectedEntity === entity.entity_id ? 'none' : 'block'
              }}
            >
              Click to view →
            </Typography>
          </Stack>
        </Box>
      </Card>
    );
  };
  
  // Handle pattern click - zoom to location (toggle on re-click)
  const handlePatternClick = (pattern: RecurringPattern) => {
    // Toggle: if clicking the same pattern, deselect it
    if (selectedPattern?.id === pattern.id) {
      setSelectedPattern(null);
      return;
    }
    
    setSelectedPattern(pattern);
    setSelectedCorrelation(null);
    setSelectedAnomaly(null);
    
    if (pattern.location_coordinates?.lat && pattern.location_coordinates?.lon) {
      setViewport({
        latitude: pattern.location_coordinates.lat,
        longitude: pattern.location_coordinates.lon,
        zoom: 16,
      });
    }
  };
  
  // Render pattern card
  const renderPatternCard = (pattern: RecurringPattern) => {
    const isSelected = selectedPattern?.id === pattern.id;
    const hasLocation = pattern.location_coordinates?.lat && pattern.location_coordinates?.lon;
    
    return (
      <Card 
        key={pattern.id} 
        sx={{ 
          mb: 1,
          cursor: hasLocation ? 'pointer' : 'default',
          transition: 'all 0.2s',
          border: isSelected ? `2px solid ${theme.palette.success.main}` : '1px solid transparent',
          backgroundColor: isSelected ? alpha(theme.palette.success.main, 0.08) : undefined,
          '&:hover': hasLocation ? {
            boxShadow: theme.shadows[4],
            transform: 'translateY(-2px)',
            borderColor: theme.palette.success.light,
          } : {}
        }}
        onClick={() => hasLocation && handlePatternClick(pattern)}
      >
        <CardContent sx={{ p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Typography variant="subtitle2" fontWeight="bold">
                {pattern.location_name || pattern.location_id}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {pattern.entity_id} • {pattern.frequency}
              </Typography>
            </Box>
            <Stack direction="row" spacing={0.5}>
              {isSelected && (
                <Chip 
                  label="On Map"
                  size="small"
                  color="success"
                  variant="filled"
                  sx={{ fontSize: '0.65rem' }}
                />
              )}
              <Tooltip
                title={
                  <Box sx={{ p: 0.5 }}>
                    <Typography variant="caption" fontWeight="bold" display="block">
                      Confidence Score: {Math.min(100, (pattern.confidence_score * 100)).toFixed(0)}%
                    </Typography>
                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                      How reliable this recurring pattern is based on:
                    </Typography>
                    <Typography variant="caption" display="block" sx={{ pl: 1 }}>
                      • Hit Rate: % of expected visits that occurred
                    </Typography>
                    <Typography variant="caption" display="block" sx={{ pl: 1 }}>
                      • Concentration: % of visits on these days
                    </Typography>
                    <Typography variant="caption" display="block" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                        <DotIcon sx={{ fontSize: 10, color: pattern.confidence_score >= 0.7 ? '#4caf50' : pattern.confidence_score >= 0.5 ? '#ffeb3b' : '#bdbdbd' }} />
                        {pattern.confidence_score >= 0.7 
                          ? 'Strong pattern - highly consistent'
                          : pattern.confidence_score >= 0.5 
                            ? 'Moderate pattern - reasonably consistent'
                            : 'Weak pattern - occasional visits'}
                      </Box>
                    </Typography>
                  </Box>
                }
                arrow
              >
                <Chip 
                  label={`${Math.min(100, (pattern.confidence_score * 100)).toFixed(0)}%`}
                  size="small"
                  color={pattern.confidence_score >= 0.7 ? 'success' : pattern.confidence_score >= 0.5 ? 'warning' : 'default'}
                  sx={{ cursor: 'help' }}
                />
              </Tooltip>
            </Stack>
          </Stack>
          
          <Stack direction="row" spacing={1} mt={1} flexWrap="wrap">
            {pattern.days_of_week?.map((day: string) => (
              <Chip key={day} label={day} size="small" variant="outlined" sx={{ fontSize: '0.65rem' }} />
            ))}
            {pattern.time_window_start && (
              <Chip 
                label={`${pattern.time_window_start} - ${pattern.time_window_end}`} 
                size="small" 
                icon={<TimeIcon sx={{ fontSize: 12 }} />}
                sx={{ fontSize: '0.65rem' }}
              />
            )}
          </Stack>
          
          <Typography variant="caption" color="text.secondary" mt={1} display="block">
            {pattern.occurrence_count} occurrences • Avg {pattern.avg_duration_minutes?.toFixed(0) || 0} min
          </Typography>
          
          {hasLocation && (
            <Typography variant="caption" color="success.main" mt={0.5} display="block">
              Click to view on map
            </Typography>
          )}
        </CardContent>
      </Card>
    );
  };
  
  // Handle correlation click - zoom to shared locations (toggle on re-click)
  const handleCorrelationClick = (corr: EntityCorrelation) => {
    // Toggle: if clicking the same correlation, deselect it
    if (selectedCorrelation?.id === corr.id) {
      setSelectedCorrelation(null);
      return;
    }
    
    setSelectedCorrelation(corr);
    setSelectedPattern(null);
    setSelectedAnomaly(null);
    
    // Zoom to shared locations if available
    if (corr.shared_locations && corr.shared_locations.length > 0) {
      const validLocs = corr.shared_locations.filter(
        (l: SharedLocationPoint) => l.lat && l.lon && !isNaN(l.lat) && !isNaN(l.lon)
      );
      const lats = validLocs.map((l: SharedLocationPoint) => l.lat);
      const lons = validLocs.map((l: SharedLocationPoint) => l.lon);
      
      if (lats.length > 0 && lons.length > 0) {
        const minLat = Math.min(...lats);
        const maxLat = Math.max(...lats);
        const minLon = Math.min(...lons);
        const maxLon = Math.max(...lons);
        
        const centerLat = (minLat + maxLat) / 2;
        const centerLon = (minLon + maxLon) / 2;
        
        // Calculate zoom based on spread of locations
        const latSpread = maxLat - minLat;
        const lonSpread = maxLon - minLon;
        const maxSpread = Math.max(latSpread, lonSpread);
        
        // Zoom level: smaller spread = higher zoom
        // ~0.01 degree = ~1km, show at zoom 14
        // ~0.1 degree = ~10km, show at zoom 11
        // ~1 degree = ~100km, show at zoom 8
        let zoom = 14;
        if (maxSpread > 0.5) zoom = 8;
        else if (maxSpread > 0.1) zoom = 10;
        else if (maxSpread > 0.05) zoom = 12;
        else if (maxSpread > 0.01) zoom = 13;
        
        setViewport({
          latitude: centerLat,
          longitude: centerLon,
          zoom,
        });
      }
    }
  };
  
  // Render correlation card
  const renderCorrelationCard = (corr: EntityCorrelation) => {
    const isSelected = selectedCorrelation?.id === corr.id;
    const hasLocations = corr.shared_locations && corr.shared_locations.length > 0;
    
    return (
      <Card 
        key={corr.id} 
        sx={{ 
          mb: 1,
          cursor: hasLocations ? 'pointer' : 'default',
          transition: 'all 0.2s',
          border: isSelected ? `2px solid ${theme.palette.warning.main}` : '1px solid transparent',
          backgroundColor: isSelected ? alpha(theme.palette.warning.main, 0.08) : undefined,
          '&:hover': hasLocations ? {
            boxShadow: theme.shadows[4],
            transform: 'translateY(-2px)',
            borderColor: theme.palette.warning.light,
          } : {}
        }}
        onClick={() => hasLocations && handleCorrelationClick(corr)}
      >
        <CardContent sx={{ p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" spacing={1} alignItems="center">
              <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem', bgcolor: theme.palette.primary.main }}>
                {corr.entity_a_id.slice(0, 2)}
              </Avatar>
              <Box sx={{ 
                width: 40, 
                height: 2, 
                bgcolor: theme.palette.divider,
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  right: 0,
                  top: -3,
                  width: 0,
                  height: 0,
                  borderLeft: '6px solid',
                  borderTop: '4px solid transparent',
                  borderBottom: '4px solid transparent',
                  borderLeftColor: theme.palette.divider,
                }
              }} />
              <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem', bgcolor: theme.palette.secondary.main }}>
                {corr.entity_b_id.slice(0, 2)}
              </Avatar>
            </Stack>
            <Stack direction="row" spacing={0.5}>
              {isSelected && (
                <Chip 
                  label="On Map"
                  size="small"
                  color="warning"
                  variant="filled"
                  sx={{ fontSize: '0.65rem' }}
                />
              )}
              <Chip 
                label={corr.correlation_type.replace('_', ' ')}
                size="small"
                color="primary"
                variant="outlined"
              />
            </Stack>
          </Stack>
          
          <Stack direction="row" justifyContent="space-between" mt={1}>
            <Typography variant="caption" color="text.secondary">
              {corr.entity_a_id} ↔ {corr.entity_b_id}
            </Typography>
            <Typography variant="caption" fontWeight="bold">
              {corr.occurrence_count} times
            </Typography>
          </Stack>
          
          <LinearProgress 
            variant="determinate" 
            value={corr.correlation_strength * 100} 
            sx={{ mt: 1, height: 4, borderRadius: 2 }}
          />
          
          <Stack direction="row" spacing={1} mt={0.5} alignItems="center">
            <Tooltip
              title={
                <Box sx={{ p: 0.5 }}>
                  <Typography variant="caption" fontWeight="bold" display="block">
                    Correlation Metrics
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                    <strong>Strength:</strong> How significant the relationship is
                  </Typography>
                  <Typography variant="caption" display="block">
                    <strong>Confidence:</strong> Statistical reliability of the correlation
                  </Typography>
                </Box>
              }
              arrow
            >
              <Typography variant="caption" color="text.secondary" sx={{ cursor: 'help' }}>
                Strength: {Math.min(100, corr.correlation_strength * 100).toFixed(0)}% • 
                Confidence: {Math.min(100, corr.confidence_score * 100).toFixed(0)}%
              </Typography>
            </Tooltip>
          </Stack>
          
          {/* Shared Locations - show actual names */}
          {hasLocations && (
            <Box 
              mt={1} 
              p={1} 
              bgcolor={alpha(theme.palette.warning.main, 0.08)} 
              borderRadius={1}
              sx={{ ...compactScrollbar, maxHeight: 80, overflowY: 'auto' }}
            >
              <Typography variant="caption" fontWeight="bold" color="warning.main" display="block" mb={0.5}>
                Shared Locations ({corr.shared_locations.length}):
              </Typography>
              <Stack spacing={0.25}>
                {corr.shared_locations.slice(0, 5).map((loc: SharedLocationPoint, idx: number) => (
                  <Stack key={idx} direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="caption" color="text.secondary" sx={{ 
                      maxWidth: 180,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      • {loc.name || 'Unknown'}
                    </Typography>
                    <Chip 
                      label={`${loc.count || 1}x`}
                      size="small"
                      sx={{ 
                        height: 18, 
                        fontSize: '0.6rem',
                        bgcolor: alpha(theme.palette.warning.main, 0.2),
                        color: theme.palette.warning.dark,
                      }}
                    />
                  </Stack>
                ))}
                {corr.shared_locations.length > 5 && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    +{corr.shared_locations.length - 5} more locations...
                  </Typography>
                )}
              </Stack>
              <Typography variant="caption" color="primary" mt={0.5} display="block" fontWeight="bold">
                Click to view on map →
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };
  
  // Handle anomaly click - zoom to location (toggle on re-click)
  const handleAnomalyClick = (anomaly: PolAnomaly) => {
    // Toggle: if clicking the same anomaly, deselect it
    if (selectedAnomaly?.id === anomaly.id) {
      setSelectedAnomaly(null);
      return;
    }

    setSelectedAnomaly(anomaly);
    setSelectedCorrelation(null);
    setSelectedPattern(null);

    // Trip-shaped (route_deviation): fitBounds across origin + destination
    const trip = getAnomalyTripEndpoints(anomaly);
    if (trip) {
      const minLat = Math.min(trip.origin.lat, trip.destination.lat);
      const maxLat = Math.max(trip.origin.lat, trip.destination.lat);
      const minLon = Math.min(trip.origin.lon, trip.destination.lon);
      const maxLon = Math.max(trip.origin.lon, trip.destination.lon);
      try {
        mapRef.current?.fitBounds(
          [
            [minLon, minLat],
            [maxLon, maxLat],
          ],
          { padding: 80, duration: 800, maxZoom: 15 }
        );
      } catch {
        setViewport({
          latitude: (minLat + maxLat) / 2,
          longitude: (minLon + maxLon) / 2,
          zoom: 13,
        });
      }
      return;
    }

    // Point-shaped: zoom to that single location
    const pt = getAnomalyPoint(anomaly);
    if (pt) {
      setViewport({
        latitude: pt.lat,
        longitude: pt.lon,
        zoom: 16,
      });
    }
  };
  
  // Parse ML actual_behavior text into structured metrics
  const parseMlMetrics = (text: string) => {
    const scoreMatch = text.match(/Anomaly score ([\d.]+)/);
    const errorMatch = text.match(/reconstruction error:\s*([\d.]+)/);
    const hoursMatch = text.match(/active hours:\s*(\d+)/);
    if (!scoreMatch) return null;
    return {
      anomalyScore: parseFloat(scoreMatch[1]),
      reconstructionError: errorMatch ? parseFloat(errorMatch[1]) : null,
      activeHours: hoursMatch ? parseInt(hoursMatch[1], 10) : null,
    };
  };

  // Render anomaly card
  const renderAnomalyCard = (anomaly: PolAnomaly) => {
    const isSelected = selectedAnomaly?.id === anomaly.id;
    const loc = anomaly.actual_location || anomaly.expected_location;
    // Card is clickable if it has either a single map point OR trip endpoints (route_deviation)
    const point = getAnomalyPoint(anomaly);
    const trip = getAnomalyTripEndpoints(anomaly);
    const hasLocation = !!(point || trip);
    const typeKey = anomaly.anomaly_type.toLowerCase();
    const baseTypeInfo = getAnomalyTypeMeta(typeKey);
    const typeInfo: AnomalyTypeMeta = ANOMALY_TYPE_INFO[typeKey]
      ? baseTypeInfo
      : { ...baseTypeInfo, explanation: anomaly.expected_behavior || baseTypeInfo.explanation };
    const isMl = typeKey.startsWith('ml_');
    const mlMetrics = isMl ? parseMlMetrics(anomaly.actual_behavior || '') : null;
    const sevColor = SEVERITY_COLORS[anomaly.severity as keyof typeof SEVERITY_COLORS] || SEVERITY_COLORS.medium;
    const deviation = anomaly.deviation_score ?? (mlMetrics?.anomalyScore ?? 0);
    const deviationPct = Math.min(100, Math.round(deviation * 100));
    const locationName = trip
      ? `${trip.origin.name || 'Origin'} -> ${trip.destination.name || 'Destination'}`
      : (loc?.name || anomaly.actual_location?.name || anomaly.expected_location?.name);

    return (
      <Card
        key={anomaly.id}
        sx={{
          mb: 1,
          borderLeft: `4px solid ${sevColor}`,
          opacity: anomaly.is_acknowledged ? 0.6 : 1,
          cursor: hasLocation ? 'pointer' : 'default',
          transition: 'all 0.2s',
          border: isSelected ? `2px solid ${theme.palette.error.main}` : undefined,
          backgroundColor: isSelected ? alpha(theme.palette.error.main, 0.08) : undefined,
          '&:hover': hasLocation ? {
            boxShadow: theme.shadows[4],
            transform: 'translateY(-2px)',
          } : {}
        }}
        onClick={() => hasLocation && handleAnomalyClick(anomaly)}
      >
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          {/* Header: entity, severity, status */}
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Typography variant="subtitle2" fontWeight="bold">
                  {anomaly.entity_id}
                </Typography>
                <Chip
                  label={anomaly.severity}
                  size="small"
                  sx={{ bgcolor: sevColor, color: 'white', fontSize: '0.6rem', height: 18 }}
                />
              </Stack>
              <Typography variant="caption" fontWeight={600} color="text.primary" display="block" mt={0.25}>
                {typeInfo.label}
              </Typography>
            </Box>
            <Stack direction="row" spacing={0.5} flexShrink={0}>
              {isSelected && (
                <Chip label="On Map" size="small" color="error" variant="filled" sx={{ fontSize: '0.6rem', height: 18 }} />
              )}
              {anomaly.is_acknowledged && (
                <Chip label="Ack'd" size="small" color="success" variant="outlined" sx={{ fontSize: '0.6rem', height: 18 }} />
              )}
            </Stack>
          </Stack>

          {/* Type explanation */}
          <Typography variant="caption" color="text.secondary" display="block" mt={0.5} sx={{ lineHeight: 1.3 }}>
            {typeInfo.explanation}
          </Typography>

          {/* ML metrics (parsed from actual_behavior) */}
          {isMl && mlMetrics && (
            <Stack direction="row" spacing={1.5} mt={1} p={0.75}
              sx={{ bgcolor: alpha(theme.palette.info.main, 0.08), borderRadius: 1 }}
            >
              <Box textAlign="center">
                <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.6rem' }}>
                  Anomaly Score
                </Typography>
                <Typography variant="body2" fontWeight="bold" sx={{ color: sevColor }}>
                  {mlMetrics.anomalyScore.toFixed(3)}
                </Typography>
              </Box>
              {mlMetrics.reconstructionError !== null && (
                <Box textAlign="center">
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.6rem' }}>
                    Recon. Error
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {mlMetrics.reconstructionError.toFixed(4)}
                  </Typography>
                </Box>
              )}
              {mlMetrics.activeHours !== null && (
                <Box textAlign="center">
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.6rem' }}>
                    Active Hrs
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {mlMetrics.activeHours}
                  </Typography>
                </Box>
              )}
            </Stack>
          )}

          {/* Rule-based: expected vs actual */}
          {!isMl && (
            <Box mt={1} p={0.75} bgcolor={alpha(theme.palette.warning.main, 0.08)} borderRadius={1}>
              <Typography variant="caption" display="block" color="text.secondary" sx={{ lineHeight: 1.3 }}>
                <strong>Baseline:</strong> {anomaly.expected_behavior}
              </Typography>
              <Typography variant="caption" display="block" mt={0.25} sx={{ lineHeight: 1.3 }}>
                <strong>Observed:</strong> {anomaly.actual_behavior}
              </Typography>
            </Box>
          )}

          {/* ML: threshold context */}
          {isMl && !mlMetrics && (
            <Box mt={1} p={0.75} bgcolor={alpha(theme.palette.warning.main, 0.08)} borderRadius={1}>
              <Typography variant="caption" display="block" color="text.secondary" sx={{ lineHeight: 1.3 }}>
                <strong>Threshold:</strong> {anomaly.expected_behavior}
              </Typography>
              <Typography variant="caption" display="block" mt={0.25} sx={{ lineHeight: 1.3 }}>
                <strong>Observed:</strong> {anomaly.actual_behavior}
              </Typography>
            </Box>
          )}

          {/* Deviation score bar */}
          {deviation > 0 && (
            <Stack direction="row" alignItems="center" spacing={1} mt={1}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', whiteSpace: 'nowrap' }}>
                Deviation
              </Typography>
              <LinearProgress
                variant="determinate"
                value={deviationPct}
                sx={{
                  flex: 1, height: 5, borderRadius: 2,
                  bgcolor: alpha(theme.palette.divider, 0.2),
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 2,
                    bgcolor: deviationPct >= 80 ? '#f44336' : deviationPct >= 50 ? '#ff9800' : '#4caf50',
                  },
                }}
              />
              <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.6rem', minWidth: 28 }}>
                {deviationPct}%
              </Typography>
            </Stack>
          )}

          {/* Footer: timestamp + location */}
          <Stack direction="row" justifyContent="space-between" alignItems="center" mt={0.75}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
              {new Date(anomaly.anomaly_time).toLocaleString()}
            </Typography>
            {locationName && (
              <Typography variant="caption" color={hasLocation ? 'error.main' : 'text.secondary'}
                sx={{ fontSize: '0.65rem', textAlign: 'right' }}
              >
                {locationName}{hasLocation ? ' · click to view' : ''}
              </Typography>
            )}
          </Stack>
        </CardContent>
      </Card>
    );
  };
  
  // Sort entities in ascending order for the dropdown
  const sortedEntityOptions = useMemo(() => {
    const entities = dashboardData?.entities || [];
    // Alphanumeric sort (works for Student-07, Trainee-03, and codenames like DoubleCross75)
    return [...entities]
      .sort((a: EntitySummary, b: EntitySummary) =>
        a.entity_id.localeCompare(b.entity_id, undefined, { numeric: true, sensitivity: 'base' })
      )
      .map((entity: EntitySummary) => entity.entity_id);
  }, [dashboardData?.entities]);

  // Match Pathfinder: highlight the most recent DHOST and DHOQC course IDs (e.g. "DHOST 26-005").
  const mostRecentCourses = useMemo(
    () => resolveLatestCourses(availableCourses.map((c) => c.id)),
    [availableCourses]
  );
  
  // Only show loading spinner when actively fetching dashboard data (course is selected)
  if (loading && selectedCourse && !dashboardData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Compact Header Controls - Link Analysis Style */}
      <Paper sx={{ px: 1.5, py: 0.75, mb: 1, flexShrink: 0, overflow: 'hidden' }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'nowrap', minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <TimelineIcon color="primary" sx={{ fontSize: '1.2rem' }} />
            Pattern of Life
          </Typography>
          
          <Tooltip title="How embedding analysis works">
            <IconButton size="small" onClick={() => setEmbeddingInfoOpen(true)} sx={{ p: 0.25 }}>
              <InfoIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
            </IconButton>
          </Tooltip>
          
          <Divider orientation="vertical" flexItem sx={{ height: 24, my: 'auto' }} />
          
          {/* Course Selection - REQUIRED */}
          <FormControl size="small" sx={{ minWidth: 0 }} required>
            <Select
              value={selectedCourse}
              onChange={(e: SelectChangeEvent<string>) => {
                setSelectedCourse(e.target.value);
                setSelectedEntity(null);
              }}
              displayEmpty
              disabled={coursesLoading}
              sx={{ 
                '& .MuiSelect-select': { 
                  py: 0.5, 
                  px: 1.5, 
                  fontSize: '0.8rem',
                  minWidth: 120,
                  fontWeight: selectedCourse ? 'normal' : 600,
                },
                '& .MuiOutlinedInput-notchedOutline': { 
                  borderColor: selectedCourse ? 'divider' : 'primary.main',
                  borderWidth: selectedCourse ? 1 : 2,
                },
                bgcolor: selectedCourse ? 'transparent' : alpha(theme.palette.primary.main, 0.05),
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main',
                },
              }}
              renderValue={(value: string) => {
                if (coursesLoading) return 'Loading...';
                if (!value) return `Select ${courseSingular}`;
                const course = availableCourses.find((c) => c.id === value);
                const label = course ? course.id : value;
                const isMostRecent =
                  value === mostRecentCourses.dhost || value === mostRecentCourses.dhoqc;
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>{label}</span>
                    {isMostRecent && (
                      <Typography
                        component="span"
                        sx={{
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          color: '#00c896',
                          backgroundColor: 'rgba(0, 200, 150, 0.2)',
                          px: 0.75,
                          py: 0.25,
                          borderRadius: 1,
                          letterSpacing: '0.5px',
                          lineHeight: 1.2,
                        }}
                      >
                        LATEST
                      </Typography>
                    )}
                  </Box>
                );
              }}
            >
              {availableCourses.map((course: (typeof availableCourses)[number]) => {
                const isMostRecent =
                  course.id === mostRecentCourses.dhost || course.id === mostRecentCourses.dhoqc;
                return (
                  <MenuItem
                    key={course.id}
                    value={course.id}
                    dense
                    sx={isMostRecent ? {
                      backgroundColor: 'rgba(0, 200, 150, 0.15)',
                      borderLeft: '3px solid',
                      borderLeftColor: '#00c896',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 200, 150, 0.25)',
                      },
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(0, 200, 150, 0.3)',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 200, 150, 0.4)',
                        },
                      },
                    } : undefined}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" fontWeight={500}>{course.id}</Typography>
                        {isMostRecent && (
                          <Typography
                            component="span"
                            sx={{
                              fontSize: '0.65rem',
                              fontWeight: 700,
                              color: '#00c896',
                              backgroundColor: 'rgba(0, 200, 150, 0.2)',
                              px: 0.75,
                              py: 0.25,
                              borderRadius: 1,
                              letterSpacing: '0.5px',
                              lineHeight: 1.2,
                            }}
                          >
                            LATEST
                          </Typography>
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {course.entity_count} entities • {course.dwell_count} dwells
                      </Typography>
                    </Box>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          
          <Autocomplete
            size="small"
            value={selectedEntity || null}
            onChange={(_: SyntheticEvent, newValue: string | null) => setSelectedEntity(newValue || null)}
            options={sortedEntityOptions}
            disabled={!selectedCourse || coursesLoading}
            noOptionsText={
              !selectedCourse
                ? `Select a ${courseSingular} first`
                : loading
                  ? 'Loading entities…'
                  : 'No entities in this date range (check dwells / reference for this domain)'
            }
            renderInput={(params: AutocompleteRenderInputParams) => {
              const entityLabel = (selectedCourse?.includes('DHOST') || selectedCourse?.includes('DHOQC')) ? 'Trainee' : singular;
              return (
                <TextField
                  {...params}
                  placeholder={`Search ${entityLabel}...`}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      py: 0.5,
                      px: 1.5,
                      fontSize: '0.8rem',
                      minWidth: 120,
                      bgcolor: 'background.paper',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'divider',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main',
                      },
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '0.8rem',
                      '&::placeholder': {
                        opacity: 0.6,
                      },
                    },
                  }}
                />
              );
            }}
            renderOption={(props: HTMLAttributes<HTMLLIElement>, option: string) => (
              <Box 
                component="li" 
                {...props} 
                key={option}
                sx={{
                  fontSize: '0.75rem !important',
                  py: '6px !important',
                  px: '12px !important',
                  minHeight: '36px !important',
                  borderRadius: '4px',
                  mx: '4px',
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                  '&.Mui-focused': {
                    bgcolor: 'action.selected',
                  },
                }}
              >
                {option}
              </Box>
            )}
            getOptionLabel={(option: string) => option || ''}
            sx={{ minWidth: 140 }}
            slotProps={{
              paper: {
                elevation: 3,
                sx: {
                  mt: 0.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  '& .MuiAutocomplete-listbox': {
                    maxHeight: '300px',
                    fontSize: '0.75rem',
                    py: 0.5,
                  },
                  '& .MuiAutocomplete-noOptions': {
                    fontSize: '0.75rem',
                    py: 1.5,
                  },
                },
              },
              popper: {
                sx: {
                  '& .MuiAutocomplete-listbox': {
                    scrollbarGutter: 'stable',
                  },
                },
              },
            }}
            clearOnEscape
            selectOnFocus
            handleHomeEndKeys
            openOnFocus
            disableListWrap
          />
          
          <FormControl size="small" sx={{ minWidth: 0 }}>
            <Select
              value={selectedDeviceType}
              onChange={(e: SelectChangeEvent<string>) => setSelectedDeviceType(e.target.value)}
              displayEmpty
              sx={{ 
                '& .MuiSelect-select': { 
                  py: 0.5, 
                  px: 1.5, 
                  fontSize: '0.8rem',
                  minWidth: 80,
                },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
              }}
              renderValue={(value: string) => {
                if (!value) return 'Device Type';
                const device = deviceTypes.find((d) => d.id === value);
                return device?.label.split(' ')[0] || 'Device';
              }}
            >
              {deviceTypes.map((device: (typeof deviceTypes)[number]) => (
                <MenuItem key={device.id} value={device.id} dense>
                  {device.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {/* Date Range: Preset or Custom */}
          {dateRangeMode === 'preset' ? (
            <FormControl size="small" sx={{ minWidth: 0 }}>
              <Select<number>
                value={daysBack}
                onChange={(e: SelectChangeEvent<number>) => setDaysBack(Number(e.target.value))}
                sx={{ 
                  '& .MuiSelect-select': { 
                    py: 0.5, 
                    px: 1.5, 
                    fontSize: '0.8rem',
                    minWidth: 70,
                  },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                }}
                renderValue={(value: number) => {
                  if (value === 7) return '7d';
                  if (value === 14) return '2w';
                  if (value === 30) return '30d';
                  if (value === 60) return '60d';
                  if (value === 90) return '3mo';
                  if (value === 180) return '6mo';
                  if (value === 365) return '1y';
                  return `${value}d`;
                }}
              >
                <MenuItem value={7} dense>Last 7 days</MenuItem>
                <MenuItem value={14} dense>Last 2 weeks</MenuItem>
                <MenuItem value={30} dense>Last 30 days</MenuItem>
                <MenuItem value={60} dense>Last 60 days</MenuItem>
                <MenuItem value={90} dense>Last 3 months</MenuItem>
                <MenuItem value={180} dense>Last 6 months</MenuItem>
                <MenuItem value={365} dense>Last year</MenuItem>
              </Select>
            </FormControl>
          ) : (
            <Box
              onClick={() => setDateDialogOpen(true)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                bgcolor: alpha(theme.palette.primary.main, 0.25),
                borderRadius: 1,
                px: 1,
                py: 0.25,
                border: `1px solid ${alpha(theme.palette.common.white, 0.25)}`,
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.35),
                },
              }}
              role="button"
              aria-label="Select date range"
            >
              <CalendarIcon sx={{ fontSize: '0.95rem', color: 'white' }} />
              <Typography variant="caption" sx={{ color: 'white', fontWeight: 600, whiteSpace: 'nowrap' }}>
                {(startDate && endDate) ? `${startDate} → ${endDate}` : 'Select range'}
              </Typography>
              <Tooltip title="Switch to preset ranges">
                <IconButton
                  size="small"
                  onClick={(e: MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    setStartDate('');
                    setEndDate('');
                    setDateRangeMode('preset');
                  }}
                  sx={{ p: 0.25, ml: 0.25, color: 'white' }}
                >
                  <CloseIcon sx={{ fontSize: '0.9rem' }} />
                </IconButton>
              </Tooltip>
            </Box>
          )}
          
          {/* Toggle to custom date range (only show when in preset mode) */}
          {dateRangeMode === 'preset' && (
            <Tooltip title="Select custom date range">
              <IconButton
                size="small"
                onClick={() => {
                  const end = new Date();
                  const start = new Date();
                  start.setDate(end.getDate() - daysBack);
                  setLocalDateRange({ start, end });
                  setDateDialogOpen(true);
                }}
                sx={{ 
                  p: 0.5,
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                  }
                }}
              >
                <CalendarIcon sx={{ fontSize: '1rem', color: 'white' }} />
              </IconButton>
            </Tooltip>
          )}
          
          {loading && <CircularProgress size={16} />}
          
          {/* Right-aligned: entity count, export (DMAFB toggle moved to map toolbar) */}
          <Box sx={{ ml: 'auto', display: 'flex', gap: 0.75, alignItems: 'center' }}>
            <Tooltip title={selectedEntity ? `Export ${selectedEntity} to PowerPoint` : 'Select an entity to export PowerPoint'}>
              <span>
                <IconButton
                  size="small"
                  disabled={!selectedEntity}
                  onClick={() => {
                    if (selectedEntity) {
                      setPptxExportEntityId(selectedEntity);
                      setPptxExportNarrative(undefined);
                      setPptxThemeOpen(true);
                    }
                  }}
                  sx={{ p: 0.5 }}
                >
                  <SlideshowIcon sx={{ fontSize: '1.1rem' }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Stack>
      </Paper>

      {coursesError && (
        <Alert severity="error" sx={{ mb: 1, py: 0.5 }} onClose={() => setCoursesError(null)}>
          Could not load the domain list: {coursesError}
        </Alert>
      )}
      {!coursesLoading && !coursesError && availableCourses.length === 0 && (
        <Alert severity="info" sx={{ mb: 1, py: 0.5 }}>
          No domains found. Add active courses and entities in Settings → Device Reference. The domain list uses the
          reference table; dwell rows are optional for the list but needed for maps and behavioral scores.
        </Alert>
      )}
      
      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}

      {/* Embedding Processes Info Dialog — rich HTML overview */}
      <Dialog
        open={embeddingInfoOpen}
        onClose={() => setEmbeddingInfoOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { height: '90vh', p: 0 } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1, flexShrink: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon color="primary" />
            Pathfinder Pipeline — How It Works
          </Box>
          <IconButton size="small" onClick={() => setEmbeddingInfoOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          <iframe
            title="Pathfinder Pipeline Overview"
            style={{ width: '100%', height: '100%', border: 'none' }}
            srcDoc={PIPELINE_INFO_HTML}
          />
        </Box>
      </Dialog>

      {/* Tab Info Dialog */}
      <Dialog
        open={tabInfoOpen !== null}
        onClose={() => setTabInfoOpen(null)}
        maxWidth="sm"
        fullWidth
      >
        {tabInfoOpen !== null && (
          <>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InfoIcon color="primary" />
                {TAB_DESCRIPTIONS[tabInfoOpen].title}
              </Box>
              <IconButton size="small" onClick={() => setTabInfoOpen(null)}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                {TAB_DESCRIPTIONS[tabInfoOpen].description}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setTabInfoOpen(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Date Range Dialog (copied from LinkAnalysisPage UX) */}
      <Dialog
        open={dateDialogOpen}
        onClose={() => setDateDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        TransitionProps={{
          onEnter: () => {
            // Initialize local state with current values when dialog opens
            if (dateRangeMode === 'custom' && startDate && endDate) {
              setLocalDateRange({ start: new Date(startDate), end: new Date(endDate) });
            } else {
              const end = new Date();
              const start = new Date();
              start.setDate(start.getDate() - daysBack);
              setLocalDateRange({ start, end });
            }
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          Select Date Range
          <IconButton size="small" onClick={() => setDateDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {(localDateRange.start || localDateRange.end) && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: 'warning.main', borderRadius: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ color: 'warning.contrastText' }}>
                {localDateRange.start?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - {localDateRange.end?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </Typography>
              <IconButton size="small" onClick={() => setLocalDateRange({ start: null, end: null })} sx={{ color: 'warning.contrastText' }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>Quick Select</Typography>
          <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
            <Button variant="outlined" size="small" onClick={() => { const end = new Date(); const start = new Date(); start.setDate(start.getDate() - 7); setLocalDateRange({ start, end }); }}>Last 7 Days</Button>
            <Button variant="outlined" size="small" onClick={() => { const end = new Date(); const start = new Date(); start.setDate(start.getDate() - 14); setLocalDateRange({ start, end }); }}>Last 14 Days</Button>
            <Button variant="outlined" size="small" onClick={() => { const end = new Date(); const start = new Date(); start.setDate(start.getDate() - 30); setLocalDateRange({ start, end }); }}>Last 30 Days</Button>
            <Button variant="outlined" size="small" onClick={() => { const end = new Date(); const start = new Date(); start.setDate(start.getDate() - 90); setLocalDateRange({ start, end }); }}>Last 90 Days</Button>
          </Stack>
          <TextField
            label="Start Date"
            type="date"
            fullWidth
            size="small"
            value={localDateRange.start ? toISODate(localDateRange.start) : ''}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              const newStart = e.target.value ? new Date(e.target.value) : null;
              setLocalDateRange(prev => ({ ...prev, start: newStart }));
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />
          <TextField
            label="End Date"
            type="date"
            fullWidth
            size="small"
            value={localDateRange.end ? toISODate(localDateRange.end) : ''}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              const newEnd = e.target.value ? new Date(e.target.value) : null;
              setLocalDateRange(prev => ({ ...prev, end: newEnd }));
            }}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLocalDateRange({ start: null, end: null })}>Clear</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (localDateRange.start && localDateRange.end) {
                setStartDate(toISODate(localDateRange.start));
                setEndDate(toISODate(localDateRange.end));
                setDateRangeMode('custom');
              } else {
                setStartDate('');
                setEndDate('');
                setDateRangeMode('preset');
              }
              setDateDialogOpen(false);
            }}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Course Selection Prompt */}
      {!selectedCourse && !coursesLoading && (
        <Box 
          sx={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            justifyContent: 'center',
            bgcolor: alpha(theme.palette.background.paper, 0.8),
            borderRadius: 2,
            p: 4,
            textAlign: 'center',
          }}
        >
          <CalendarIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2, opacity: 0.7 }} />
          <Typography variant="h5" fontWeight={600} gutterBottom>
            {`Select a ${courseSingular} to Begin`}
          </Typography>
          <Typography color="text.secondary" sx={{ maxWidth: 400 }}>
            {`Choose a ${courseSingular.toLowerCase()} from the dropdown above to load Pattern of Life data. `}
            Visuals will automatically adjust to show the available date range.
          </Typography>
        </Box>
      )}
      
      {/* Main Content Row - Map Panel with integrated Sentinel + Right Panel */}
      {selectedCourse && (
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden', gap: 0 }}>
        {/* Map Panel with integrated Sentinel */}
        <Paper sx={{ 
          flex: 1, 
          minWidth: 0,
          overflow: 'hidden', 
          display: 'flex', 
          flexDirection: 'column',
        }}>
        {/* Header spanning full width */}
        <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle2" fontWeight="bold">
            Location Activity Map
          </Typography>
        </Box>
        
        {/* Content area with Sentinel and Map side by side */}
        <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden', position: 'relative' }}>
          {/* Left Panel - AI Sentinel (Collapsible) */}
          <Box sx={{ 
            display: 'flex',
            flexShrink: 0,
            height: '100%',
            transition: 'width 0.3s ease',
            width: showSentinel ? 280 : 0,
            overflow: 'hidden',
          }}>
            {showSentinel && (
              <Box sx={{ 
                width: 280, 
                height: '100%',
                display: 'flex', 
                flexDirection: 'column', 
                overflow: 'hidden',
                borderRight: 1,
                borderColor: 'divider',
              }}>
                <POLSentinel
                  entities={dashboardData?.entities || []}
                  patterns={dashboardData?.top_patterns || []}
                  correlations={dashboardData?.top_correlations || []}
                  anomalies={dashboardData?.recent_anomalies || []}
                  routePatterns={routePatterns}
                  highlightHour={wheelCellFilter?.hour ?? null}
                  highlightDay={wheelCellFilter?.day ?? null}
                  digitalPatterns={digitalPatterns}
                  digitalInsights={digitalInsights}
                  topAtypicalDays={dashboardData?.top_atypical_days || []}
                  selectedCourse={selectedCourse}
                  selectedEntity={selectedEntity}
                  daysBack={daysBack}
                  periodSummary={dashboardData?.period_summary}
                  onEntitySelect={setSelectedEntity}
                  onCourseSelect={setSelectedCourse}
                  onDaysBackChange={setDaysBack}
                  onTabChange={(tab: POLTabId | number) => {
                    setActiveTab(typeof tab === 'number' ? tab : polTabIndex(tab));
                  }}
                  temporalHeatmap={dashboardData?.temporal_heatmap || []}
                  onRouteSelect={setSelectedRoutePattern}
                  onCorrelationSelect={setSelectedCorrelation}
                  onAnomalySelect={setSelectedAnomaly}
                  // AI Embedding-powered callbacks
                  onFindSimilar={(entityId) => {
                    // Select the entity - this triggers the SimilarEntitiesPanel to show
                    setSelectedEntity(entityId);
                    setActiveTab(POL_TAB.ENTITIES);
                    console.log('[AI Sentinel] Find similar entities for:', entityId);
                  }}
                  onHighlightCluster={(entityIds) => {
                    // Could highlight these entities on the map
                    console.log('[AI Sentinel] Highlight cluster:', entityIds);
                  }}
                  onCompareEntities={(entityA, entityB) => {
                    console.log('[AI Sentinel] Compare:', entityA, 'vs', entityB);
                  }}
                  onExportPPTX={(entityId, aiNarrative) => {
                    setPptxExportEntityId(entityId);
                    setPptxExportNarrative(aiNarrative);
                    setPptxThemeOpen(true);
                  }}
                />
              </Box>
            )}
          </Box>
          
          {/* Collapse/Expand Tab */}
          <Box
            onClick={() => setShowSentinel(!showSentinel)}
            sx={{
              position: 'absolute',
              left: showSentinel ? 280 : 0,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 24,
              height: 80,
              bgcolor: 'primary.main',
              borderRadius: '0 8px 8px 0',
              cursor: 'pointer',
              transition: 'left 0.3s ease, background-color 0.2s',
              '&:hover': {
                bgcolor: 'primary.dark',
              },
              boxShadow: 2,
            }}
          >
            <Tooltip title={showSentinel ? "Hide Sentinel" : "Show Sentinel"} placement="right">
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', color: 'white' }}>
                {showSentinel ? (
                  <ChevronLeftIcon sx={{ fontSize: '1.2rem' }} />
                ) : (
                  <>
                    <AIIcon sx={{ fontSize: '1rem', mb: 0.5 }} />
                    <ChevronRightIcon sx={{ fontSize: '1rem' }} />
                  </>
                )}
              </Box>
            </Tooltip>
          </Box>
          
          {/* Map Container */}
          <Box 
            id="pol-map-container"
            ref={mapContainerRef}
            sx={{ 
              flex: 1, 
              minWidth: 0,
              ml: showSentinel ? 0 : 3, // Space for the tab when collapsed 
              position: 'relative',
              transition: 'margin-left 0.3s ease',
            }}
          >
            <MapGL
              ref={mapRef}
              mapboxAccessToken={MAPBOX_TOKEN}
              {...viewport}
              onMove={(evt) => setViewport(evt.viewState)}
              onMoveEnd={() => emitMapBounds()}
              mapStyle={getMapboxStyle(mapStyle)}
              projection={{name: 'mercator'}}
              style={{ width: '100%', height: '100%', cursor: isMeasuring ? 'crosshair' : 'auto' }}
              onLoad={handleMapLoad}
              preserveDrawingBuffer={true}
              terrain={is3DMode ? { source: 'mapbox-dem', exaggeration: 1.5 } : undefined}
              pitch={is3DMode ? 45 : 0}
              interactiveLayerIds={[...DIGITAL_EVENT_INTERACTIVE_LAYER_IDS, 'dwell-clusters', 'dwell-unclustered']}
              onClick={(event) => {
                // Handle measurement clicks first
                if (isMeasuring) {
                  handleMeasurementClick(event.lngLat);
                  return;
                }
                
                const features = event.features;
                // Clear selections when clicking empty map area
                if (!features || features.length === 0) {
                  setSelectedCorrelation(null);
                  setSelectedPattern(null);
                  setSelectedAnomaly(null);
                  setSelectedMarker(null);
                  return;
                }
                const feature = features[0];
                
                // Show popup for selected Digital PoL event points and type clusters
                const digitalLayerMatch = feature.layer?.id?.match(/^digital-pol-(events|clusters)-(.+)$/);
                if (digitalLayerMatch) {
                  const [, layerKind, eventType] = digitalLayerMatch;
                  const eventStyle = DIGITAL_EVENT_TYPE_STYLES.find((style) => style.type === eventType);
                  const isCluster = layerKind === 'clusters';
                  setSelectedMarker({
                    lat: event.lngLat.lat,
                    lon: event.lngLat.lng,
                    name: isCluster
                      ? `${eventStyle?.label ?? 'Digital'} Events`
                      : `${feature.properties?.entity_id || 'Entity'} ${String(feature.properties?.event_type || 'event').toUpperCase()}`,
                    visits: isCluster ? Number(feature.properties?.point_count || 0) : 1,
                    entities: feature.properties?.entity_id ? [feature.properties.entity_id] : [],
                    metricLabel: isCluster ? 'events' : 'event',
                    detail: isCluster ? 'Clustered Digital PoL events by type' : feature.properties?.detail,
                  });
                  return;
                }

                // Show popup for clusters, double-click to expand
                if (feature.layer?.id === 'dwell-clusters') {
                  const totalVisits = feature.properties?.total_visits || 0;
                  const locationCount = feature.properties?.point_count || 0;
                  setSelectedMarker({
                    lat: event.lngLat.lat,
                    lon: event.lngLat.lng,
                    name: `${locationCount} Locations`,
                    visits: totalVisits,
                    entities: [],
                    isCluster: true,
                    locationCount,
                  });
                  return;
                }
                
                // Show popup for unclustered points
                if (feature.layer?.id === 'dwell-unclustered') {
                  setSelectedMarker({
                    lat: event.lngLat.lat,
                    lon: event.lngLat.lng,
                    name: feature.properties?.name || 'Unknown Location',
                    visits: feature.properties?.visits || 0,
                    entities: (feature.properties?.entities || '').split(', ').filter(Boolean),
                  });
                }
              }}
            >
              <ScaleControl position="bottom-right" />
              
              {/* 3D Terrain Source */}
              <Source
                id="mapbox-dem"
                type="raster-dem"
                url="mapbox://mapbox.mapbox-terrain-dem-v1"
                tileSize={512}
                maxzoom={14}
              />
              
              {/* Unified Map Controls Panel */}
              <Paper
                elevation={2}
                sx={{
                  position: 'absolute',
                  top: 10,
                  right: 10,
                  zIndex: 1000,
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 1,
                  overflow: 'hidden',
                  bgcolor: isDarkMode ? 'rgba(30, 30, 30, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                  border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : 'none',
                }}
              >
                {/* Zoom In */}
                <Tooltip title="Zoom In" placement="left">
                  <IconButton onClick={handleZoomIn} size="small" sx={{ borderRadius: 0, p: 1, color: isDarkMode ? 'white' : 'inherit' }}>
                    <ZoomInIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                {/* Zoom Out */}
                <Tooltip title="Zoom Out" placement="left">
                  <IconButton onClick={handleZoomOut} size="small" sx={{ borderRadius: 0, p: 1, color: isDarkMode ? 'white' : 'inherit' }}>
                    <ZoomOutIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                {/* Reset North */}
                <Tooltip title="Reset North" placement="left">
                  <IconButton onClick={handleResetBearing} size="small" sx={{ borderRadius: 0, p: 1, color: isDarkMode ? 'white' : 'inherit' }}>
                    <CompassIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                {/* Fullscreen */}
                <Tooltip title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"} placement="left">
                  <IconButton onClick={toggleFullscreen} size="small" sx={{ borderRadius: 0, p: 1, color: isDarkMode ? 'white' : 'inherit' }}>
                    {isFullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>

                <Divider sx={{ borderColor: isDarkMode ? 'rgba(255,255,255,0.2)' : 'inherit' }} />

                {/* My Location */}
                <Tooltip title="My Location" placement="left">
                  <IconButton
                    onClick={() => {
                      navigator.geolocation?.getCurrentPosition(
                        (pos) => {
                          mapRef.current?.flyTo({
                            center: [pos.coords.longitude, pos.coords.latitude],
                            zoom: 14,
                            duration: 1000
                          });
                        },
                        (err) => console.error('Geolocation error:', err)
                      );
                    }}
                    size="small"
                    sx={{ borderRadius: 0, p: 1, color: isDarkMode ? 'white' : 'inherit' }}
                  >
                    <MyLocationIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                {/* Center on Entities */}
                <Tooltip title="Center on Entities" placement="left">
                  <IconButton onClick={centerOnEntities} size="small" sx={{ borderRadius: 0, p: 1, color: isDarkMode ? 'white' : 'inherit' }}>
                    <CenterIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                {/* Crop tabs to map viewport — map layers stay full */}
                <Tooltip
                  title={
                    filterByMapExtent
                      ? 'Showing only tab entries inside the current map view. Click to show all.'
                      : 'Crop right-panel tabs (Entities, Patterns, Correlations, Anomalies, Routes, Dwells) to the current map view.'
                  }
                  placement="left"
                >
                  <IconButton
                    onClick={() => setFilterByMapExtent((v) => !v)}
                    size="small"
                    sx={{
                      borderRadius: 0,
                      p: 1,
                      bgcolor: filterByMapExtent ? 'primary.main' : 'transparent',
                      color: filterByMapExtent ? 'white' : (isDarkMode ? 'white' : 'inherit'),
                      '&:hover': {
                        bgcolor: filterByMapExtent ? 'primary.dark' : 'action.hover',
                      },
                    }}
                  >
                    <FilterByMapIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                {/* 3D Mode */}
                <Tooltip title={is3DMode ? "Switch to 2D" : "Switch to 3D"} placement="left">
                  <IconButton
                    onClick={() => setIs3DMode(!is3DMode)}
                    size="small"
                    sx={{
                      borderRadius: 0,
                      p: 1,
                      bgcolor: is3DMode ? 'primary.main' : 'transparent',
                      color: is3DMode ? 'white' : (isDarkMode ? 'white' : 'inherit'),
                      '&:hover': { bgcolor: is3DMode ? 'primary.dark' : 'action.hover' },
                    }}
                  >
                    <Typography sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>3D</Typography>
                  </IconButton>
                </Tooltip>

                {/* Routes Toggle + Filter */}
                <Tooltip title={showAllRoutes ? 'Click: hide routes | Right-click: filter' : 'Show Routes'} placement="left">
                  <IconButton
                    onClick={() => {
                      if (!showAllRoutes) {
                        setShowAllRoutes(true);
                      } else if (routeFilterOpen) {
                        setRouteFilterOpen(false);
                      } else {
                        setShowAllRoutes(false);
                        setRouteFilterOpen(false);
                      }
                    }}
                    onContextMenu={(e: MouseEvent<HTMLButtonElement>) => {
                      e.preventDefault();
                      if (!showAllRoutes) setShowAllRoutes(true);
                      setRouteFilterOpen(prev => !prev);
                    }}
                    size="small"
                    sx={{
                      borderRadius: 0,
                      p: 1,
                      position: 'relative',
                      bgcolor: showAllRoutes ? 'success.main' : 'transparent',
                      color: showAllRoutes ? 'white' : (isDarkMode ? 'white' : 'inherit'),
                      '&:hover': { bgcolor: showAllRoutes ? 'success.dark' : 'action.hover' },
                    }}
                  >
                    <TrafficIcon fontSize="small" />
                    {showAllRoutes && (!routeUsageFilter.high || !routeUsageFilter.medium || !routeUsageFilter.low) && (
                      <Box sx={{
                        position: 'absolute', top: 2, right: 2,
                        width: 6, height: 6, borderRadius: '50%',
                        bgcolor: '#f59e0b',
                      }} />
                    )}
                  </IconButton>
                </Tooltip>

                {/* Legend Toggle */}
                <Tooltip title={showLegend ? 'Hide Legend' : 'Show Legend'} placement="left">
                  <IconButton
                    onClick={() => setShowLegend(!showLegend)}
                    size="small"
                    sx={{
                      borderRadius: 0,
                      p: 1,
                      bgcolor: showLegend ? 'warning.main' : 'transparent',
                      color: showLegend ? 'white' : (isDarkMode ? 'white' : 'inherit'),
                      '&:hover': { bgcolor: showLegend ? 'warning.dark' : 'action.hover' },
                    }}
                  >
                    <MapLegendIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                {/* Davis-Monthan AFB Exclusion Toggle */}
                <Tooltip
                  title={
                    !dmPolygonRings
                      ? 'Davis-Monthan exclusion zone not loaded'
                      : dmFilter === 'off'
                        ? 'Exclude Davis-Monthan AFB data'
                        : 'Show all data (incl. Davis-Monthan AFB)'
                  }
                  placement="left"
                >
                  <IconButton
                    onClick={() => setDmFilter(dmFilter === 'off' ? 'exclude' : 'off')}
                    size="small"
                    disabled={!dmPolygonRings}
                    sx={{
                      borderRadius: 0,
                      p: 1,
                      bgcolor: dmFilter === 'exclude' ? 'error.main' : 'transparent',
                      color: dmFilter === 'exclude' ? 'white' : (isDarkMode ? 'white' : 'inherit'),
                      opacity: dmPolygonRings ? 1 : 0.4,
                      '&:hover': { bgcolor: dmFilter === 'exclude' ? 'error.dark' : 'action.hover' },
                    }}
                  >
                    <Typography sx={{ fontWeight: 'bold', fontSize: '0.7rem', lineHeight: 1 }}>DM</Typography>
                  </IconButton>
                </Tooltip>

                <Divider sx={{ borderColor: isDarkMode ? 'rgba(255,255,255,0.2)' : 'inherit' }} />

                {/* Bookmarks */}
                <Tooltip title="Location Bookmarks" placement="left">
                  <IconButton onClick={(e: MouseEvent<HTMLButtonElement>) => setBookmarkAnchorEl(e.currentTarget)} size="small" sx={{ borderRadius: 0, p: 1, color: isDarkMode ? 'white' : 'inherit' }}>
                    <BookmarkIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                {/* Map Style Selector */}
                <Tooltip title="Map Style" placement="left">
                  <IconButton onClick={(e: MouseEvent<HTMLButtonElement>) => setStyleAnchorEl(e.currentTarget)} size="small" sx={{ borderRadius: 0, p: 1, color: isDarkMode ? 'white' : 'inherit' }}>
                    <SatelliteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                {/* Measure Distance */}
                <Tooltip title={isMeasuring ? "Cancel Measurement" : "Measure Distance"} placement="left">
                  <IconButton
                    onClick={toggleMeasurement}
                    size="small"
                    sx={{
                      borderRadius: 0,
                      p: 1,
                      bgcolor: isMeasuring ? 'primary.main' : 'transparent',
                      color: isMeasuring ? 'white' : (isDarkMode ? 'white' : 'inherit'),
                      '&:hover': { bgcolor: isMeasuring ? 'primary.dark' : 'action.hover' },
                    }}
                  >
                    <MeasureIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                {/* Clear Measurement */}
                {measurementPoints.length > 0 && (
                  <Tooltip title="Clear Measurement" placement="left">
                    <IconButton onClick={clearMeasurement} size="small" sx={{ borderRadius: 0, p: 1, color: 'error.main' }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Paper>

              {/* Route Usage Filter Popover */}
              {routeFilterOpen && (
                <Paper
                  sx={{
                    position: 'absolute',
                    top: 90,
                    right: 60,
                    zIndex: 1001,
                    p: 1.5,
                    minWidth: 160,
                    bgcolor: isDarkMode ? 'rgba(30, 30, 30, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(8px)',
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 'bold', color: isDarkMode ? 'white' : 'inherit', display: 'block', mb: 1 }}>
                    Route Usage Filter
                  </Typography>
                  {([
                    { key: 'high' as const, label: 'High (10+)', color: '#ef4444', count: routePatterns.filter(r => r.usage_count >= 10).length },
                    { key: 'medium' as const, label: 'Medium (5-9)', color: '#f59e0b', count: routePatterns.filter(r => r.usage_count >= 5 && r.usage_count < 10).length },
                    { key: 'low' as const, label: 'Low (<5)', color: '#22c55e', count: routePatterns.filter(r => r.usage_count < 5).length },
                  ]).map(level => (
                    <Box
                      key={level.key}
                      onClick={() => setRouteUsageFilter(prev => ({ ...prev, [level.key]: !prev[level.key] }))}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        py: 0.5,
                        px: 0.75,
                        mb: 0.25,
                        borderRadius: 1,
                        cursor: 'pointer',
                        opacity: routeUsageFilter[level.key] ? 1 : 0.4,
                        bgcolor: routeUsageFilter[level.key] ? alpha(level.color, 0.1) : 'transparent',
                        border: `1px solid ${routeUsageFilter[level.key] ? alpha(level.color, 0.4) : 'transparent'}`,
                        transition: 'all 0.15s',
                        '&:hover': { bgcolor: alpha(level.color, 0.15) },
                      }}
                    >
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: level.color, flexShrink: 0 }} />
                      <Typography variant="caption" sx={{ flex: 1, color: isDarkMode ? 'white' : 'inherit', fontSize: '0.7rem' }}>
                        {level.label}
                      </Typography>
                      <Typography variant="caption" sx={{ color: isDarkMode ? 'rgba(255,255,255,0.5)' : 'text.secondary', fontSize: '0.65rem' }}>
                        {level.count}
                      </Typography>
                    </Box>
                  ))}
                  <Divider sx={{ my: 0.75 }} />
                  <Stack direction="row" spacing={0.5}>
                    <Button
                      size="small"
                      onClick={() => setRouteUsageFilter({ high: true, medium: true, low: true })}
                      sx={{ flex: 1, fontSize: '0.6rem', py: 0.25, textTransform: 'none', minWidth: 0 }}
                    >
                      All
                    </Button>
                    <Button
                      size="small"
                      onClick={() => setRouteUsageFilter({ high: false, medium: false, low: false })}
                      sx={{ flex: 1, fontSize: '0.6rem', py: 0.25, textTransform: 'none', minWidth: 0 }}
                    >
                      None
                    </Button>
                  </Stack>
                </Paper>
              )}

              {/* Map Style Popover */}
              {styleAnchorEl && (
                <Paper
                  sx={{
                    position: 'absolute',
                    top: 10,
                    right: 60,
                    zIndex: 1001,
                    p: 2,
                    minWidth: 200,
                    bgcolor: isDarkMode ? 'rgba(30, 30, 30, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: isDarkMode ? 'white' : 'inherit' }}>
                    Map Style
                  </Typography>
                  <Grid container spacing={1}>
                    {[
                      { value: 'streets', label: 'Streets' },
                      { value: 'satellite', label: 'Satellite' },
                      { value: 'satellite-streets', label: 'Hybrid' },
                      { value: 'dark', label: 'Dark' },
                      { value: 'light', label: 'Light' },
                      { value: 'outdoors', label: 'Outdoors' }
                    ].map((style: { value: string; label: string }) => (
                      <Grid item xs={6} key={style.value}>
                        <Card
                          onClick={() => {
                            setMapStyle(style.value as MapStyleType);
                            setStyleAnchorEl(null);
                          }}
                          sx={{
                            cursor: 'pointer',
                            border: mapStyle === style.value ? '2px solid #2196f3' : `1px solid ${isDarkMode ? 'rgba(255,255,255,0.2)' : '#ddd'}`,
                            bgcolor: isDarkMode ? 'rgba(50, 50, 50, 0.9)' : 'white',
                            '&:hover': { boxShadow: 2 }
                          }}
                        >
                          <Box sx={{ p: 1, textAlign: 'center' }}>
                            <Typography variant="caption" sx={{ color: isDarkMode ? 'white' : 'inherit' }}>{style.label}</Typography>
                          </Box>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                  <Box sx={{ mt: 1, textAlign: 'right' }}>
                    <Typography
                      variant="caption"
                      sx={{ cursor: 'pointer', color: 'primary.main' }}
                      onClick={() => setStyleAnchorEl(null)}
                    >
                      Close
                    </Typography>
                  </Box>
                </Paper>
              )}

              {/* Measurement Line */}
              {measurementPoints.length >= 2 && (
                <Source
                  id="measurement-line"
                  type="geojson"
                  data={{
                    type: 'Feature',
                    properties: {},
                    geometry: {
                      type: 'LineString',
                      coordinates: measurementPoints
                    }
                  }}
                >
                  <Layer
                    id="measurement-line-layer"
                    type="line"
                    paint={{
                      'line-color': '#ff6b35',
                      'line-width': 3,
                      'line-dasharray': [2, 2]
                    }}
                  />
                </Source>
              )}

              {/* Measurement Points */}
              {measurementPoints.map((point: [number, number], index: number) => (
                <Marker key={`measure-point-${index}`} longitude={point[0]} latitude={point[1]}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      bgcolor: '#ff6b35',
                      border: '2px solid white',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                    }}
                  />
                </Marker>
              ))}

              {/* Measurement Result Display */}
              {measurementPoints.length >= 2 && totalDistance > 0 && (
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 40,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    bgcolor: 'rgba(255, 107, 53, 0.95)',
                    color: 'white',
                    px: 2,
                    py: 1,
                    borderRadius: 1,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                    zIndex: 1000,
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {formatDistance(totalDistance)}
                  </Typography>
                </Box>
              )}
              
              {/* Map Legend - shown when legend button is active */}
              {showLegend && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 90,
                    left: 52,
                    zIndex: 999,
                    backgroundColor: 'rgba(30, 30, 30, 0.95)',
                    borderRadius: 1,
                    p: 1.5,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                    minWidth: 150,
                  }}
                >
                  {/* Dwell Locations */}
                  <Typography variant="caption" fontWeight="bold" display="block" mb={0.5} color="white">
                    Dwell Locations
                  </Typography>
                  <Stack spacing={0.5} mb={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box sx={{ 
                        width: 14, 
                        height: 14, 
                        borderRadius: '50%', 
                        bgcolor: 'rgba(255, 0, 0, 0.3)',
                        border: '2px solid #ff0000',
                      }} />
                      <Typography variant="caption" color="white">Single Location</Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box sx={{ 
                        width: 18, 
                        height: 18, 
                        borderRadius: '50%', 
                        bgcolor: '#ff6b6b',
                        border: '2px solid white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <Typography sx={{ fontSize: '0.5rem', color: 'white', fontWeight: 'bold' }}>5</Typography>
                      </Box>
                      <Typography variant="caption" color="white">Clustered (count)</Typography>
                    </Stack>
                  </Stack>
                  
                  {/* Digital PoL Events */}
                  <Typography variant="caption" fontWeight="bold" display="block" mb={0.5} color="white">
                    Digital PoL Events
                  </Typography>
                  <Stack spacing={0.5} mb={1.5}>
                    {DIGITAL_EVENT_TYPE_STYLES.map((eventStyle) => (
                      <Stack key={eventStyle.type} direction="row" spacing={1} alignItems="center">
                        <Box sx={{
                          width: 9,
                          height: 9,
                          borderRadius: '50%',
                          bgcolor: eventStyle.color,
                          border: '1.5px solid white',
                          boxShadow: `0 0 0 5px ${alpha(eventStyle.color, 0.16)}`,
                          ml: '5px',
                          mr: '1px',
                        }} />
                        <Typography variant="caption" color="white">{eventStyle.label}</Typography>
                      </Stack>
                    ))}
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box sx={{
                        width: 18,
                        height: 18,
                        borderRadius: '50%',
                        bgcolor: '#58a6ff',
                        border: '2px solid white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <Typography sx={{ fontSize: '0.5rem', color: 'white', fontWeight: 'bold' }}>8</Typography>
                      </Box>
                      <Typography variant="caption" color="white">Clustered by type</Typography>
                    </Stack>
                  </Stack>

                  {/* Route Usage */}
                  <Typography variant="caption" fontWeight="bold" display="block" mb={0.5} color="white">
                    Route Usage
                  </Typography>
                  <Stack spacing={0.5} mb={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box sx={{ width: 20, height: 4, bgcolor: '#ef4444', borderRadius: 0.5 }} />
                      <Typography variant="caption" color="white">High (10+)</Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box sx={{ width: 20, height: 4, bgcolor: '#f59e0b', borderRadius: 0.5 }} />
                      <Typography variant="caption" color="white">Medium (5-9)</Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box sx={{ width: 20, height: 4, bgcolor: '#22c55e', borderRadius: 0.5 }} />
                      <Typography variant="caption" color="white">Low (1-4)</Typography>
                    </Stack>
                  </Stack>

                  {/* Anomaly Visualization */}
                  <Typography variant="caption" fontWeight="bold" display="block" mb={0.5} color="white">
                    Anomalies
                  </Typography>
                  <Stack spacing={0.5}>
                    {/* Point-anomaly: pulsing red ring + ! marker */}
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box sx={{ position: 'relative', width: 18, height: 18 }}>
                        <Box sx={{
                          position: 'absolute', inset: 0,
                          borderRadius: '50%',
                          bgcolor: alpha('#f44336', 0.25),
                          border: '2px solid #f44336',
                        }} />
                        <Box sx={{
                          position: 'absolute', top: 3, left: 3,
                          width: 12, height: 12,
                          borderRadius: '50%',
                          bgcolor: '#f44336',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.5rem',
                          fontWeight: 'bold',
                          border: '1.5px solid white',
                        }}>!</Box>
                      </Box>
                      <Typography variant="caption" color="white">Anomaly Event</Typography>
                    </Stack>
                    {/* Route-deviation: dashed line between green start + red end */}
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box sx={{ display: 'flex', alignItems: 'center', width: 38 }}>
                        <Box sx={{
                          width: 8, height: 8, borderRadius: '50%',
                          bgcolor: '#22c55e', border: '1.5px solid white',
                          flexShrink: 0,
                        }} />
                        <Box sx={{
                          flex: 1,
                          height: 0,
                          borderTop: '2px dashed #f44336',
                        }} />
                        <Box sx={{
                          width: 8, height: 8, borderRadius: '50%',
                          bgcolor: '#f44336', border: '1.5px solid white',
                          flexShrink: 0,
                        }} />
                      </Box>
                      <Typography variant="caption" color="white">Route Deviation</Typography>
                    </Stack>
                    {/* Severity ramp legend */}
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Stack direction="row" spacing={0.25}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: SEVERITY_COLORS.low }} />
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: SEVERITY_COLORS.medium }} />
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: SEVERITY_COLORS.high }} />
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: SEVERITY_COLORS.critical }} />
                      </Stack>
                      <Typography variant="caption" color="white">Severity (low - critical)</Typography>
                    </Stack>
                  </Stack>
                </Box>
              )}
              
              {/* City Bookmarks Menu */}
              <Menu
                anchorEl={bookmarkAnchorEl}
                open={Boolean(bookmarkAnchorEl)}
                onClose={() => setBookmarkAnchorEl(null)}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
              >
                {cityBookmarks.map((city: (typeof cityBookmarks)[number]) => (
                  <MenuItem
                    key={city.name}
                    onClick={() => handleCitySelect(city)}
                    sx={{ minWidth: 150 }}
                  >
                    <ListItemIcon>
                      <PlaceIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{city.name}</ListItemText>
                  </MenuItem>
                ))}
              </Menu>
              
              {/* Davis-Monthan AFB Exclusion Zone Polygon */}
              {dmPolygon && dmFilter === 'exclude' && (
                <Source id="dm-exclusion-zone" type="geojson" data={dmPolygon}>
                  <Layer
                    id="dm-zone-fill"
                    type="fill"
                    paint={{
                      'fill-color': '#ef4444',
                      'fill-opacity': 0.15,
                    }}
                  />
                  <Layer
                    id="dm-zone-outline"
                    type="line"
                    paint={{
                      'line-color': '#ef4444',
                      'line-width': 3,
                      'line-dasharray': [4, 2],
                      'line-opacity': 0.8,
                    }}
                  />
                  <Layer
                    id="dm-zone-label"
                    type="symbol"
                    layout={{
                      'text-field': 'DMAFB (Excluded)',
                      'text-size': 14,
                      'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
                      'text-anchor': 'center',
                      'text-allow-overlap': true,
                    }}
                    paint={{
                      'text-color': '#ef4444',
                      'text-halo-color': isDarkMode ? '#1a1a2e' : '#ffffff',
                      'text-halo-width': 2,
                    }}
                  />
                </Source>
              )}
              
              {/* Clustered Dwell Locations (like Pathfinder) */}
              <Source
                id="dwell-locations"
                type="geojson"
                data={{
                  type: 'FeatureCollection',
                  features: wheelFilteredMarkers.map((loc: LocationMarker) => ({
                    type: 'Feature' as const,
                    properties: {
                      name: loc.name,
                      visits: loc.visits,
                      entities: loc.entities.join(', '),
                    },
                    geometry: {
                      type: 'Point' as const,
                      coordinates: [loc.lon, loc.lat],
                    },
                  })),
                }}
                cluster={true}
                clusterMaxZoom={16}
                clusterRadius={60}
                clusterProperties={{
                  total_visits: ['+', ['get', 'visits']],
                }}
              >
                {/* Cluster circles — sized by total visits */}
                <Layer
                  id="dwell-clusters"
                  type="circle"
                  filter={['has', 'point_count']}
                  paint={{
                    'circle-color': [
                      'interpolate', ['linear'], ['get', 'total_visits'],
                      1, '#ff6b6b',
                      50, '#ff4444',
                      200, '#cc0000',
                      500, '#990000'
                    ],
                    'circle-radius': [
                      'interpolate', ['linear'], ['get', 'total_visits'],
                      1, 16,
                      50, 22,
                      200, 30,
                      500, 40
                    ],
                    'circle-opacity': 0.7,
                    'circle-stroke-width': 2,
                    'circle-stroke-color': '#ffffff',
                    'circle-stroke-opacity': 0.9,
                  }}
                />

                {/* Cluster label — total visits */}
                <Layer
                  id="dwell-cluster-count"
                  type="symbol"
                  filter={['has', 'point_count']}
                  layout={{
                    'text-field': ['to-string', ['get', 'total_visits']],
                    'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
                    'text-size': 12,
                  }}
                  paint={{
                    'text-color': '#ffffff',
                  }}
                />
                
                {/* Unclustered dwell circles (red like Pathfinder) */}
                <Layer
                  id="dwell-unclustered"
                  type="circle"
                  filter={['!', ['has', 'point_count']]}
                  paint={{
                    'circle-radius': [
                      'interpolate',
                      ['linear'],
                      ['zoom'],
                      8, 6,
                      12, 12,
                      16, 20,
                      20, 30
                    ],
                    'circle-color': '#ff0000',
                    'circle-opacity': 0.3,
                    'circle-stroke-color': '#ff0000',
                    'circle-stroke-width': 2,
                    'circle-stroke-opacity': 0.8,
                  }}
                />
              </Source>

              {DIGITAL_EVENT_TYPE_STYLES.map((eventStyle) => {
                const features = selectedDigitalEventGeoJson.features.filter(
                  (feature) => feature.properties.event_type === eventStyle.type,
                );
                if (features.length === 0) return null;

                return (
                  <Source
                    key={eventStyle.type}
                    id={`digital-pol-events-source-${eventStyle.type}`}
                    type="geojson"
                    data={{ ...selectedDigitalEventGeoJson, features } as any}
                    cluster={true}
                    clusterMaxZoom={15}
                    clusterRadius={36}
                  >
                    <Layer
                      id={`digital-pol-clusters-${eventStyle.type}`}
                      type="circle"
                      filter={['has', 'point_count']}
                      paint={{
                        'circle-color': eventStyle.color,
                        'circle-radius': [
                          'interpolate',
                          ['linear'],
                          ['get', 'point_count'],
                          2, 11,
                          10, 15,
                          30, 20,
                        ],
                        'circle-opacity': 0.72,
                        'circle-stroke-color': '#ffffff',
                        'circle-stroke-width': 2,
                        'circle-stroke-opacity': 0.85,
                      }}
                    />
                    <Layer
                      id={`digital-pol-cluster-count-${eventStyle.type}`}
                      type="symbol"
                      filter={['has', 'point_count']}
                      layout={{
                        'text-field': ['to-string', ['get', 'point_count']],
                        'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
                        'text-size': 10,
                      }}
                      paint={{
                        'text-color': '#ffffff',
                      }}
                    />
                    <Layer
                      id={`digital-pol-events-halo-${eventStyle.type}`}
                      type="circle"
                      filter={['!', ['has', 'point_count']]}
                      paint={{
                        'circle-radius': 9,
                        'circle-color': eventStyle.color,
                        'circle-opacity': 0.14,
                      }}
                    />
                    <Layer
                      id={`digital-pol-events-${eventStyle.type}`}
                      type="circle"
                      filter={['!', ['has', 'point_count']]}
                      paint={{
                        'circle-radius': 4.5,
                        'circle-color': eventStyle.color,
                        'circle-opacity': 0.95,
                        'circle-stroke-color': isDarkMode ? '#0d1117' : '#ffffff',
                        'circle-stroke-width': 1.5,
                      }}
                    />
                  </Source>
                );
              })}
              
              {/* Flash marker for dwell zoom from list */}
              {flashDwellLocation && (
                <Marker
                  longitude={flashDwellLocation.lon}
                  latitude={flashDwellLocation.lat}
                  anchor="center"
                >
                  <Box sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    border: '3px solid',
                    borderColor: '#ffeb3b',
                    backgroundColor: 'rgba(255, 235, 59, 0.25)',
                    '@keyframes dwellFlash': {
                      '0%': { transform: 'scale(0.5)', opacity: 1, boxShadow: '0 0 0 0 rgba(255,235,59,0.8)' },
                      '50%': { transform: 'scale(1.5)', opacity: 0.7, boxShadow: '0 0 20px 10px rgba(255,235,59,0.4)' },
                      '100%': { transform: 'scale(0.5)', opacity: 0, boxShadow: '0 0 0 0 rgba(255,235,59,0)' },
                    },
                    animation: 'dwellFlash 0.6s ease-out 3',
                  }} />
                </Marker>
              )}

              {/* Popup for selected marker or cluster — shared dark command card */}
              {selectedMarker && (
                <Popup
                  longitude={selectedMarker.lon}
                  latitude={selectedMarker.lat}
                  anchor="bottom"
                  onClose={() => setSelectedMarker(null)}
                  closeButton={false}
                  closeOnClick={false}
                  className={popupClassName}
                  maxWidth="320px"
                  style={{ padding: 0, background: 'transparent', boxShadow: 'none' }}
                >
                  {selectedMarker.isCluster ? (
                    <DarkMapPopupShell
                      accent="#90caf9"
                      title={`${selectedMarker.visits} total visits`}
                      badge="CLUSTER"
                      onClose={() => setSelectedMarker(null)}
                      actions={
                        <button
                          type="button"
                          style={{
                            border: 'none',
                            background: 'transparent',
                            color: '#69f0ae',
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                            cursor: 'pointer',
                            padding: 0,
                          }}
                          onClick={() => {
                            const mapboxMap = mapRef.current?.getMap?.();
                            if (mapboxMap) {
                              mapboxMap.easeTo({
                                center: [selectedMarker.lon, selectedMarker.lat],
                                zoom: mapboxMap.getZoom() + 3,
                              });
                              setSelectedMarker(null);
                            }
                          }}
                        >
                          Zoom in
                        </button>
                      }
                    >
                      <DarkMapPopupRow label="Locs">{selectedMarker.locationCount} locations</DarkMapPopupRow>
                      <DarkMapPopupCoords lat={selectedMarker.lat} lon={selectedMarker.lon} />
                    </DarkMapPopupShell>
                  ) : (
                    <DarkMapPopupShell
                      accent="#69f0ae"
                      title={selectedMarker.name}
                      onClose={() => setSelectedMarker(null)}
                    >
                      <DarkMapPopupRow label={selectedMarker.metricLabel ?? 'Visits'}>
                        {selectedMarker.visits}
                      </DarkMapPopupRow>
                      {selectedMarker.detail && (
                        <DarkMapPopupRow label="Detail">{selectedMarker.detail}</DarkMapPopupRow>
                      )}
                      {selectedMarker.entities.length > 0 && (
                        <DarkMapPopupRow label="Entities" mono>
                          {selectedMarker.entities.join(', ')}
                        </DarkMapPopupRow>
                      )}
                      <DarkMapPopupCoords lat={selectedMarker.lat} lon={selectedMarker.lon} />
                    </DarkMapPopupShell>
                  )}
                </Popup>
              )}
              
              {/* Route Line - shown when a route is selected */}
              {routeLineData && (
                <Source id="route-line" type="geojson" data={routeLineData}>
                  {/* Animated dashed line showing direction */}
                  <Layer
                    id="route-line-animated"
                    type="line"
                    paint={{
                      'line-color': theme.palette.secondary.main,
                      'line-width': 4,
                      'line-dasharray': [0, 2, 1],
                    }}
                  />
                  {/* Solid line underneath */}
                  <Layer
                    id="route-line-base"
                    type="line"
                    beforeId="route-line-animated"
                    paint={{
                      'line-color': theme.palette.primary.main,
                      'line-width': 6,
                      'line-opacity': 0.8,
                    }}
                  />
                  {/* Arrow symbols showing direction */}
                  <Layer
                    id="route-arrows"
                    type="symbol"
                    layout={{
                      'symbol-placement': 'line',
                      'symbol-spacing': 100,
                      'icon-image': 'arrow',
                      'icon-size': 0.5,
                      'icon-allow-overlap': true,
                    }}
                  />
                </Source>
              )}
              
              {/* Route Stop Markers - numbered markers for each stop */}
              {routeMarkers.filter((m: RouteStopMarker) => m.lat && m.lon && !isNaN(m.lat) && !isNaN(m.lon)).map((marker: RouteStopMarker, idx: number) => (
                <Marker
                  key={`route-marker-${idx}`}
                  longitude={marker.lon}
                  latitude={marker.lat}
                  anchor="center"
                >
                  <Box
                    sx={{
                      width: marker.isFirst || marker.isLast ? 32 : 24,
                      height: marker.isFirst || marker.isLast ? 32 : 24,
                      borderRadius: '50%',
                      backgroundColor: marker.isFirst 
                        ? theme.palette.success.main 
                        : marker.isLast 
                          ? theme.palette.error.main 
                          : theme.palette.primary.main,
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      fontSize: marker.isFirst || marker.isLast ? 14 : 12,
                      border: '3px solid #fff',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                      cursor: 'pointer',
                      transition: 'transform 0.2s',
                      '&:hover': {
                        transform: 'scale(1.2)',
                      },
                    }}
                    title={`${marker.order}. ${marker.name}`}
                  >
                    {marker.isFirst ? '▶' : marker.isLast ? '◼' : marker.order}
                  </Box>
                </Marker>
              ))}
              
              {/* Route info popup when route is selected — shared dark command card */}
              {selectedRoute && routeMarkers.length > 0 && (
                <Popup
                  longitude={routeMarkers[0].lon}
                  latitude={routeMarkers[0].lat}
                  anchor="bottom"
                  offset={[0, -20] as [number, number]}
                  onClose={() => setSelectedRoute(null)}
                  closeButton={false}
                  closeOnClick={false}
                  className={popupClassName}
                  maxWidth="320px"
                  style={{ padding: 0, background: 'transparent', boxShadow: 'none' }}
                >
                  <DarkMapPopupShell
                    accent="#ce93d8"
                    title={`${selectedRoute.entity_id}'s Route`}
                    badge="ROUTE"
                    onClose={() => setSelectedRoute(null)}
                  >
                    <DarkMapPopupRow label="Stops">{selectedRoute.sequence_length}</DarkMapPopupRow>
                    <DarkMapPopupRow label="Seen">{selectedRoute.occurrence_count}x observed</DarkMapPopupRow>
                    {routeMarkers.map((m: RouteStopMarker, i: number) => (
                      <DarkMapPopupRow key={i} label={`${m.order}.`}>{m.name}</DarkMapPopupRow>
                    ))}
                    <DarkMapPopupCoords lat={routeMarkers[0].lat} lon={routeMarkers[0].lon} />
                  </DarkMapPopupShell>
                </Popup>
              )}
              
              {/* All Routes - color coded by usage (High=Red, Medium=Orange, Low=Green) */}
              {/* Hidden when a specific route is selected or compare mode is active to reduce visual clutter */}
              {allRoutesGeoJSON && !selectedRoutePattern && !isComparing && (
                <Source id="all-routes" type="geojson" data={allRoutesGeoJSON}>
                  {/* Route lines - colored by usage */}
                  <Layer
                    id="all-routes-lines"
                    type="line"
                    paint={{
                      'line-color': ['get', 'color'],
                      'line-width': [
                        'interpolate', ['linear'], ['zoom'],
                        8, 2,
                        12, 3,
                        16, 4
                      ],
                      'line-opacity': 0.7,
                    }}
                  />
                </Source>
              )}
              
              {/* Route Pattern GPS Line - rich route with actual GPS path */}
              {routePatternLineData && (
                <Source id="route-pattern-line" type="geojson" data={routePatternLineData}>
                  {/* Glow effect underneath */}
                  <Layer
                    id="route-pattern-glow"
                    type="line"
                    paint={{
                      'line-color': '#00ff88',
                      'line-width': 12,
                      'line-opacity': 0.3,
                      'line-blur': 8,
                    }}
                  />
                  {/* Main solid line */}
                  <Layer
                    id="route-pattern-base"
                    type="line"
                    paint={{
                      'line-color': '#00ff88',
                      'line-width': 4,
                      'line-opacity': 0.9,
                    }}
                  />
                  {/* Animated dashed overlay */}
                  <Layer
                    id="route-pattern-animated"
                    type="line"
                    paint={{
                      'line-color': '#ffffff',
                      'line-width': 2,
                      'line-dasharray': [2, 4],
                    }}
                  />
                </Source>
              )}
              
              {/* Route Pattern Origin Marker */}
              {selectedRoutePattern?.origin?.lat && selectedRoutePattern?.origin?.lon && 
               !isNaN(selectedRoutePattern.origin.lat) && !isNaN(selectedRoutePattern.origin.lon) && (
                <Marker
                  longitude={selectedRoutePattern.origin.lon}
                  latitude={selectedRoutePattern.origin.lat}
                  anchor="center"
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      backgroundColor: theme.palette.success.main,
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      fontSize: 18,
                      border: '4px solid #fff',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
                    }}
                    title={`Origin: ${selectedRoutePattern.origin.name}`}
                  >
                    ▶
                  </Box>
                </Marker>
              )}
              
              {/* Route Pattern Destination Marker */}
              {selectedRoutePattern?.destination?.lat && selectedRoutePattern?.destination?.lon &&
               !isNaN(selectedRoutePattern.destination.lat) && !isNaN(selectedRoutePattern.destination.lon) && (
                <Marker
                  longitude={selectedRoutePattern.destination.lon}
                  latitude={selectedRoutePattern.destination.lat}
                  anchor="center"
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      backgroundColor: theme.palette.error.main,
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      fontSize: 18,
                      border: '4px solid #fff',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
                    }}
                    title={`Destination: ${selectedRoutePattern.destination.name}`}
                  >
                    ◼
                  </Box>
                </Marker>
              )}
              
              {/* COMPARISON MODE - Multiple routes from different entities */}
              {isComparing && compareRoutes.filter(r => {
                if (hiddenCompareRoutes.has(r.id)) return false;
                if (dmFilter === 'exclude' && isRouteInExclusionZone(r, dmPolygonRings)) return false;
                return true;
              }).map((route: CompareRouteRow) => {
                const originalIdx = compareRoutes.indexOf(route);
                const routeColor = route.is_primary ? '#00ff88' : COLORS[originalIdx % COLORS.length];
                let validCoords = normalizeRouteGeometry(route.route_geometry, route.origin);
                if (
                  validCoords.length < 2 &&
                  route.origin?.lat != null &&
                  route.origin?.lon != null &&
                  route.destination?.lat != null &&
                  route.destination?.lon != null
                ) {
                  validCoords = [
                    [route.origin.lon, route.origin.lat],
                    [route.destination.lon, route.destination.lat],
                  ];
                }
                if (validCoords.length < 2) return null;
                
                const geojsonData: GeoJSON.FeatureCollection<GeoJSON.LineString> = {
                  type: 'FeatureCollection',
                  features: [{
                    type: 'Feature',
                    properties: { entity: route.entity_id },
                    geometry: { type: 'LineString', coordinates: validCoords }
                  }]
                };
                
                return (
                  <React.Fragment key={`compare-${route.id}`}>
                    <Source id={`compare-route-${route.id}`} type="geojson" data={geojsonData}>
                      <Layer
                        id={`compare-route-glow-${route.id}`}
                        type="line"
                        paint={{
                          'line-color': routeColor,
                          'line-width': route.is_primary ? 10 : 8,
                          'line-opacity': 0.3,
                          'line-blur': 6,
                        }}
                      />
                      <Layer
                        id={`compare-route-line-${route.id}`}
                        type="line"
                        paint={{
                          'line-color': routeColor,
                          'line-width': route.is_primary ? 4 : 3,
                          'line-opacity': 0.9,
                        }}
                      />
                    </Source>
                    {/* Origin marker for this route */}
                    {route.origin.lat && route.origin.lon && !isNaN(route.origin.lat) && !isNaN(route.origin.lon) && (
                      <Marker
                        longitude={route.origin.lon}
                        latitude={route.origin.lat}
                        anchor="center"
                      >
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: '50%',
                            backgroundColor: routeColor,
                            color: '#000',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 'bold',
                            fontSize: 10,
                            border: '3px solid #fff',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
                          }}
                          title={`${route.entity_id}: ${route.origin.name}`}
                        >
                          {route.entity_id.slice(0, 2)}
                        </Box>
                      </Marker>
                    )}
                    {/* Destination marker for this route */}
                    {route.destination.lat && route.destination.lon && !isNaN(route.destination.lat) && !isNaN(route.destination.lon) && (
                      <Marker
                        longitude={route.destination.lon}
                        latitude={route.destination.lat}
                        anchor="center"
                      >
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: '50%',
                            backgroundColor: routeColor,
                            color: '#000',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 'bold',
                            fontSize: 10,
                            border: '3px solid #fff',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
                          }}
                          title={`${route.entity_id}: ${route.destination.name}`}
                        >
                          {route.entity_id.slice(0, 2)}
                        </Box>
                      </Marker>
                    )}
                  </React.Fragment>
                );
              })}
              
              {/* Comparison Legend Panel */}
              {isComparing && compareRoutes.length > 0 && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 10,
                    left: 10,
                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                    borderRadius: 2,
                    p: 1.5,
                    maxWidth: 280,
                    boxShadow: theme.shadows[4],
                    zIndex: 100,
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Route Comparison ({compareRoutes.length})
                    </Typography>
                    <Stack direction="row" spacing={0.25} alignItems="center">
                      <Button
                        size="small"
                        onClick={() => setHiddenCompareRoutes(new Set())}
                        sx={{ minWidth: 'auto', p: 0.25, fontSize: '0.6rem' }}
                        title="Show all routes"
                      >
                        All
                      </Button>
                      <Button
                        size="small"
                        onClick={() => setHiddenCompareRoutes(new Set(compareRoutes.map(r => r.id)))}
                        sx={{ minWidth: 'auto', p: 0.25, fontSize: '0.6rem' }}
                        title="Hide all routes"
                      >
                        None
                      </Button>
                      <Button 
                        size="small" 
                        onClick={exitCompareMode}
                        sx={{ minWidth: 'auto', p: 0.5 }}
                      >
                        ✕
                      </Button>
                    </Stack>
                  </Stack>
                  <Stack spacing={0.25}>
                    {compareRoutes.map((route: CompareRouteRow, idx: number) => {
                      const routeColor = route.is_primary ? '#00ff88' : COLORS[idx % COLORS.length];
                      const isHidden = hiddenCompareRoutes.has(route.id);
                      const trips = route.trip_instances || [];
                      return (
                        <Box
                          key={route.id}
                          sx={{
                            opacity: isHidden ? 0.4 : 1,
                            cursor: 'pointer',
                            borderRadius: 1,
                            px: 0.5,
                            py: 0.25,
                            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) },
                            transition: 'all 0.15s',
                          }}
                          onClick={() => {
                            setHiddenCompareRoutes(prev => {
                              const next = new Set(prev);
                              if (next.has(route.id)) {
                                next.delete(route.id);
                              } else {
                                next.add(route.id);
                              }
                              return next;
                            });
                          }}
                        >
                          <Stack direction="row" spacing={0.75} alignItems="center">
                            {isHidden
                              ? <VisibilityOffIcon sx={{ fontSize: 14, color: alpha(routeColor, 0.35) }} />
                              : <VisibilityIcon sx={{ fontSize: 14, color: routeColor }} />}
                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: routeColor, flexShrink: 0, opacity: isHidden ? 0.3 : 1 }} />
                            <Typography variant="caption" sx={{ flex: 1, fontSize: '0.7rem' }}>
                              <strong>{route.entity_id}</strong>
                              {route.is_primary && ' (primary)'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                              {route.usage_count}x • {(route.avg_distance_meters / 1609.34).toFixed(1)}mi
                            </Typography>
                          </Stack>
                          {compareTimeRange && trips.length > 0 && (
                            <Tooltip title={`${trips.length} trips: ${trips[trips.length - 1]?.date || ''} — ${trips[0]?.date || ''}`} placement="bottom" arrow>
                              <Box sx={{ ml: 3.5, mt: 0.25, height: 8, position: 'relative', bgcolor: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)', borderRadius: 1, overflow: 'hidden' }}>
                                {trips.map((t, ti) => {
                                  const ts = new Date(t.timestamp).getTime();
                                  if (isNaN(ts)) return null;
                                  const pct = ((ts - compareTimeRange.min) / compareTimeRange.span) * 100;
                                  return (
                                    <Tooltip key={ti} title={`${t.day} ${t.date} ${t.time}`} placement="top" arrow>
                                      <Box sx={{
                                        position: 'absolute',
                                        left: `${Math.min(Math.max(pct, 1), 99)}%`,
                                        top: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        width: 6, height: 6,
                                        borderRadius: '50%',
                                        bgcolor: routeColor,
                                        border: `1px solid ${isDarkMode ? 'rgba(0,0,0,0.3)' : '#fff'}`,
                                      }} />
                                    </Tooltip>
                                  );
                                })}
                              </Box>
                            </Tooltip>
                          )}
                        </Box>
                      );
                    })}
                  </Stack>
                  {compareTimeRange && (
                    <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5, ml: 3.5, px: 0.25 }}>
                      <Typography variant="caption" sx={{ fontSize: '0.55rem', color: 'text.disabled' }}>
                        {new Date(compareTimeRange.min).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.55rem', color: 'text.disabled' }}>
                        {new Date(compareTimeRange.max).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                      </Typography>
                    </Stack>
                  )}
                </Box>
              )}
              
              {/* PATTERN VISUALIZATION - Show location when pattern is selected */}
              {selectedPattern && selectedPattern.location_coordinates?.lat && selectedPattern.location_coordinates?.lon && 
               !isNaN(selectedPattern.location_coordinates.lat) && !isNaN(selectedPattern.location_coordinates.lon) && (
                <>
                  <Marker
                    longitude={selectedPattern.location_coordinates.lon}
                    latitude={selectedPattern.location_coordinates.lat}
                    anchor="center"
                  >
                    <Box
                      sx={{
                        width: 50,
                        height: 50,
                        borderRadius: '50%',
                        backgroundColor: alpha(theme.palette.success.main, 0.2),
                        border: `3px solid ${theme.palette.success.main}`,
                        animation: 'pulse 2s infinite',
                        '@keyframes pulse': {
                          '0%': { transform: 'scale(0.95)', opacity: 0.7 },
                          '50%': { transform: 'scale(1.05)', opacity: 1 },
                          '100%': { transform: 'scale(0.95)', opacity: 0.7 },
                        }
                      }}
                    />
                  </Marker>
                  <Marker
                    longitude={selectedPattern.location_coordinates.lon}
                    latitude={selectedPattern.location_coordinates.lat}
                    anchor="center"
                  >
                    <Tooltip 
                      title={
                        <Box>
                          <Typography variant="caption" fontWeight="bold">{selectedPattern.location_name}</Typography>
                          <Typography variant="caption" display="block">
                            {selectedPattern.entity_id} • {selectedPattern.frequency}
                          </Typography>
                          <Typography variant="caption" display="block">
                            {selectedPattern.days_of_week?.join(', ')}
                          </Typography>
                        </Box>
                      }
                    >
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: '50%',
                          backgroundColor: theme.palette.success.main,
                          color: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 'bold',
                          fontSize: 14,
                          border: '4px solid #fff',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
                        }}
                      >
                        P
                      </Box>
                    </Tooltip>
                  </Marker>
                  
                  {/* Pattern Info Panel */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 10,
                      left: 10,
                      backgroundColor: alpha(theme.palette.background.paper, 0.95),
                      borderRadius: 2,
                      p: 1.5,
                      maxWidth: 280,
                      boxShadow: theme.shadows[4],
                      zIndex: 100,
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="subtitle2" fontWeight="bold" color="success.main">
                        RECURRING PATTERN
                      </Typography>
                      <Button 
                        size="small" 
                        onClick={() => setSelectedPattern(null)}
                        sx={{ minWidth: 'auto', p: 0.5 }}
                      >
                        ✕
                      </Button>
                    </Stack>
                    <Typography variant="body2" fontWeight="bold">
                      {selectedPattern.location_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {selectedPattern.entity_id}
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="caption" display="block">
                      <strong>Frequency:</strong> {selectedPattern.frequency}
                    </Typography>
                    <Typography variant="caption" display="block">
                      <strong>Days:</strong> {selectedPattern.days_of_week?.join(', ')}
                    </Typography>
                    {selectedPattern.time_window_start && (
                      <Typography variant="caption" display="block">
                        <strong>Time:</strong> {selectedPattern.time_window_start} - {selectedPattern.time_window_end}
                      </Typography>
                    )}
                    <Typography variant="caption" display="block">
                      <strong>Occurrences:</strong> {selectedPattern.occurrence_count}
                    </Typography>
                    <Typography variant="caption" display="block">
                      <strong>Avg Duration:</strong> {selectedPattern.avg_duration_minutes?.toFixed(0) || 0} min
                    </Typography>
                  </Box>
                </>
              )}
              
              {/* ROUTE DEVIATION VISUALIZATION - Origin->Destination line + endpoint markers */}
              {selectedAnomaly?.anomaly_type === 'route_deviation' && routeDeviationLineData && (() => {
                const trip = getAnomalyTripEndpoints(selectedAnomaly);
                if (!trip) return null;
                const sevColor = SEVERITY_COLORS[selectedAnomaly.severity as keyof typeof SEVERITY_COLORS] || SEVERITY_COLORS.medium;
                const matchedRoute = findMatchingRoutePattern(selectedAnomaly);
                return (
                  <>
                    {/* Deviation route line: amber/red dashed to signal "off-pattern" */}
                    <Source id="route-deviation-line" type="geojson" data={routeDeviationLineData}>
                      <Layer
                        id="route-deviation-glow"
                        type="line"
                        paint={{
                          'line-color': sevColor,
                          'line-width': 14,
                          'line-opacity': 0.25,
                          'line-blur': 10,
                        }}
                      />
                      <Layer
                        id="route-deviation-base"
                        type="line"
                        paint={{
                          'line-color': sevColor,
                          'line-width': 4,
                          'line-opacity': 0.95,
                        }}
                      />
                      <Layer
                        id="route-deviation-dashes"
                        type="line"
                        paint={{
                          'line-color': '#ffffff',
                          'line-width': 2,
                          'line-dasharray': [2, 3],
                          'line-opacity': 0.8,
                        }}
                      />
                    </Source>

                    {/* Origin marker (green ▶) */}
                    <Marker longitude={trip.origin.lon} latitude={trip.origin.lat} anchor="center">
                      <Tooltip title={`Origin: ${trip.origin.name || 'Unknown'}`}>
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '50%',
                            backgroundColor: theme.palette.success.main,
                            color: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 'bold',
                            fontSize: 16,
                            border: '4px solid #fff',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
                          }}
                        >
                          {'\u25B6'}
                        </Box>
                      </Tooltip>
                    </Marker>

                    {/* Destination marker (severity-colored ! pulse) */}
                    <Marker longitude={trip.destination.lon} latitude={trip.destination.lat} anchor="center">
                      <Box
                        sx={{
                          width: 50,
                          height: 50,
                          borderRadius: '50%',
                          backgroundColor: alpha(sevColor, 0.2),
                          border: `3px solid ${sevColor}`,
                          animation: 'pulse 2s infinite',
                          '@keyframes pulse': {
                            '0%': { transform: 'scale(0.95)', opacity: 0.7 },
                            '50%': { transform: 'scale(1.1)', opacity: 1 },
                            '100%': { transform: 'scale(0.95)', opacity: 0.7 },
                          },
                        }}
                      />
                    </Marker>
                    <Marker longitude={trip.destination.lon} latitude={trip.destination.lat} anchor="center">
                      <Tooltip
                        title={
                          <Box>
                            <Typography variant="caption" fontWeight="bold">Route Deviation</Typography>
                            <Typography variant="caption" display="block">{selectedAnomaly.entity_id}</Typography>
                            <Typography variant="caption" display="block">
                              {trip.origin.name || 'Origin'} {'\u2192'} {trip.destination.name || 'Destination'}
                            </Typography>
                            {selectedAnomaly.deviation_score != null && (
                              <Typography variant="caption" display="block">
                                Deviation: {(selectedAnomaly.deviation_score * 100).toFixed(0)}%
                              </Typography>
                            )}
                          </Box>
                        }
                      >
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '50%',
                            backgroundColor: sevColor,
                            color: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 'bold',
                            fontSize: 14,
                            border: '4px solid #fff',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
                          }}
                        >
                          !
                        </Box>
                      </Tooltip>
                    </Marker>

                    {/* Side info panel */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 10,
                        left: 10,
                        backgroundColor: alpha(theme.palette.background.paper, 0.95),
                        borderRadius: 2,
                        p: 1.5,
                        maxWidth: 320,
                        boxShadow: theme.shadows[4],
                        zIndex: 100,
                        borderLeft: `4px solid ${sevColor}`,
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ color: sevColor }}>
                          ROUTE DEVIATION
                        </Typography>
                        <Button
                          size="small"
                          onClick={() => setSelectedAnomaly(null)}
                          sx={{ minWidth: 'auto', p: 0.5 }}
                        >
                          {'\u2715'}
                        </Button>
                      </Stack>
                      <Typography variant="body2" fontWeight="bold">
                        {selectedAnomaly.entity_id}
                      </Typography>
                      <Chip
                        label={selectedAnomaly.severity}
                        size="small"
                        sx={{ bgcolor: sevColor, color: 'white', fontSize: '0.6rem', mt: 0.5 }}
                      />
                      <Typography variant="caption" display="block" mt={1} sx={{ fontWeight: 600 }}>
                        {trip.origin.name || 'Origin'} {'\u2192'} {trip.destination.name || 'Destination'}
                      </Typography>
                      {selectedAnomaly.expected_behavior && (
                        <Typography variant="caption" display="block" color="text.secondary" mt={0.5}>
                          <strong>Expected:</strong> {selectedAnomaly.expected_behavior}
                        </Typography>
                      )}
                      {selectedAnomaly.actual_behavior && (
                        <Typography variant="caption" display="block" color="text.secondary" mt={0.5}>
                          <strong>Actual:</strong> {selectedAnomaly.actual_behavior}
                        </Typography>
                      )}
                      <Typography variant="caption" display="block" color="text.secondary" mt={0.5} sx={{ fontStyle: 'italic' }}>
                        {matchedRoute
                          ? `Showing historical route geometry (used ${matchedRoute.usage_count}x)`
                          : 'Showing straight-line trip (no matching baseline route geometry)'}
                      </Typography>
                    </Box>
                  </>
                );
              })()}

              {/* TRIAGE DAY VISUALIZATION — show every plottable anomaly for the
                  focused (entity, day) pair as a clickable severity-tinted dot.
                  Hidden when an individual anomaly is selected (the existing
                  single-anomaly block below takes over), and skipped when there
                  is nothing to plot. */}
              {selectedTriageDay && !selectedAnomaly && triageDayAnomalies.length > 0 && (
                <>
                  {triageDayAnomalies.map((a) => {
                    const sevColor =
                      SEVERITY_COLORS[a.severity as keyof typeof SEVERITY_COLORS] ||
                      theme.palette.error.main;
                    const meta = getAnomalyTypeMeta(a.anomaly_type);
                    return (
                      <Marker
                        key={`triage-day-${a.id}`}
                        longitude={a.mapLon}
                        latitude={a.mapLat}
                        anchor="center"
                      >
                        <Tooltip
                          arrow
                          placement="top"
                          title={
                            <Box>
                              <Typography variant="caption" fontWeight="bold">
                                {meta.label}
                              </Typography>
                              <Typography variant="caption" display="block">
                                {a.entity_id} · {a.severity}
                              </Typography>
                              <Typography variant="caption" display="block">
                                {new Date(a.anomaly_time).toLocaleString()}
                              </Typography>
                              <Typography variant="caption" display="block" sx={{ opacity: 0.8, mt: 0.5 }}>
                                Click to drill into details
                              </Typography>
                            </Box>
                          }
                        >
                          <Box
                            onClick={() => setSelectedAnomaly(a)}
                            sx={{
                              width: 22,
                              height: 22,
                              borderRadius: '50%',
                              backgroundColor: alpha(sevColor, 0.85),
                              border: '2px solid #fff',
                              boxShadow: `0 2px 6px ${alpha(sevColor, 0.6)}`,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#fff',
                              fontWeight: 700,
                              fontSize: 11,
                              lineHeight: 1,
                              transition: 'transform 0.15s',
                              '&:hover': { transform: 'scale(1.15)' },
                            }}
                          >
                            !
                          </Box>
                        </Tooltip>
                      </Marker>
                    );
                  })}

                  {/* Map-area pill indicating a day filter is active, with a quick clear */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 10,
                      left: 10,
                      backgroundColor: alpha(theme.palette.background.paper, 0.95),
                      borderRadius: 2,
                      px: 1.25,
                      py: 0.75,
                      boxShadow: theme.shadows[3],
                      borderLeft: `4px solid ${theme.palette.warning.main}`,
                      zIndex: 100,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      maxWidth: 320,
                    }}
                  >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        sx={{
                          fontSize: '0.55rem',
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                          fontWeight: 700,
                          color: 'warning.main',
                          lineHeight: 1,
                        }}
                      >
                        Day Filter Active
                      </Typography>
                      <Typography
                        noWrap
                        sx={{ fontSize: '0.75rem', fontWeight: 600, mt: 0.25, color: 'text.primary' }}
                      >
                        {selectedTriageDay.entityId} ·{' '}
                        {(() => {
                          try {
                            return new Date(selectedTriageDay.day).toLocaleDateString(undefined, {
                              weekday: 'short', month: 'short', day: 'numeric',
                            });
                          } catch {
                            return selectedTriageDay.day;
                          }
                        })()}
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>
                        {triageDayAnomalies.length} event{triageDayAnomalies.length === 1 ? '' : 's'} ·
                        click marker for details
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => setSelectedTriageDay(null)}
                      sx={{ minWidth: 'auto', px: 0.75, fontSize: '0.65rem' }}
                    >
                      Clear
                    </Button>
                  </Box>
                </>
              )}

              {/* ANOMALY VISUALIZATION - Show location when anomaly is selected */}
              {selectedAnomaly && selectedAnomaly.anomaly_type !== 'route_deviation' && (() => {
                const loc = selectedAnomaly.actual_location || selectedAnomaly.expected_location;
                if (!loc?.lat || !loc?.lon || isNaN(loc.lat) || isNaN(loc.lon)) return null;
                
                return (
                  <>
                    <Marker
                      longitude={loc.lon}
                      latitude={loc.lat}
                      anchor="center"
                    >
                      <Box
                        sx={{
                          width: 50,
                          height: 50,
                          borderRadius: '50%',
                          backgroundColor: alpha(theme.palette.error.main, 0.2),
                          border: `3px solid ${theme.palette.error.main}`,
                          animation: 'pulse 2s infinite',
                          '@keyframes pulse': {
                            '0%': { transform: 'scale(0.95)', opacity: 0.7 },
                            '50%': { transform: 'scale(1.05)', opacity: 1 },
                            '100%': { transform: 'scale(0.95)', opacity: 0.7 },
                          }
                        }}
                      />
                    </Marker>
                    <Marker
                      longitude={loc.lon}
                      latitude={loc.lat}
                      anchor="center"
                    >
                      <Tooltip 
                        title={
                          <Box>
                            <Typography variant="caption" fontWeight="bold">{selectedAnomaly.anomaly_type.replace('_', ' ')}</Typography>
                            <Typography variant="caption" display="block">
                              {selectedAnomaly.entity_id}
                            </Typography>
                            <Typography variant="caption" display="block">
                              {loc.name || 'Location'}
                            </Typography>
                          </Box>
                        }
                      >
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '50%',
                            backgroundColor: theme.palette.error.main,
                            color: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 'bold',
                            fontSize: 14,
                            border: '4px solid #fff',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
                          }}
                        >
                          !
                        </Box>
                      </Tooltip>
                    </Marker>
                    
                    {/* Anomaly Info Panel */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 10,
                        left: 10,
                        backgroundColor: alpha(theme.palette.background.paper, 0.95),
                        borderRadius: 2,
                        p: 1.5,
                        maxWidth: 280,
                        boxShadow: theme.shadows[4],
                        zIndex: 100,
                        borderLeft: `4px solid ${theme.palette.error.main}`,
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="subtitle2" fontWeight="bold" color="error.main">
                          ANOMALY DETECTED
                        </Typography>
                        <Button 
                          size="small" 
                          onClick={() => setSelectedAnomaly(null)}
                          sx={{ minWidth: 'auto', p: 0.5 }}
                        >
                          ✕
                        </Button>
                      </Stack>
                      <Typography variant="body2" fontWeight="bold">
                        {selectedAnomaly.entity_id}
                      </Typography>
                      <Chip 
                        label={selectedAnomaly.severity}
                        size="small"
                        sx={{ 
                          bgcolor: SEVERITY_COLORS[selectedAnomaly.severity as keyof typeof SEVERITY_COLORS],
                          color: 'white',
                          fontSize: '0.6rem',
                          mt: 0.5
                        }}
                      />
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="caption" display="block" fontWeight="bold">
                        {selectedAnomaly.anomaly_type.replace('_', ' ')}
                      </Typography>
                      <Typography variant="caption" display="block" color="text.secondary" mt={0.5}>
                        <strong>Location:</strong> {loc.name || 'Unknown'}
                      </Typography>
                      <Typography variant="caption" display="block" color="text.secondary">
                        <strong>Expected:</strong> {selectedAnomaly.expected_behavior}
                      </Typography>
                      <Typography variant="caption" display="block" color="text.secondary">
                        <strong>Actual:</strong> {selectedAnomaly.actual_behavior}
                      </Typography>
                      <Typography variant="caption" display="block" color="text.secondary" mt={0.5}>
                        {new Date(selectedAnomaly.anomaly_time).toLocaleString()}
                      </Typography>
                    </Box>
                  </>
                );
              })()}
              
              {/* CORRELATION VISUALIZATION - Show shared locations when correlation is selected */}
              {selectedCorrelation && selectedCorrelation.shared_locations && selectedCorrelation.shared_locations.length > 0 && (
                <>
                  {selectedCorrelation.shared_locations.filter((loc: SharedLocationPoint) => loc.lat && loc.lon && !isNaN(loc.lat) && !isNaN(loc.lon)).map((loc: SharedLocationPoint, idx: number) => {
                    // Cap the circle size - use log scale for large counts
                    const circleSize = Math.min(80, 50 + Math.log10(Math.max(1, loc.count || 1)) * 15);
                    
                    return (
                      <Marker
                        key={`corr-loc-${idx}`}
                        longitude={loc.lon}
                        latitude={loc.lat}
                        anchor="center"
                      >
                        <Tooltip 
                          title={
                            <Box>
                              <Typography variant="caption" fontWeight="bold">{loc.name || 'Shared Location'}</Typography>
                              <Typography variant="caption" display="block">
                                {selectedCorrelation.entity_a_id} & {selectedCorrelation.entity_b_id}
                              </Typography>
                              <Typography variant="caption" display="block">
                                Co-located {loc.count || 1}x here
                              </Typography>
                            </Box>
                          }
                        >
                          <Box
                            sx={{
                              width: circleSize,
                              height: circleSize,
                              borderRadius: '50%',
                              backgroundColor: alpha(theme.palette.warning.main, 0.3),
                              border: `4px solid ${theme.palette.warning.main}`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxShadow: '0 4px 20px rgba(255, 152, 0, 0.5)',
                              animation: 'pulse 2s infinite',
                              cursor: 'pointer',
                              '@keyframes pulse': {
                                '0%': { transform: 'scale(0.95)', boxShadow: '0 4px 20px rgba(255, 152, 0, 0.3)' },
                                '50%': { transform: 'scale(1.05)', boxShadow: '0 4px 30px rgba(255, 152, 0, 0.6)' },
                                '100%': { transform: 'scale(0.95)', boxShadow: '0 4px 20px rgba(255, 152, 0, 0.3)' },
                              }
                            }}
                          >
                            <Typography 
                              sx={{ 
                                fontSize: circleSize > 60 ? 16 : 12, 
                                fontWeight: 'bold',
                                color: '#fff',
                                textShadow: '0 1px 2px rgba(0,0,0,0.5)'
                              }}
                            >
                              {loc.count || 1}x
                            </Typography>
                          </Box>
                        </Tooltip>
                      </Marker>
                    );
                  })}
                  
                  {/* Correlation Info Panel */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 10,
                      left: 10,
                      backgroundColor: alpha(theme.palette.background.paper, 0.95),
                      borderRadius: 2,
                      p: 1.5,
                      maxWidth: 280,
                      boxShadow: theme.shadows[4],
                      zIndex: 100,
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="subtitle2" fontWeight="bold" color="warning.main">
                        {selectedCorrelation.correlation_type.replace('_', ' ').toUpperCase()}
                      </Typography>
                      <Button 
                        size="small" 
                        onClick={() => setSelectedCorrelation(null)}
                        sx={{ minWidth: 'auto', p: 0.5 }}
                      >
                        ✕
                      </Button>
                    </Stack>
                    <Typography variant="body2" fontWeight="bold">
                      {selectedCorrelation.entity_a_id} ↔ {selectedCorrelation.entity_b_id}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {selectedCorrelation.occurrence_count} occurrences
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Avg overlap: {selectedCorrelation.avg_overlap_duration_minutes?.toFixed(0) || '?'} min
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="caption" fontWeight="bold" display="block" mb={0.5}>
                      Shared Locations ({selectedCorrelation.shared_locations.length}):
                    </Typography>
                    <Stack spacing={0.25} sx={{ maxHeight: 100, overflow: 'auto', ...compactScrollbar }}>
                      {selectedCorrelation.shared_locations.slice(0, 5).map((loc: SharedLocationPoint, idx: number) => (
                        <Typography key={idx} variant="caption" color="text.secondary">
                          • {loc.name || 'Unknown'} ({loc.count || 1}x)
                        </Typography>
                      ))}
                      {selectedCorrelation.shared_locations.length > 5 && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          +{selectedCorrelation.shared_locations.length - 5} more...
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                </>
              )}
              
            </MapGL>
          </Box>
        </Box>
      </Paper>
        
        {/* Resize Handle */}
        <Box
          onMouseDown={handleResizeStart}
          sx={{
            width: 6,
            cursor: 'ew-resize',
            bgcolor: isResizing ? alpha(theme.palette.primary.main, 0.3) : 'transparent',
            transition: 'background-color 0.15s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            '&:hover': {
              bgcolor: alpha(theme.palette.primary.main, 0.2),
            },
            '&::after': {
              content: '""',
              width: 2,
              height: 40,
              borderRadius: 1,
              bgcolor: isResizing ? theme.palette.primary.main : alpha(theme.palette.divider, 0.5),
              transition: 'all 0.15s',
            },
            '&:hover::after': {
              bgcolor: theme.palette.primary.main,
              height: 60,
            }
          }}
        />

        {/* Right Panel - Tabs */}
        <Paper sx={{ 
          width: rightPanelWidth, 
          minWidth: 320,
          maxWidth: 800,
          display: 'flex', 
          flexDirection: 'column', 
          overflow: 'hidden', 
          flexShrink: 0, 
          bgcolor: 'transparent',
          transition: isResizing ? 'none' : 'width 0.1s ease-out',
        }}>
          <Tabs 
            value={activeTab} 
            onChange={(_e: SyntheticEvent, v: number) => setActiveTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}
          >
            {[
              { label: `Entities${extentFilteredEntities.length ? ` (${extentFilteredEntities.length}${filterByMapExtent && (dashboardData?.entities?.length || 0) !== extentFilteredEntities.length ? `/${dashboardData?.entities?.length || 0}` : ''})` : ''}`, baseLabel: 'Entities', icon: <SocialIcon sx={{ fontSize: 16 }} /> },
              { label: 'Activity', baseLabel: 'Activity', icon: <CalendarIcon sx={{ fontSize: 16 }} /> },
              { label: `Patterns${extentFilteredPatterns.length ? ` (${extentFilteredPatterns.length}${filterByMapExtent && (dashboardData?.top_patterns?.length || 0) !== extentFilteredPatterns.length ? `/${dashboardData?.top_patterns?.length || 0}` : ''})` : ''}`, baseLabel: 'Patterns', icon: <RouteIcon sx={{ fontSize: 16 }} /> },
              { label: `Correlations${extentFilteredCorrelations.length ? ` (${extentFilteredCorrelations.length}${filterByMapExtent && (dashboardData?.top_correlations?.length || 0) !== extentFilteredCorrelations.length ? `/${dashboardData?.top_correlations?.length || 0}` : ''})` : ''}`, baseLabel: 'Correlations', icon: <GroupsIcon sx={{ fontSize: 16 }} /> },
              { label: `Anomalies${extentFilteredAnomalies.length ? ` (${extentFilteredAnomalies.length}${filterByMapExtent && (dashboardData?.recent_anomalies?.length || 0) !== extentFilteredAnomalies.length ? `/${dashboardData?.recent_anomalies?.length || 0}` : ''})` : ''}`, baseLabel: 'Anomalies', icon: <AlertIcon sx={{ fontSize: 16 }} /> },
              { label: `Outlier Rankings${dashboardData?.top_atypical_days?.length ? ` (${dashboardData.top_atypical_days.length})` : ''}`, baseLabel: 'Outlier Rankings', icon: <TrendingUpIcon sx={{ fontSize: 16 }} /> },
              { label: 'Forecast', baseLabel: 'Forecast', icon: <InsightsIcon sx={{ fontSize: 16 }} /> },
              { label: `Routes${extentFilteredRoutes.length ? ` (${extentFilteredRoutes.length}${filterByMapExtent && routePatterns.length !== extentFilteredRoutes.length ? `/${routePatterns.length}` : ''})` : ''}`, baseLabel: 'Routes', icon: <TimelineIcon sx={{ fontSize: 16 }} /> },
              { label: `Dwells${extentFilteredDwells.length ? ` (${extentFilteredDwells.length}${filterByMapExtent && dwellTabData.length !== extentFilteredDwells.length ? `/${dwellTabData.length}` : ''})` : ''}`, baseLabel: 'Dwells', icon: <PlaceIcon sx={{ fontSize: 16 }} /> },
              { label: 'Digital', baseLabel: 'Digital', icon: <DigitalIcon sx={{ fontSize: 16 }} /> },
            ].filter((tab) => polTabAllowed(tab.baseLabel)).map((tab, idx) => (
              <Tab
                key={tab.baseLabel}
                value={['Entities','Activity','Patterns','Correlations','Anomalies','Outlier Rankings','Forecast','Routes','Dwells','Digital'].indexOf(tab.baseLabel)}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {tab.label}
                    <InfoIcon
                      sx={{ fontSize: 13, color: 'text.disabled', opacity: 0.6, '&:hover': { opacity: 1, color: 'primary.main' } }}
                      onClick={(e: MouseEvent) => {
                        e.stopPropagation();
                        setTabInfoOpen(['Entities','Activity','Patterns','Correlations','Anomalies','Outlier Rankings','Forecast','Routes','Dwells','Digital'].indexOf(tab.baseLabel));
                      }}
                    />
                  </Box>
                }
                icon={tab.icon}
                iconPosition="start"
                sx={{ minHeight: 48 }}
              />
            ))}
          </Tabs>
          
          <Box sx={{ flex: 1, overflow: 'auto', p: 1, ...sleekScrollbar }}>
            {/* Entities Tab */}
            <TabPanel value={activeTab} index={POL_TAB.ENTITIES}>
              {/* AI-Powered Similar Entities Panel - Shows when entity is selected */}
              {selectedEntity && selectedCourse && (
                <Stack spacing={2} sx={{ mb: 2 }}>
                  <SimilarEntitiesPanel
                    selectedEntityId={selectedEntity}
                    courseId={selectedCourse}
                    onEntitySelect={(entityId) => setSelectedEntity(entityId)}
                    onCompareEntities={(entityA, entityB) => {
                      // Could open a comparison dialog
                      console.log(`Compare ${entityA} with ${entityB}`);
                    }}
                    maxResults={8}
                    minSimilarity={0.35}
                    showAnomalyInfo={true}
                    title="Similar Behavioral Profiles"
                  />
                  <AnomalyDetectionPanel
                    selectedEntityId={selectedEntity}
                    courseId={selectedCourse}
                    compact={true}
                    daysToShow={daysBack}
                    detectorEvents={selectedEntityDetectorEvents}
                  />
                </Stack>
              )}
              
              {filterByMapExtent && (
                <Alert severity="info" sx={{ mb: 1, py: 0 }}>
                  Cropped to map view — {extentFilteredEntities.length} of {dashboardData?.entities?.length || 0} entities
                </Alert>
              )}
              <Grid container spacing={1}>
                {extentFilteredEntities.map(entity => (
                  <Grid item xs={12} key={entity.entity_id}>
                    {renderEntityCard(entity)}
                  </Grid>
                ))}
              </Grid>
              {extentFilteredEntities.length === 0 && (
                <Box textAlign="center" py={4}>
                  <Typography color="text.secondary">
                    {filterByMapExtent ? 'No entities in the current map view' : 'No entity data available'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {filterByMapExtent
                      ? 'Pan/zoom the map or turn off map-extent crop'
                      : 'Run PoL processing or adjust filters'}
                  </Typography>
                </Box>
              )}
            </TabPanel>
            
            {/* Patterns Tab */}
            <TabPanel value={activeTab} index={POL_TAB.PATTERNS}>
              {filterByMapExtent && (
                <Alert severity="info" sx={{ mb: 1, py: 0 }}>
                  Cropped to map view — {extentFilteredPatterns.length} of {dashboardData?.top_patterns?.length || 0} patterns
                </Alert>
              )}
              {extentFilteredPatterns.map(pattern => renderPatternCard(pattern))}
              {extentFilteredPatterns.length === 0 && (
                <Box textAlign="center" py={4}>
                  <Typography color="text.secondary">
                    {filterByMapExtent ? 'No patterns in the current map view' : 'No patterns detected'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {filterByMapExtent
                      ? 'Pan/zoom the map or turn off map-extent crop'
                      : 'Patterns are detected when entities show recurring behaviors'}
                  </Typography>
                </Box>
              )}
            </TabPanel>
            
            {/* Correlations Tab */}
            <TabPanel value={activeTab} index={POL_TAB.CORRELATIONS}>
              {filterByMapExtent && (
                <Alert severity="info" sx={{ mb: 1, py: 0 }}>
                  Cropped to map view — {extentFilteredCorrelations.length} of {dashboardData?.top_correlations?.length || 0} correlations
                </Alert>
              )}
              {extentFilteredCorrelations.map(corr => renderCorrelationCard(corr))}
              {extentFilteredCorrelations.length === 0 && (
                <Box textAlign="center" py={4}>
                  <Typography color="text.secondary">
                    {filterByMapExtent ? 'No correlations in the current map view' : 'No correlations detected'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {filterByMapExtent
                      ? 'Pan/zoom the map or turn off map-extent crop'
                      : 'Correlations show entities that interact (co-location, co-travel)'}
                  </Typography>
                </Box>
              )}
            </TabPanel>
            
            {/* Anomalies Tab */}
            <TabPanel value={activeTab} index={POL_TAB.ANOMALIES}>
              {/* AI-Powered Anomaly Activity Panel
                  Pass Layer-2 detector events for the selected entity so the panel
                  can render a combined, honest dual-layer view (ML autoencoder days
                  + per-event statistical detectors). Without this prop the panel
                  would only show Layer-1 ML output and look misleadingly small. */}
              {selectedEntity && selectedCourse && (
                  <Box sx={{ mb: 2 }}>
                    <AnomalyDetectionPanel
                      selectedEntityId={selectedEntity}
                      courseId={selectedCourse}
                      onDaySelect={(date) => {
                        console.log('Selected anomalous day:', date);
                      }}
                      compact={false}
                      daysToShow={daysBack}
                      detectorEvents={selectedEntityDetectorEvents}
                    />
                  </Box>
              )}

              {(() => {
                const allAnomalies = extentFilteredAnomalies;
                if (allAnomalies.length === 0) {
                  return (
                    <Box textAlign="center" py={4}>
                      <Typography color="text.secondary">
                        {filterByMapExtent ? 'No anomalies in the current map view' : 'No anomalies detected'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {filterByMapExtent
                          ? 'Pan/zoom the map or turn off map-extent crop. Anomalies without coordinates are hidden while crop is on.'
                          : 'Anomalies appear when entities break their routine patterns. See the Outlier Rankings tab for the daily atypicality ranking.'}
                      </Typography>
                    </Box>
                  );
                }

                // Tally counts per anomaly type
                const typeCounts = new Map<string, number>();
                allAnomalies.forEach((a: PolAnomaly) => {
                  const key = (a.anomaly_type || 'unknown').toLowerCase();
                  typeCounts.set(key, (typeCounts.get(key) || 0) + 1);
                });

                // Build the indicator list: always include CORE_ANOMALY_TYPES (even at zero
                // count so analysts see the full detector menu), then append any extra types
                // that appear in live data but aren't in the core list.
                const coreSet = new Set(CORE_ANOMALY_TYPES);
                const extraTypes = Array.from(typeCounts.keys())
                  .filter((t) => !coreSet.has(t))
                  .sort();
                const indicatorList: Array<{ type: string; count: number; meta: AnomalyTypeMeta; shortLabel: string }> =
                  [...CORE_ANOMALY_TYPES, ...extraTypes].map((type) => ({
                    type,
                    count: typeCounts.get(type) || 0,
                    meta: getAnomalyTypeMeta(type),
                    shortLabel:
                      ANOMALY_TYPE_SHORT_LABEL[type] ||
                      getAnomalyTypeMeta(type).label.split(' ').slice(0, 2).join(' '),
                  }));

                const noFilter = !selectedAnomalyType;
                const matchesFilter = (a: PolAnomaly) =>
                  noFilter || (a.anomaly_type || 'unknown').toLowerCase() === selectedAnomalyType;

                const toggleType = (type: string) => {
                  setSelectedAnomalyType((prev) => (prev === type ? null : type));
                };

                const filtered = allAnomalies.filter(matchesFilter);
                const mlAnomalies = [...filtered]
                  .filter((a: PolAnomaly) => a.anomaly_type?.startsWith('ml_'))
                  .sort((a: PolAnomaly, b: PolAnomaly) => new Date(b.anomaly_time).getTime() - new Date(a.anomaly_time).getTime());
                const ruleAnomalies = [...filtered]
                  .filter((a: PolAnomaly) => !a.anomaly_type?.startsWith('ml_'))
                  .sort((a: PolAnomaly, b: PolAnomaly) => new Date(b.anomaly_time).getTime() - new Date(a.anomaly_time).getTime());

                return (
                  <>
                    {/* Anomaly type indicators - KPI-box style, single-select */}
                    <Box sx={{ mb: 1.5 }}>
                      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.75 }}>
                        <FilterIcon sx={{ fontSize: 13, color: 'text.secondary', opacity: 0.7 }} />
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: '0.6rem',
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                            fontWeight: 600,
                            color: 'text.secondary',
                          }}
                        >
                          Filter by type
                        </Typography>
                        <Tooltip
                          title="Click any indicator to show only that type. Click again to clear. Single-select."
                          placement="top"
                          arrow
                        >
                          <InfoIcon sx={{ fontSize: 11, color: 'text.disabled', opacity: 0.6 }} />
                        </Tooltip>
                        {selectedAnomalyType && (
                          <Chip
                            label="Clear"
                            size="small"
                            onClick={() => setSelectedAnomalyType(null)}
                            sx={{ height: 18, fontSize: '0.6rem', ml: 'auto' }}
                          />
                        )}
                      </Stack>
                      {/* Tightened grid: minmax(60px) + 4px gap lets the 9 core detector
                          chips fit on one row down to ~590px of available width, which
                          covers every right-panel size we've seen so far. Falls back to
                          two rows on very narrow panels without breaking layout. */}
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(60px, 1fr))',
                          gap: 0.5,
                        }}
                      >
                        {indicatorList.map(({ type, count, meta, shortLabel }) => {
                          const isSelected = selectedAnomalyType === type;
                          const isInactive = count === 0;
                          const Icon = meta.icon;
                          return (
                            <Tooltip
                              key={type}
                              title={
                                isInactive
                                  ? `${meta.label} - no events in current data`
                                  : `${meta.label} - ${count} ${count === 1 ? 'event' : 'events'} - click to ${isSelected ? 'clear filter' : 'filter to this type only'}`
                              }
                              placement="top"
                              arrow
                            >
                              <Box
                                onClick={() => !isInactive && toggleType(type)}
                                sx={{
                                  py: 0.5,
                                  px: 0.25,
                                  borderRadius: 1,
                                  textAlign: 'center',
                                  cursor: isInactive ? 'default' : 'pointer',
                                  bgcolor: isSelected
                                    ? alpha(meta.color, 0.12)
                                    : (isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)'),
                                  border: '1px solid',
                                  borderColor: isSelected ? meta.color : 'divider',
                                  opacity: isInactive ? 0.4 : 1,
                                  position: 'relative',
                                  minHeight: 50,
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  transition: 'all 0.15s',
                                  '&:hover': isInactive
                                    ? {}
                                    : {
                                        borderColor: meta.color,
                                        bgcolor: alpha(meta.color, isSelected ? 0.18 : 0.06),
                                      },
                                  '&:hover .filter-hint-icon': { opacity: 0.9 },
                                }}
                              >
                                {/* Filter-hint icon — shown only on hover/active so it doesn't
                                    steal pixels from the label in the tighter cell. */}
                                <Box
                                  className="filter-hint-icon"
                                  sx={{
                                    position: 'absolute',
                                    top: 2,
                                    left: 2,
                                    opacity: isSelected ? 0.9 : 0,
                                    transition: 'opacity 0.15s',
                                    color: isSelected ? meta.color : 'text.disabled',
                                    lineHeight: 0,
                                  }}
                                >
                                  <FilterIcon sx={{ fontSize: 8 }} />
                                </Box>
                                <Icon
                                  sx={{
                                    fontSize: 13,
                                    color: isSelected || !isInactive ? meta.color : 'text.disabled',
                                    mb: 0.1,
                                  }}
                                />
                                <Typography
                                  sx={{
                                    fontSize: '0.48rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.2,
                                    fontWeight: 600,
                                    color: isSelected ? meta.color : 'text.secondary',
                                    lineHeight: 1,
                                    mb: 0.2,
                                    px: 0,
                                    width: '100%',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  }}
                                >
                                  {shortLabel}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: '0.8rem',
                                    fontWeight: 700,
                                    color: isSelected
                                      ? meta.color
                                      : (isInactive ? 'text.disabled' : 'text.primary'),
                                    lineHeight: 1,
                                  }}
                                >
                                  {count > 0 ? count : '-'}
                                </Typography>
                              </Box>
                            </Tooltip>
                          );
                        })}
                      </Box>
                    </Box>

                    {/* ML-detected anomalies (from embedding models) */}
                    {mlAnomalies.length > 0 && (
                      <>
                        <Typography variant="subtitle2" sx={{ mb: 1, mt: selectedEntity ? 2 : 0, opacity: 0.8, color: 'info.main' }}>
                          AI-Detected Anomalies
                        </Typography>
                        {mlAnomalies.map((anomaly: PolAnomaly) => renderAnomalyCard(anomaly))}
                      </>
                    )}

                    {/* Rule-based anomalies (from pattern analysis) */}
                    {ruleAnomalies.length > 0 && (
                      <Typography variant="subtitle2" sx={{ mb: 1, mt: 1, opacity: 0.8 }}>
                        Pattern-Based Anomalies
                      </Typography>
                    )}
                    {ruleAnomalies.map((anomaly: PolAnomaly) => renderAnomalyCard(anomaly))}

                    {/* Filter resulted in zero matches */}
                    {filtered.length === 0 && (
                      <Box textAlign="center" py={4}>
                        <Typography color="text.secondary">No anomalies match the active filter</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Click an indicator above to remove it, or clear the filter
                        </Typography>
                      </Box>
                    )}
                  </>
                );
              })()}
            </TabPanel>

            {/* Triage Tab — Top Atypical Days (Step 12 daily rollup) */}
            <TabPanel value={activeTab} index={POL_TAB.OUTLIER_RANKINGS}>
              {(() => {
                const atypicalDays = dashboardData?.top_atypical_days ?? [];

                const scoreColor = (s: number) => {
                  if (s >= 0.7) return theme.palette.error.main;
                  if (s >= 0.5) return theme.palette.warning.main;
                  if (s >= 0.3) return theme.palette.info.main;
                  return theme.palette.success.main;
                };
                const scoreLabel = (s: number) => {
                  if (s >= 0.7) return 'High';
                  if (s >= 0.5) return 'Elevated';
                  if (s >= 0.3) return 'Moderate';
                  return 'Low';
                };
                const formatDay = (iso: string) => {
                  try {
                    return new Date(iso).toLocaleDateString(undefined, {
                      weekday: 'short', month: 'short', day: 'numeric',
                    });
                  } catch { return iso; }
                };

                // Severity-band tally for the summary header
                const bandCounts = atypicalDays.reduce(
                  (acc, d) => {
                    if (d.atypicality_score >= 0.7) acc.high++;
                    else if (d.atypicality_score >= 0.5) acc.elevated++;
                    else if (d.atypicality_score >= 0.3) acc.moderate++;
                    else acc.low++;
                    return acc;
                  },
                  { high: 0, elevated: 0, moderate: 0, low: 0 }
                );

                if (atypicalDays.length === 0) {
                  return (
                    <Box textAlign="center" py={4}>
                      <Typography color="text.secondary">No atypical days computed</Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1, maxWidth: 360, mx: 'auto' }}>
                        The pipeline computes a daily atypicality_score per entity once enough
                        baseline data exists. If you just ingested data, run the embedding
                        pipeline (Step 12 - Daily Activity Metrics) and check back.
                      </Typography>
                    </Box>
                  );
                }

                return (
                  <>
                    {/* Header explainer + export-brief action */}
                    <Box sx={{
                      mb: 1.5,
                      p: 1,
                      borderRadius: 1,
                      bgcolor: alpha(theme.palette.warning.main, 0.06),
                      border: '1px solid',
                      borderColor: alpha(theme.palette.warning.main, 0.25),
                    }}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 0.5 }}>
                        <Stack direction="row" alignItems="center" spacing={0.75}>
                          <TrendingUpIcon sx={{ fontSize: 14, color: 'warning.main' }} />
                          <Typography variant="caption" sx={{
                            fontSize: '0.6rem',
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                            fontWeight: 700,
                            color: 'warning.main',
                          }}>
                            Daily Atypicality · Outlier Rankings
                          </Typography>
                        </Stack>
                        <Tooltip
                          title={
                            !selectedEntity
                              ? 'Select an entity in the top filter to enable the Outlier Rankings brief'
                              : briefExporting
                                ? 'Generating brief...'
                                : `Export a focused PowerPoint brief for ${selectedEntity} with AI analysis of their atypical days`
                          }
                          placement="left"
                          arrow
                        >
                          <span>
                            <Button
                              size="small"
                              variant="outlined"
                              color="warning"
                              startIcon={
                                briefExporting
                                  ? <CircularProgress size={12} color="inherit" />
                                  : <SlideshowIcon sx={{ fontSize: '0.9rem' }} />
                              }
                              disabled={!selectedEntity || briefExporting}
                              onClick={handleTriageBriefExport}
                              sx={{
                                fontSize: '0.6rem',
                                textTransform: 'uppercase',
                                letterSpacing: 0.5,
                                fontWeight: 700,
                                py: 0.25,
                                px: 1,
                                minHeight: 0,
                                lineHeight: 1.2,
                              }}
                            >
                              {briefExporting ? 'Building...' : 'Export Brief'}
                            </Button>
                          </span>
                        </Tooltip>
                      </Stack>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', lineHeight: 1.4, display: 'block' }}>
                        Per-entity-per-day rankings ordered by composite <strong>atypicality_score</strong> (mobility deviation + anomaly density). Click a card to focus that entity. Use the Anomalies tab for the underlying events.
                      </Typography>
                    </Box>

                    {/* Severity-band KPI strip (matches Anomalies indicator visual style) */}
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(4, 1fr)',
                        gap: 0.5,
                        mb: 1.5,
                      }}
                    >
                      {([
                        { label: 'High', value: bandCounts.high, color: theme.palette.error.main, hint: 'Score >= 0.70' },
                        { label: 'Elevated', value: bandCounts.elevated, color: theme.palette.warning.main, hint: 'Score 0.50-0.69' },
                        { label: 'Moderate', value: bandCounts.moderate, color: theme.palette.info.main, hint: 'Score 0.30-0.49' },
                        { label: 'Low', value: bandCounts.low, color: theme.palette.success.main, hint: 'Score < 0.30' },
                      ]).map((b) => (
                        <Tooltip key={b.label} title={`${b.label} severity band - ${b.hint}`} placement="top" arrow>
                          <Box
                            sx={{
                              py: 0.75,
                              px: 0.5,
                              borderRadius: 1,
                              textAlign: 'center',
                              bgcolor: isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
                              border: '1px solid',
                              borderColor: 'divider',
                              opacity: b.value === 0 ? 0.5 : 1,
                            }}
                          >
                            <Typography sx={{
                              fontSize: '0.5rem',
                              textTransform: 'uppercase',
                              letterSpacing: 0.5,
                              fontWeight: 600,
                              color: b.color,
                              lineHeight: 1,
                              mb: 0.25,
                            }}>
                              {b.label}
                            </Typography>
                            <Typography sx={{
                              fontSize: '1rem',
                              fontWeight: 700,
                              color: b.value === 0 ? 'text.disabled' : 'text.primary',
                              lineHeight: 1,
                            }}>
                              {b.value}
                            </Typography>
                          </Box>
                        </Tooltip>
                      ))}
                    </Box>

                    {/* Atypical day cards */}
                    <Stack spacing={0.75}>
                      {atypicalDays.map((d, idx) => {
                        const c = scoreColor(d.atypicality_score);
                        const isSelectedEntity = selectedEntity === d.entity_id;
                        const isActiveTriageDay =
                          selectedTriageDay?.entityId === d.entity_id &&
                          isoToLocalDayKey(selectedTriageDay.day) === isoToLocalDayKey(d.day);
                        const anomalyChips: Array<[string, number]> = (
                          Object.entries((d.anomaly_breakdown ?? {}) as Record<string, unknown>)
                            .map(([type, count]) => [type, Number(count) || 0] as [string, number])
                            .filter((entry) => entry[1] > 0)
                            .sort((a, b) => b[1] - a[1])
                        );
                        return (
                          <Card
                            key={`${d.entity_id}-${d.day}-${idx}`}
                            onClick={() => {
                              // Toggle day focus: clicking the active card clears,
                              // clicking another card switches both entity and day.
                              if (isActiveTriageDay) {
                                setSelectedTriageDay(null);
                                setSelectedEntity(null);
                                setSelectedAnomaly(null);
                              } else {
                                setSelectedEntity(d.entity_id);
                                setSelectedTriageDay({ entityId: d.entity_id, day: d.day });
                                setSelectedAnomaly(null);
                              }
                            }}
                            sx={{
                              borderLeft: `4px solid ${c}`,
                              cursor: 'pointer',
                              transition: 'all 0.15s',
                              border: isActiveTriageDay
                                ? `2px solid ${c}`
                                : isSelectedEntity
                                ? `1px solid ${alpha(c, 0.6)}`
                                : undefined,
                              backgroundColor: isActiveTriageDay
                                ? alpha(c, 0.12)
                                : isSelectedEntity
                                ? alpha(c, 0.05)
                                : undefined,
                              boxShadow: isActiveTriageDay ? theme.shadows[4] : undefined,
                              '&:hover': { boxShadow: theme.shadows[3], transform: 'translateY(-1px)' },
                            }}
                          >
                            <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                              <Stack direction="row" spacing={1} alignItems="center">
                                {/* Score badge */}
                                <Box
                                  sx={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: '50%',
                                    bgcolor: alpha(c, 0.15),
                                    border: `2px solid ${c}`,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                  }}
                                >
                                  <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: c, lineHeight: 1 }}>
                                    {(d.atypicality_score).toFixed(2)}
                                  </Typography>
                                  <Typography sx={{
                                    fontSize: '0.5rem',
                                    color: c,
                                    lineHeight: 1,
                                    mt: 0.25,
                                    textTransform: 'uppercase',
                                    fontWeight: 600,
                                  }}>
                                    {scoreLabel(d.atypicality_score)}
                                  </Typography>
                                </Box>

                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.25 }}>
                                    <Typography variant="subtitle2" fontWeight={700} noWrap sx={{ fontSize: '0.8rem' }}>
                                      {d.entity_id}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                      {formatDay(d.day)}
                                    </Typography>
                                  </Stack>
                                  <Stack direction="row" spacing={1} sx={{ mb: 0.5 }} flexWrap="wrap">
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                      <strong>{d.distance_traveled_km.toFixed(1)}</strong> km
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                      <strong>{d.dwell_count}</strong> dwell{d.dwell_count === 1 ? '' : 's'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                      <strong>{d.unique_places_visited}</strong> place{d.unique_places_visited === 1 ? '' : 's'}
                                    </Typography>
                                    {d.total_anomaly_count > 0 && (
                                      <Typography variant="caption" sx={{ fontSize: '0.65rem', color: c, fontWeight: 600 }}>
                                        <strong>{d.total_anomaly_count}</strong> alert{d.total_anomaly_count === 1 ? '' : 's'}
                                      </Typography>
                                    )}
                                  </Stack>
                                  {anomalyChips.length > 0 ? (
                                    <Stack direction="row" flexWrap="wrap" gap={0.4}>
                                      {anomalyChips.map((entry) => {
                                        const type: string = entry[0];
                                        const count: number = entry[1];
                                        const meta = getAnomalyTypeMeta(type);
                                        const Icon = meta.icon;
                                        return (
                                          <Tooltip key={type} title={`${meta.label}: ${count}`} placement="top" arrow>
                                            <Chip
                                              size="small"
                                              icon={<Icon sx={{ fontSize: 10, color: meta.color }} />}
                                              label={String(count)}
                                              sx={{
                                                height: 16,
                                                fontSize: '0.6rem',
                                                fontWeight: 700,
                                                borderRadius: 1,
                                                border: `1px solid ${alpha(meta.color, 0.4)}`,
                                                bgcolor: alpha(meta.color, 0.1),
                                                color: meta.color,
                                                '& .MuiChip-icon': { ml: 0.4, mr: -0.25, color: 'inherit' },
                                                '& .MuiChip-label': { px: 0.5 },
                                              }}
                                            />
                                          </Tooltip>
                                        );
                                      })}
                                    </Stack>
                                  ) : (
                                    <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.6rem', fontStyle: 'italic' }}>
                                      Mobility-driven (no detector hits)
                                    </Typography>
                                  )}
                                </Box>
                              </Stack>
                            </CardContent>
                            {/* Day-focus drill-down: expanded list of the underlying anomalies for
                                this (entity, day), each clickable to drive the existing
                                selectedAnomaly map view. Stops click propagation so row clicks
                                don't collapse the card. */}
                            {isActiveTriageDay && (
                              <Box
                                onClick={(e) => e.stopPropagation()}
                                sx={{
                                  borderTop: '1px solid',
                                  borderColor: 'divider',
                                  bgcolor: alpha(c, 0.04),
                                  px: 1.25,
                                  py: 1,
                                }}
                              >
                                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                                  <Typography
                                    sx={{
                                      fontSize: '0.6rem',
                                      textTransform: 'uppercase',
                                      letterSpacing: 0.6,
                                      fontWeight: 700,
                                      color: c,
                                    }}
                                  >
                                    Day Detail · {formatDay(d.day)}
                                  </Typography>
                                  <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>
                                    Map filtered to this day · {triageDayAnomalies.length} plottable event{triageDayAnomalies.length === 1 ? '' : 's'}
                                  </Typography>
                                </Stack>
                                {triageDayAnomalies.length === 0 ? (
                                  <Typography
                                    variant="caption"
                                    color="text.disabled"
                                    sx={{ fontSize: '0.65rem', fontStyle: 'italic' }}
                                  >
                                    No mappable anomaly events recorded — this day's score was driven by
                                    mobility deviation (distance / places / dwells) rather than discrete detectors.
                                  </Typography>
                                ) : (
                                  <Stack spacing={0.4}>
                                    {triageDayAnomalies
                                      .slice()
                                      .sort((a, b) =>
                                        new Date(a.anomaly_time).getTime() - new Date(b.anomaly_time).getTime()
                                      )
                                      .map((a) => {
                                        const meta = getAnomalyTypeMeta(a.anomaly_type);
                                        const Icon = meta.icon;
                                        const sevColor = SEVERITY_COLORS[a.severity as keyof typeof SEVERITY_COLORS] || meta.color;
                                        const isSelectedRow = selectedAnomaly?.id === a.id;
                                        const timeStr = (() => {
                                          try {
                                            return new Date(a.anomaly_time).toLocaleTimeString(undefined, {
                                              hour: 'numeric', minute: '2-digit',
                                            });
                                          } catch { return ''; }
                                        })();
                                        const locName =
                                          a.actual_location?.name ||
                                          a.expected_location?.name ||
                                          getAnomalyTripEndpoints(a)?.destination?.name ||
                                          '';
                                        return (
                                          <Box
                                            key={a.id}
                                            onClick={() => {
                                              setSelectedAnomaly(isSelectedRow ? null : a);
                                            }}
                                            sx={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: 0.75,
                                              px: 0.75,
                                              py: 0.5,
                                              borderRadius: 1,
                                              cursor: 'pointer',
                                              border: '1px solid',
                                              borderColor: isSelectedRow ? sevColor : 'transparent',
                                              bgcolor: isSelectedRow
                                                ? alpha(sevColor, 0.12)
                                                : alpha(theme.palette.background.paper, 0.6),
                                              transition: 'background-color 0.15s, border-color 0.15s',
                                              '&:hover': {
                                                bgcolor: alpha(sevColor, 0.08),
                                                borderColor: alpha(sevColor, 0.5),
                                              },
                                            }}
                                          >
                                            <Box
                                              sx={{
                                                width: 6,
                                                height: 6,
                                                borderRadius: '50%',
                                                bgcolor: sevColor,
                                                flexShrink: 0,
                                              }}
                                            />
                                            <Icon sx={{ fontSize: 12, color: meta.color, flexShrink: 0 }} />
                                            <Typography
                                              sx={{
                                                fontSize: '0.65rem',
                                                fontWeight: 600,
                                                color: 'text.primary',
                                                flexShrink: 0,
                                              }}
                                            >
                                              {meta.label}
                                            </Typography>
                                            {timeStr && (
                                              <Typography
                                                sx={{ fontSize: '0.6rem', color: 'text.secondary', flexShrink: 0 }}
                                              >
                                                {timeStr}
                                              </Typography>
                                            )}
                                            {locName && (
                                              <Typography
                                                noWrap
                                                sx={{
                                                  fontSize: '0.6rem',
                                                  color: 'text.disabled',
                                                  flex: 1,
                                                  minWidth: 0,
                                                }}
                                              >
                                                · {locName}
                                              </Typography>
                                            )}
                                            <Chip
                                              size="small"
                                              label={a.severity}
                                              sx={{
                                                height: 14,
                                                fontSize: '0.55rem',
                                                fontWeight: 700,
                                                bgcolor: alpha(sevColor, 0.18),
                                                color: sevColor,
                                                border: `1px solid ${alpha(sevColor, 0.5)}`,
                                                textTransform: 'uppercase',
                                                '& .MuiChip-label': { px: 0.5 },
                                              }}
                                            />
                                          </Box>
                                        );
                                      })}
                                  </Stack>
                                )}
                              </Box>
                            )}
                          </Card>
                        );
                      })}
                    </Stack>
                  </>
                );
              })()}
            </TabPanel>

            {/* Forecast Tab (Phase 1: Expected Day) */}
            <TabPanel value={activeTab} index={POL_TAB.FORECAST}>
              <ForecastTab
                selectedEntityId={selectedEntity}
                courseId={selectedCourse || null}
                courseName={selectedCourse || undefined}
              />
            </TabPanel>

            {/* Routes Tab (Rich GPS Route Patterns) */}
            <TabPanel value={activeTab} index={POL_TAB.ROUTES}>
              {/* Route filter controls */}
              <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                <ToggleButtonGroup
                  value={routeRepeatFilter}
                  exclusive
                  onChange={(_e, val) => { if (val) setRouteRepeatFilter(val); }}
                  size="small"
                  sx={{ '& .MuiToggleButton-root': { py: 0.25, px: 1.5, fontSize: '0.7rem', textTransform: 'none' } }}
                >
                  <ToggleButton value="repeated">Repeated (2+)</ToggleButton>
                  <ToggleButton value="single">One-time Only</ToggleButton>
                  <ToggleButton value="all">All Routes</ToggleButton>
                </ToggleButtonGroup>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                  {routePatterns.length} route{routePatterns.length !== 1 ? 's' : ''}
                </Typography>
              </Box>

              {routePatternsLoading && (
                <Box display="flex" justifyContent="center" py={2}>
                  <CircularProgress size={24} />
                </Box>
              )}
              {selectedRoutePattern && (
                <Alert 
                  severity="info" 
                  sx={{ mb: 1 }}
                  action={
                    <Button size="small" onClick={() => setSelectedRoutePattern(null)}>
                      Clear
                    </Button>
                  }
                >
                  Showing GPS route: {selectedRoutePattern.route_name}
                </Alert>
              )}
              {filterByMapExtent && (
                <Alert severity="info" sx={{ mb: 1, py: 0 }}>
                  Cropped to map view — {extentFilteredRoutes.length} of {routePatterns.length} routes
                </Alert>
              )}
              {(routeRepeatFilter === 'single'
                ? extentFilteredRoutes.filter((r: RoutePattern) => r.usage_count === 1)
                : extentFilteredRoutes
              ).filter((r: RoutePattern) => !(dmFilter === 'exclude' && isRouteInExclusionZone(r, dmPolygonRings)))
              .map((route: RoutePattern) => (
                <Card 
                  key={route.id} 
                  sx={{ 
                    mb: 1,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    border: selectedRoutePattern?.id === route.id ? `2px solid ${theme.palette.primary.main}` : '1px solid transparent',
                    backgroundColor: selectedRoutePattern?.id === route.id ? alpha(theme.palette.primary.main, 0.08) : undefined,
                    '&:hover': {
                      boxShadow: theme.shadows[4],
                      transform: 'translateY(-2px)',
                      borderColor: theme.palette.primary.light,
                    }
                  }}
                  onClick={() => {
                    if (selectedRoutePattern?.id === route.id) {
                      setSelectedRoutePattern(null);
                      return;
                    }
                    setSelectedRoutePattern(route);
                    const pathCoords = normalizeRouteGeometry(route.route_geometry, route.origin);
                    const map = mapRef.current?.getMap?.();
                    if (pathCoords.length >= 2 && map) {
                      const lons = pathCoords.map((c) => c[0]);
                      const lats = pathCoords.map((c) => c[1]);
                      const pad = 0.002;
                      try {
                        map.fitBounds(
                          [
                            [Math.min(...lons) - pad, Math.min(...lats) - pad],
                            [Math.max(...lons) + pad, Math.max(...lats) + pad],
                          ],
                          { padding: { top: 80, bottom: 80, left: 80, right: 80 }, duration: 800, maxZoom: 15 },
                        );
                        return;
                      } catch {
                        // fall through to center zoom
                      }
                    }
                    if (route.origin.lat != null && route.destination.lat != null &&
                        route.origin.lon != null && route.destination.lon != null) {
                      const centerLat = (route.origin.lat + route.destination.lat) / 2;
                      const centerLon = (route.origin.lon + route.destination.lon) / 2;
                      setViewport({
                        latitude: centerLat,
                        longitude: centerLon,
                        zoom: 13,
                      });
                    }
                  }}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {route.route_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {route.entity_id}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        {selectedRoutePattern?.id === route.id && (
                          <Chip 
                            label="On Map"
                            size="small"
                            color="success"
                            variant="filled"
                            sx={{ fontSize: '0.65rem' }}
                          />
                        )}
                        <Chip 
                          label={`${route.usage_count}x`}
                          size="small"
                          sx={{ 
                            bgcolor: getRouteColor(route.usage_count),
                            color: '#fff',
                            fontWeight: 'bold',
                          }}
                        />
                        {route.confidence_score >= 0.7 && (
                          <Chip 
                            label="High Confidence"
                            size="small"
                            color="success"
                            variant="outlined"
                            sx={{ fontSize: '0.6rem' }}
                          />
                        )}
                      </Stack>
                    </Stack>
                    
                    {/* Route Origin → Destination */}
                    <Box mt={1} p={1} bgcolor={alpha(theme.palette.primary.main, 0.05)} borderRadius={1}>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Chip 
                          label={route.origin.name || 'Origin'}
                          size="small"
                          color="success"
                          variant="filled"
                          sx={{ fontSize: '0.65rem' }}
                        />
                        <Typography variant="caption" color="text.secondary">→</Typography>
                        <Chip 
                          label={route.destination.name || 'Destination'}
                          size="small"
                          color="error"
                          variant="filled"
                          sx={{ fontSize: '0.65rem' }}
                        />
                      </Stack>
                    </Box>
                    
                    {/* Route Stats */}
                    <Stack direction="row" spacing={2} mt={1} flexWrap="wrap">
                      {route.avg_distance_meters > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          {(route.avg_distance_meters / 1609.34).toFixed(1)} mi
                        </Typography>
                      )}
                      {route.avg_duration_seconds > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          <TimeIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                          {Math.round(route.avg_duration_seconds / 60)} min
                        </Typography>
                      )}
                      {route.peak_hour !== null && (
                        <Typography variant="caption" color="text.secondary">
                          Peak: {route.peak_hour}:00
                        </Typography>
                      )}
                      {route.peak_day && (
                        <Typography variant="caption" color="text.secondary">
                          {route.peak_day}
                        </Typography>
                      )}
                    </Stack>
                    
                   {/* Shared Route Indicator */}
                   {(route.shared_route_count ?? 0) > 0 && (
                      <Box 
                        mt={1} 
                        p={1} 
                        bgcolor={alpha(theme.palette.warning.main, 0.1)} 
                        borderRadius={1}
                        border={`1px solid ${alpha(theme.palette.warning.main, 0.3)}`}
                      >
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Typography variant="caption" fontWeight="bold" color="warning.main">
                              Shared Route - {route.shared_route_count} other {route.shared_route_count === 1 ? 'entity' : 'entities'}
                            </Typography>
                            <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                              Also used by: {route.shared_with_entities?.slice(0, 5).join(', ')}
                              {(route.shared_with_entities?.length || 0) > 5 && ` +${(route.shared_with_entities?.length || 0) - 5} more`}
                            </Typography>
                          </Box>
                          <Button
                            size="small"
                            variant="contained"
                            color="warning"
                            onClick={(e: MouseEvent<HTMLButtonElement>) => {
                              e.stopPropagation();
                              handleCompareRoutes(route.id);
                            }}
                            disabled={compareLoading}
                            sx={{ 
                              fontSize: '0.65rem', 
                              py: 0.25, 
                              px: 1,
                              minWidth: 'auto'
                            }}
                          >
                            {compareLoading ? '...' : 'Compare'}
                          </Button>
                        </Stack>
                      </Box>
                    )}
                    
                    {/* Behavioral Tags */}
                    {route.behavioral_tags && route.behavioral_tags.length > 0 && (
                      <Stack direction="row" spacing={0.5} mt={1} flexWrap="wrap">
                        {route.behavioral_tags.map((tag: string, idx: number) => (
                          <Chip 
                            key={idx}
                            label={tag}
                            size="small"
                            variant="outlined"
                            color="secondary"
                            sx={{ fontSize: '0.6rem' }}
                          />
                        ))}
                      </Stack>
                    )}
                    
                    {/* Pattern Summary */}
                    {route.pattern_summary && (
                      <Typography variant="caption" color="text.secondary" mt={1} display="block" sx={{ fontStyle: 'italic' }}>
                        {route.pattern_summary}
                      </Typography>
                    )}
                    
                    {/* Trip Timeline – time-of-day scatter showing when each trip occurred */}
                    {route.trip_instances && route.trip_instances.length > 0 && (() => {
                      const trips = route.trip_instances!
                        .map((t: RouteTripInstance) => {
                          const [h, m] = t.time.split(':').map(Number);
                          const timeDecimal = h + m / 60;
                          const d = new Date(t.date + 'T00:00:00');
                          return {
                            date: t.date,
                            dateLabel: d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
                            timeDecimal,
                            timeLabel: t.time,
                            day: t.day,
                            durationMin: Math.round(t.duration_s / 60),
                            distanceKm: +(t.distance_m / 1000).toFixed(1),
                            pointCount: t.point_count,
                            idx: 0,
                          };
                        })
                        .sort((a, b) => a.date.localeCompare(b.date) || a.timeDecimal - b.timeDecimal);

                      trips.forEach((t, i) => { t.idx = i; });

                      const times = trips.map(t => t.timeDecimal);
                      const minH = Math.max(0, Math.floor(Math.min(...times)) - 1);
                      const maxH = Math.min(24, Math.ceil(Math.max(...times)) + 1);

                      const durations = trips.map(t => t.durationMin);
                      const minDur = Math.min(...durations);
                      const maxDur = Math.max(...durations);

                      const formatHour = (h: number) => {
                        const hr = Math.round(h);
                        if (hr === 0 || hr === 24) return '12a';
                        if (hr === 12) return '12p';
                        return hr < 12 ? `${hr}a` : `${hr - 12}p`;
                      };

                      const getDotOpacity = (dur: number) => {
                        if (maxDur <= minDur) return 0.85;
                        return 0.4 + ((dur - minDur) / (maxDur - minDur)) * 0.55;
                      };

                      return (
                        <Box mt={1.5} pt={1} borderTop={`1px dashed ${alpha(theme.palette.divider, 0.5)}`}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                            <Typography variant="caption" fontWeight="bold" color="text.secondary">
                              Trip Timeline ({route.trip_instances!.length} trips)
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.6, fontSize: '0.6rem' }}>
                              {formatHour(minH + 1)}–{formatHour(maxH - 1)} • dot opacity = duration
                            </Typography>
                          </Stack>
                          <ResponsiveContainer width="100%" height={80}>
                            <ScatterChart margin={{ top: 4, right: 4, left: -16, bottom: 4 }}>
                              <CartesianGrid
                                strokeDasharray="3 3"
                                stroke={alpha(theme.palette.divider, 0.15)}
                                horizontal
                                vertical={false}
                              />
                              <XAxis
                                dataKey="idx"
                                type="number"
                                domain={[-0.5, trips.length - 0.5]}
                                tick={false}
                                axisLine={false}
                                tickLine={false}
                              />
                              <YAxis
                                dataKey="timeDecimal"
                                type="number"
                                domain={[minH, maxH]}
                                tickCount={Math.min(maxH - minH + 1, 5)}
                                tickFormatter={formatHour}
                                tick={{ fontSize: 9, fill: theme.palette.text.secondary }}
                                axisLine={false}
                                tickLine={false}
                                width={30}
                              />
                              <ZAxis range={[30, 30]} />
                              {route.peak_hour != null && (
                                <ReferenceLine
                                  y={route.peak_hour}
                                  stroke={alpha(theme.palette.warning.main, 0.5)}
                                  strokeDasharray="4 3"
                                  label={{
                                    value: `peak ${formatHour(route.peak_hour)}`,
                                    fontSize: 8,
                                    fill: alpha(theme.palette.warning.main, 0.8),
                                    position: 'insideTopRight',
                                  }}
                                />
                              )}
                              <RechartsTooltip
                                cursor={false}
                                content={({ payload }: any) => {
                                  if (!payload?.length) return null;
                                  const d = payload[0].payload;
                                  return (
                                    <Box sx={{
                                      background: isDarkMode ? '#1a1a2e' : '#fff',
                                      border: `1px solid ${isDarkMode ? '#333' : '#ddd'}`,
                                      borderRadius: '6px',
                                      fontSize: '0.7rem',
                                      p: '6px 10px',
                                      lineHeight: 1.5,
                                    }}>
                                      <Box sx={{ fontWeight: 600 }}>{d.dateLabel}</Box>
                                      <Box sx={{ color: theme.palette.text.secondary }}>
                                        {d.timeLabel} · {d.durationMin}min · {d.distanceKm}km
                                      </Box>
                                    </Box>
                                  );
                                }}
                              />
                              <Scatter data={trips}>
                                {trips.map((entry, i) => (
                                  <Cell
                                    key={i}
                                    fill={theme.palette.primary.main}
                                    fillOpacity={getDotOpacity(entry.durationMin)}
                                    stroke={alpha(theme.palette.primary.light, 0.4)}
                                    strokeWidth={0.5}
                                  />
                                ))}
                              </Scatter>
                            </ScatterChart>
                          </ResponsiveContainer>
                        </Box>
                      );
                    })()}
                    
                    <Typography variant="caption" color="primary" mt={1} display="block">
                      <RouteIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                      {normalizeRouteGeometry(route.route_geometry, route.origin).length || route.route_geometry?.length || 0} GPS points • Click to view on map
                    </Typography>
                  </CardContent>
                </Card>
              ))}
              {extentFilteredRoutes.length === 0 && !routePatternsLoading && (
                <Box textAlign="center" py={4}>
                  <Typography color="text.secondary">
                    {filterByMapExtent
                      ? 'No routes in the current map view'
                      : routeRepeatFilter === 'single'
                        ? 'No one-time routes found'
                        : 'No route patterns detected'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {filterByMapExtent
                      ? 'Pan/zoom the map or turn off map-extent crop'
                      : routeRepeatFilter === 'single'
                      ? 'No routes with exactly one occurrence in the selected time range'
                      : routeRepeatFilter === 'repeated'
                        ? 'No routes with 2+ occurrences in the selected time range'
                        : 'No route patterns found for this entity/time range'
                    }
                  </Typography>
                </Box>
              )}
              {routeRepeatFilter === 'single' && extentFilteredRoutes.length > 0 && extentFilteredRoutes.filter((r: RoutePattern) => r.usage_count === 1).length === 0 && (
                <Box textAlign="center" py={4}>
                  <Typography color="text.secondary">No one-time routes in results</Typography>
                  <Typography variant="caption" color="text.secondary">
                    All {extentFilteredRoutes.length} routes have been used more than once
                  </Typography>
                </Box>
              )}
            </TabPanel>
            
            {/* Dwell Analysis Tab */}
            <TabPanel value={activeTab} index={POL_TAB.DWELLS}>
              {filterByMapExtent && (
                <Alert severity="info" sx={{ mb: 1, py: 0 }}>
                  Cropped to map view — {extentFilteredDwells.length} of {dwellTabData.length} dwells
                </Alert>
              )}
              <DwellAnalysisTab
                dwells={extentFilteredDwells}
                loading={dwellTabLoading}
                onDwellClick={(dwell) => {
                  if (dwell && dwell.lat && dwell.lon) {
                    mapRef.current?.getMap()?.flyTo({
                      center: [dwell.lon, dwell.lat],
                      zoom: 17,
                      duration: 1500,
                    });
                    setFlashDwellLocation({ lat: dwell.lat, lon: dwell.lon });
                    setTimeout(() => setFlashDwellLocation(null), 2000);
                  } else {
                    setFlashDwellLocation(null);
                  }
                }}
                onGroupClick={(group) => {
                  if (group) {
                    mapRef.current?.getMap()?.flyTo({
                      center: [group.lon, group.lat],
                      zoom: 16,
                      duration: 1500,
                    });
                    setFlashDwellLocation({ lat: group.lat, lon: group.lon });
                    setTimeout(() => setFlashDwellLocation(null), 2000);
                  }
                }}
              />
            </TabPanel>

            {/* Activity Heatmap Tab */}
            <TabPanel value={activeTab} index={POL_TAB.ACTIVITY}>
              {/* KPI Summary Cards - filterable to slice the Pattern Wheel.
                  Period totals use the same temporal_heatmap aggregates as the wheel (dwell/route trip/call/SMS
                  buckets match backend _generate_temporal_heatmap). Entity summaries and routePatterns.length
                  use different definitions and previously diverged from the graphic. */}
              {dashboardData?.entities && dashboardData.entities.length > 0 && (() => {
                const isDark = theme.palette.mode === 'dark';
                type KpiFilterKey = 'dwells' | 'routes' | 'calls' | 'sms' | 'apps' | 'browsing';
                type KpiItem = {
                  label: string;
                  value: string;
                  color: string;
                  filterKey: KpiFilterKey;
                  tooltip: string;
                };
                // When a wheel cell is selected, values show counts FOR THAT CELL ONLY.
                // Otherwise period totals = sum of temporal_heatmap cells (same source as the wheel).
                const isCell = !!wheelCellFilter;
                const cellLabel = isCell
                  ? `${['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][wheelCellFilter!.day]} ${wheelCellFilter!.hour.toString().padStart(2,'0')}:00`
                  : '';
                const dwellsVal   = isCell ? (heatmapCellTotals?.dwell    ?? 0) : heatmapTotals.dwell;
                const routesVal   = isCell ? (heatmapCellTotals?.route    ?? 0) : heatmapTotals.route;
                const callsVal    = isCell ? (heatmapCellTotals?.call     ?? 0) : heatmapTotals.call;
                const smsVal      = isCell ? (heatmapCellTotals?.sms      ?? 0) : heatmapTotals.sms;
                const appsVal     = isCell ? (heatmapCellTotals?.app      ?? 0) : heatmapTotals.app;
                const browsingVal = isCell ? (heatmapCellTotals?.browsing ?? 0) : heatmapTotals.browsing;
                const mkTip = (kind: string) =>
                  isCell ? `${kind} during ${cellLabel} · click card again or cell to clear` : `Total ${kind.toLowerCase()} · click to filter wheel`;
                const kpis: KpiItem[] = [
                  { label: 'Dwells',   value: dwellsVal.toLocaleString(),   color: '#f59e0b', filterKey: 'dwells',   tooltip: mkTip('Dwells') },
                  { label: 'Routes',   value: routesVal.toLocaleString(),   color: '#a371f7', filterKey: 'routes',   tooltip: mkTip('Route trips') },
                  { label: 'Calls',    value: callsVal.toLocaleString(),    color: '#f85149', filterKey: 'calls',    tooltip: mkTip('Calls') },
                  { label: 'SMS',      value: smsVal.toLocaleString(),      color: '#ec4899', filterKey: 'sms',      tooltip: mkTip('SMS') },
                  { label: 'Apps',     value: appsVal.toLocaleString(),     color: '#3fb950', filterKey: 'apps',     tooltip: mkTip('App events') },
                  { label: 'Browsing', value: browsingVal.toLocaleString(), color: '#58a6ff', filterKey: 'browsing', tooltip: mkTip('Browsing events') },
                ];
                const filterAccent = '#f59e0b';
                return (
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 0.5, mb: 2 }}>
                    {kpis.map((kpi, i) => {
                      const isSelected = kpiFilter === kpi.filterKey;
                      return (
                        <Tooltip key={i} title={kpi.tooltip} placement="top" arrow>
                          <Box
                            onClick={() => setKpiFilter(prev => prev === kpi.filterKey ? 'all' : kpi.filterKey)}
                            sx={{
                              py: 1,
                              px: 0.75,
                              borderRadius: 1,
                              textAlign: 'center',
                              bgcolor: isSelected ? alpha(filterAccent, 0.1) : 'rgba(255,255,255,0.04)',
                              border: '1px solid',
                              borderColor: isSelected ? filterAccent : 'divider',
                              cursor: 'pointer',
                              transition: 'all 0.15s',
                              position: 'relative',
                              '&:hover': {
                                borderColor: filterAccent,
                                bgcolor: alpha(filterAccent, isSelected ? 0.12 : 0.04),
                              },
                              '&:hover .filter-hint-icon': { opacity: 0.9 },
                            }}
                          >
                            {/* Filter icon in top-left corner — indicates card is filterable */}
                            <Box
                              className="filter-hint-icon"
                              sx={{
                                position: 'absolute',
                                top: 4,
                                left: 4,
                                opacity: isSelected ? 0.9 : 0.35,
                                transition: 'opacity 0.15s',
                                color: isSelected ? filterAccent : (isDark ? '#7d8590' : '#999'),
                                lineHeight: 0,
                              }}
                            >
                              <FilterIcon sx={{ fontSize: 11 }} />
                            </Box>
                            <Typography
                              sx={{
                                fontSize: '0.55rem',
                                textTransform: 'uppercase',
                                letterSpacing: 0.5,
                                fontWeight: 600,
                                color: isSelected ? filterAccent : (isDark ? '#7d8590' : '#999'),
                                display: 'block',
                                lineHeight: 1,
                                mb: 0.5,
                              }}
                            >
                              {kpi.label}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: '1.1rem',
                                fontWeight: 700,
                                color: isSelected ? filterAccent : (isDark ? '#e6edf3' : '#24292f'),
                                lineHeight: 1.1,
                              }}
                            >
                              {kpi.value}
                            </Typography>
                          </Box>
                        </Tooltip>
                      );
                    })}
                  </Box>
                );
              })()}

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  Activity by Hour & Day
                </Typography>
                {wheelCellFilter && (
                  <Chip
                    label={`Filtered · ${['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][wheelCellFilter.day]} ${wheelCellFilter.hour.toString().padStart(2,'0')}:00`}
                    size="small"
                    onDelete={() => setWheelCellFilter(null)}
                    color="warning"
                    sx={{ fontWeight: 600, fontSize: '0.65rem', height: 20 }}
                  />
                )}
              </Box>
              {(dashboardData?.temporal_heatmap?.length ?? 0) > 0 ? (
                <Box sx={{ py: 1 }}>
                  <PatternWheel
                    data={wheelHeatmapData}
                    title={wheelTitle}
                    size={Math.min(400, window.innerWidth - 440)}
                    onCellClick={(hour, day) =>
                      setWheelCellFilter(prev =>
                        prev?.hour === hour && prev?.day === day ? null : { hour, day }
                      )
                    }
                    selectedCell={wheelCellFilter}
                  />
                  {wheelCellFilter && (() => {
                    // Compute effective period window — must match what the backend
                    // used when aggregating the temporal_heatmap so KPI cards and
                    // timeline occurrence counts agree exactly.
                    const useCustom = dateRangeMode === 'custom' && startDate && endDate;
                    const periodEnd = useCustom
                      ? new Date(`${endDate}T23:59:59.999Z`)
                      : new Date();
                    const periodStart = useCustom
                      ? new Date(`${startDate}T00:00:00.000Z`)
                      : new Date(Date.now() - daysBack * 86400000);
                    const isoStart = useCustom
                      ? startDate
                      : periodStart.toISOString().split('T')[0];
                    const isoEnd = useCustom
                      ? endDate
                      : periodEnd.toISOString().split('T')[0];
                    // Expected digital count for this cell (from heatmap) — lets the
                    // timeline show the Digital row even if the per-event fetch
                    // returns nothing (cache/limit/NULL-ref edge cases).
                    const expectedDigital =
                      (heatmapCellTotals?.call ?? 0) +
                      (heatmapCellTotals?.sms ?? 0) +
                      (heatmapCellTotals?.app ?? 0) +
                      (heatmapCellTotals?.browsing ?? 0);
                    return (
                      <WheelCellTimeline
                        dwells={dwellTabData}
                        routes={routePatterns}
                        hour={wheelCellFilter.hour}
                        day={wheelCellFilter.day}
                        courseId={selectedCourse || undefined}
                        daysBack={daysBack}
                        entityIds={selectedEntity || undefined}
                        startDate={isoStart}
                        endDate={isoEnd}
                        periodStart={periodStart}
                        periodEnd={periodEnd}
                        expectedDigitalTotal={expectedDigital}
                      />
                    );
                  })()}
                </Box>
              ) : (
                <Box textAlign="center" py={4}>
                  <Typography color="text.secondary">No activity data available</Typography>
                </Box>
              )}
            </TabPanel>
            
            {/* Digital Patterns - Comprehensive Insights Panel */}
            <TabPanel value={activeTab} index={POL_TAB.DIGITAL}>
              <DigitalInsightsPanel
                digitalPatterns={digitalPatterns}
                digitalSimilarities={digitalSimilarities}
                digitalInsights={digitalInsights}
                loading={digitalPatternsLoading}
                category={digitalCategory}
                onCategoryChange={setDigitalCategory}
                onEntitySelect={setSelectedEntity}
                selectedEntity={selectedEntity}
                entityIds={selectedEntity ? [selectedEntity] : undefined}
                courseId={selectedCourse || undefined}
                daysBack={daysBack}
                startDate={dateRangeMode === 'custom' && startDate ? startDate : undefined}
                endDate={dateRangeMode === 'custom' && endDate ? endDate : undefined}
                mapSelectedCell={wheelCellFilter}
                onDigitalEventSelect={handleDigitalEventMapSelect}
                onDigitalEventsLoad={handleDigitalEventsMapLoad}
              />
            </TabPanel>
          </Box>
        </Paper>
      </Box>
      )}
      {/* Export Notification */}
      <Snackbar
        open={exportSnackbar.open}
        autoHideDuration={exportSnackbar.severity === 'info' ? null : 4000}
        onClose={() => setExportSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={exportSnackbar.severity}
          onClose={() => setExportSnackbar(prev => ({ ...prev, open: false }))}
          sx={{ minWidth: 300 }}
          icon={exportSnackbar.severity === 'info' ? <CircularProgress size={18} color="inherit" /> : undefined}
        >
          {exportSnackbar.message}
        </Alert>
      </Snackbar>

      {/* PPTX Theme Picker Dialog */}
      <Dialog open={pptxThemeOpen} onClose={() => setPptxThemeOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          Export to PowerPoint
          <Typography variant="caption" display="block" color="text.secondary">
            {pptxExportEntityId} — Pattern of Life Report
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1.5 }}>Select a theme:</Typography>
          <Stack spacing={1}>
            {(Object.keys(THEME_DEFINITIONS) as PowerPointTheme[]).map((key) => {
              const def = THEME_DEFINITIONS[key];
              const isSelected = pptxTheme === key;
              return (
                <Box
                  key={key}
                  onClick={() => setPptxTheme(key)}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5, p: 1, borderRadius: 1, cursor: 'pointer',
                    border: `2px solid ${isSelected ? 'primary.main' : 'divider'}`,
                    bgcolor: isSelected ? 'action.selected' : 'transparent',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <Box sx={{ width: 32, height: 32, borderRadius: 0.5, bgcolor: `#${def.colors.titleBg}`, border: '1px solid', borderColor: 'divider', flexShrink: 0 }} />
                  <Box>
                    <Typography variant="body2" fontWeight={600}>{def.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{def.description}</Typography>
                  </Box>
                </Box>
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPptxThemeOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handlePOLExport} disabled={pptxExporting} startIcon={pptxExporting ? <CircularProgress size={16} /> : undefined}>
            {pptxExporting ? 'Exporting...' : 'Export'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PatternOfLifePage;


